import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import { API_BASE } from './config';
import socket from '../socket/socket';
import '../css/RandomMatchPanel.css';
import { resolveAvatarSource } from '../utils/avatar';
import { apiFetch } from '../utils/apiFetch';
import { getPlayIntensity, openAdminPlayerEditor } from '../utils/playIntensity';
import { matchesSearch } from '../utils/textSearch';

const AdminPlayerName = ({ player }) => <button type="button" className="rmp-admin-player-name" onClick={event=>{event.stopPropagation();openAdminPlayerEditor(player);}}>{player?.name||'-'}</button>;
const intensityText = player => { const item=getPlayIntensity(player?.play_intensity); return `${item.icon} ${item.label}`; };
const intensityClass = player => String(player?.play_intensity || 'ANY').toLowerCase();
const genderText = player => String(player?.gender || '').toUpperCase() === 'MALE' ? '♂ ชาย' : String(player?.gender || '').toUpperCase() === 'FEMALE' ? '♀ หญิง' : String(player?.gender || '').toUpperCase() === 'BEAUTIFUL' ? '🌈 สวยที่สุด' : 'ไม่ระบุ';

const MODES = [
  {
    value: 'PICK_ONE',
    label: 'Pick One',
    shortLabel: 'Pick',
    description: 'ล็อกผู้เล่นหนึ่งคนแล้วหาอีกสามคน'
  },
  {
    value: 'AI_RANDOM',
    label: 'AI RANDOM',
    shortLabel: 'AI',
    description: 'มาก่อนได้ก่อน เลือกคะแนนรายบุคคลใกล้กัน แล้วจัดทีมให้สูสี'
  },
  {
    value: 'MANUAL_4',
    label: 'Select 4',
    shortLabel: 'Select4',
    description: 'เลือก Team 1 สองคน และ Team 2 สองคน'
  }
];

const AUTOMATIC_MODES = new Set(['AI_RANDOM']);
const RECOMMENDATION_VISIBLE_STORAGE_KEY =
  'badminton-match-recommendation-visible';

const POINT_RANGES = [100, 150, 200, 300];
const RANK_ORDER = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const INTENSITY_ORDER = ['ANY', 'LIGHT', 'MEDIUM', 'HEAVY'];
const PICK_ONE_STATUS_ORDER = ['ACTIVE', 'PLAY', 'QUEUE', 'HOLD', 'CHECKOUT'];

const getNearbyIntensityPriority = (anchor, candidate) => {
  const anchorIntensity = String(anchor?.play_intensity || 'ANY').toUpperCase();
  const candidateIntensity = String(candidate?.play_intensity || 'ANY').toUpperCase();
  if (candidateIntensity === anchorIntensity) return 0;
  if (candidateIntensity === 'ANY') return 1;
  if (anchorIntensity === 'ANY') return INTENSITY_ORDER.indexOf(candidateIntensity) + 1;
  const anchorIndex = INTENSITY_ORDER.indexOf(anchorIntensity);
  const candidateIndex = INTENSITY_ORDER.indexOf(candidateIntensity);
  return 2 + Math.abs(candidateIndex - anchorIndex);
};
const WAITING_ALERT_MINUTES = 5;
const NO_GAME_ALERT_MINUTES = 10;
const positionWaitingPanelBeside = (button, preferredWidth = 360, preferredHeight = 650) => {
  const rect = button.getBoundingClientRect();
  const width = Math.min(preferredWidth, window.innerWidth - 16);
  const height = Math.min(preferredHeight, window.innerWidth <= 700 ? window.innerHeight * 0.46 : window.innerHeight - 16);
  const clamp = (x, y) => ({
    x: Math.max(8, Math.min(window.innerWidth - width - 8, x)),
    y: Math.max(8, Math.min(window.innerHeight - height - 8, y))
  });
  if (window.innerWidth <= 700) {
    const below = rect.bottom + 8;
    return clamp(8, below + height <= window.innerHeight - 8 ? below : rect.top - height - 8);
  }
  if (rect.right + 10 + width <= window.innerWidth - 8) return clamp(rect.right + 10, rect.top);
  if (rect.left - 10 - width >= 8) return clamp(rect.left - width - 10, rect.top);
  return clamp(rect.left, rect.bottom + 8);
};
const USER_MATCH_TYPE_LABELS = {
  PLAY_ALL_FAST: 'เล่นได้หมด เล่นไว',
  NO_CARRY: 'ไม่ตีคุ้ม',
  TIGHT: 'ขอตึง',
  LIGHT: 'ขอเบา ๆ'
};

const PLAY_STYLE_OPTIONS = [
  {
    value: 'ANY',
    icon: '🏸',
    label: 'ทุก Mood',
    description: 'ไม่กรอง Mood ของวันนี้'
  },
  {
    value: 'FAST_POWER_MIX',
    icon: '⚡',
    label: 'ทั่วไป',
    description: 'เร็ว หนัก สลับ เบา'
  },
  {
    value: 'TIGHT_MEDIUM_HEAVY',
    icon: '🎯',
    label: 'ขอเหนื่อย',
    description: 'ตึง ปานกลาง–หนัก'
  },
  {
    value: 'ALL_ROUND_READY',
    icon: '🔥',
    label: 'ได้หมด พร้อมชน',
    description: 'พร้อมเล่นทุกรูปแบบ'
  },
  {
    value: 'ENJOY_FUN',
    icon: '😄',
    label: 'เน้นฮาและเหนื่อย',
    description: 'Enjoy เฮฮา ไม่เครียด'
  }
];

function getPlayStyleLabel(value) {
  return PLAY_STYLE_OPTIONS.find(option => option.value === value)?.label || '-';
}

const PLAY_STYLE_ALIASES = new Map([
  ['FAST_POWER_MIX', 'FAST_POWER_MIX'],
  ['เน้นเร็ว หนัก สลับ เบา (ตามระดับมือ)', 'FAST_POWER_MIX'],
  ['เร็ว หนัก สลับ เบา', 'FAST_POWER_MIX'],
  ['TIGHT_MEDIUM_HEAVY', 'TIGHT_MEDIUM_HEAVY'],
  ['เน้นตึง ปานกลาง–หนัก (ตามระดับมือ)', 'TIGHT_MEDIUM_HEAVY'],
  ['เน้นตึง ปานกลาง - หนัก (ตามระดับมือ)', 'TIGHT_MEDIUM_HEAVY'],
  ['ตึง ปานกลาง–หนัก', 'TIGHT_MEDIUM_HEAVY'],
  ['ALL_ROUND_READY', 'ALL_ROUND_READY'],
  ['ได้หมด พร้อมชน', 'ALL_ROUND_READY'],
  ['ENJOY_FUN', 'ENJOY_FUN'],
  ['เน้น Enjoy เฮฮา ไม่เครียด (ตามระดับมือ)', 'ENJOY_FUN'],
  ['Enjoy เฮฮา', 'ENJOY_FUN']
]);

function normalizePlayStyleValue(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return null;

  return (
    PLAY_STYLE_ALIASES.get(rawValue) ||
    PLAY_STYLE_ALIASES.get(rawValue.toUpperCase()) ||
    null
  );
}

function getStatus(player) {
  return String(player?.memberStatus ?? player?.status ?? 'ACTIVE').toUpperCase();
}
function getQueueMatchNo(player) {

  const value =
    player?.queueMatchNo ??
    player?.queue_match_no;

  const matchNo =
    Number(value);

  return Number.isFinite(matchNo) &&
    matchNo > 0
    ? matchNo
    : null;
} function getQueueMatchId(player) {

  const value =
    player?.queueMatchId ??
    player?.queue_match_id;

  const matchId =
    Number(value);

  return Number.isFinite(matchId) &&
    matchId > 0
    ? matchId
    : null;
}
function getPlayingMatchNo(player) {
  const value =
    player?.playingMatchNo ??
    player?.playingGroupMatchNo ??
    player?.playing_match_no;
  const matchNo = Number(value);
  return Number.isFinite(matchNo) && matchNo > 0 ? matchNo : null;
}

function getPlayingMatchId(player) {
  const value = player?.playingMatchId ?? player?.playing_match_id;
  const matchId = Number(value);
  return Number.isFinite(matchId) && matchId > 0 ? matchId : null;
}
function getPickOneStatusPriority(player) {
  const index = PICK_ONE_STATUS_ORDER.indexOf(getStatus(player));
  return index === -1 ? PICK_ONE_STATUS_ORDER.length : index;
}

function getPlayerGames(player) {
  return Number(
    player?.totalMatches ??
    player?.total_matches ??
    player?.total_games ??
    0
  ) || 0;
}

function normalizePlayerRank(value, point = 0) {
  const rawRank = String(value || '').trim();
  const rankWithoutPrefix = rawRank.startsWith('มือ')
    ? rawRank.slice('มือ'.length).trim()
    : rawRank;
  const normalized = rankWithoutPrefix
    .toUpperCase()
    .replace(/^SP$/, 'S/P')
    .replace(/^S-P$/, 'S/P')
    .replace(/^NP$/, 'N/S')
    .replace(/^N-S$/, 'N/S');

  if (RANK_ORDER.includes(normalized)) return normalized;

  const safePoint = Number(point || 0);
  if (safePoint < 350) return 'BG';
  if (safePoint < 400) return 'BGM';
  if (safePoint < 450) return 'NB';
  if (safePoint < 500) return 'NB+';
  if (safePoint < 550) return 'N-';
  if (safePoint < 600) return 'N';
  if (safePoint < 650) return 'N/S';
  if (safePoint < 700) return 'S';
  return 'S/P';
}

function getRankDistance(left, right) {
  const leftRank = normalizePlayerRank(left?.level_rank, left?.point);
  const rightRank = normalizePlayerRank(right?.level_rank, right?.point);
  return Math.abs(RANK_ORDER.indexOf(leftRank) - RANK_ORDER.indexOf(rightRank));
}

function normalizeAutoIntensity(player) {
  const intensity = String(player?.play_intensity || 'ANY').trim().toUpperCase();
  return ['LIGHT', 'MEDIUM', 'HEAVY'].includes(intensity) ? intensity : 'ANY';
}

function isPickOneAutoIntensityCompatible(players) {
  const level = { LIGHT: 0, MEDIUM: 1, HEAVY: 2 };
  const values = players
    .map(normalizeAutoIntensity)
    .filter(intensity => Object.prototype.hasOwnProperty.call(level, intensity))
    .map(intensity => level[intensity]);
  return values.length < 2 || Math.max(...values) - Math.min(...values) <= 1;
}

function getPickOneAutoPreference(players, anchorPlayer) {
  const anchorRank = normalizePlayerRank(anchorPlayer?.level_rank, anchorPlayer?.point);
  const anchorIntensity = normalizeAutoIntensity(anchorPlayer);
  return players
    .filter(player => Number(player.user_id) !== Number(anchorPlayer?.user_id))
    .reduce((preference, player) => {
      const rank = normalizePlayerRank(player.level_rank, player.point);
      const intensity = normalizeAutoIntensity(player);
      preference.rankMismatch += Number(rank !== anchorRank);
      if (anchorIntensity === 'ANY') {
        preference.intensity += intensity === 'MEDIUM' ? 0 : intensity === 'ANY' ? 1 : 2;
      } else {
        preference.intensity += intensity === anchorIntensity ? 0 : intensity === 'ANY' ? 1 : 2;
      }
      return preference;
    }, { rankMismatch: 0, intensity: 0 });
}

function parsePlayerTime(value) {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function getLatestPlayerTime(...values) {
  const validTimes = values
    .map(parsePlayerTime)
    .filter(value => Number.isFinite(value) && value !== Number.MAX_SAFE_INTEGER);
  return validTimes.length > 0 ? Math.max(...validTimes) : Number.MAX_SAFE_INTEGER;
}

function getQueueStartedAt(player) {
  if (getStatus(player) === 'QUEUE') {
    return parsePlayerTime(
      player?.queueCreatedAt ??
      player?.queue_created_at ??
      player?.queueStartTime
    );
  }

  if (getStatus(player) === 'PLAY') {
    return parsePlayerTime(
      player?.playingStartTime ??
      player?.playing_start_time ??
      player?.start_time
    );
  }

  // ACTIVE starts waiting from the latest real lifecycle event. Using the
  // first non-empty timestamp kept an old active_since alive after a newer
  // check-in/finished match and produced waits of many thousands of minutes.
  return getLatestPlayerTime(
    player?.wait_started_at,
    player?.waitStartedAt,
    player?.last_finished_at,
    player?.lastFinishedAt,
    player?.active_since,
    player?.activeSince,
    player?.checkin_time,
    player?.checkinTime,
    player?.created_at
  );
}

function getExplicitWaitMinutes(player) {
  const value = Number(
    player?.waitMinutes ??
    player?.wait_minutes ??
    player?.waitingMinutes ??
    player?.waiting_minutes
  );
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function getPlayerMatchStatusLabel(player) {
  const status = getStatus(player);
  if (status === 'QUEUE') {
    const matchNo = getQueueMatchNo(player) || getQueueMatchId(player);
    return matchNo ? `Queue Match #${matchNo}` : 'อยู่ Match Queue';
  }
  if (status === 'PLAY') {
    const matchNo = getPlayingMatchNo(player) || getPlayingMatchId(player);
    return matchNo ? `PLAY Match #${matchNo}` : 'กำลัง PLAY';
  }
  return status;
}

function getPreviewWaitPriority(match) {
  const players = [...(match?.team1 || []), ...(match?.team2 || [])];
  const times = players.map(getQueueStartedAt).filter(Number.isFinite);
  return {
    oldest: times.length ? Math.min(...times) : Number.MAX_SAFE_INTEGER,
    total: times.length ? times.reduce((sum, value) => sum + value, 0) : Number.MAX_SAFE_INTEGER
  };
}

function getPreviewConditionPenalty(match) {
  let penalty = 0;
  if (match?.requiresRecentEncounterApproval) penalty += 1000;
  if (Number(match?.analysis?.teamGap ?? match?.teamGap ?? 0) > 50) penalty += 100;
  if (Number(match?.rankSpread || 0) > 1) penalty += 20;
  if (Number(match?.pointSpread || 0) > 100) penalty += 10;
  return penalty;
}

function getArrivalTime(player) {
  return getLatestPlayerTime(
    player?.active_since,
    player?.checkin_time,
    player?.last_finished_at
  );
}

function comparePlayersByPriority(left, right, priority) {
  if (priority === 'LEAST_GAMES') {
    const gameDiff = getPlayerGames(left) - getPlayerGames(right);
    if (gameDiff !== 0) return gameDiff;
  }

  if (priority === 'WAIT_LONGEST') {
    const waitDiff = getQueueStartedAt(left) - getQueueStartedAt(right);
    if (waitDiff !== 0) return waitDiff;
  }

  const arrivalDiff = getArrivalTime(left) - getArrivalTime(right);
  if (arrivalDiff !== 0) return arrivalDiff;

  return Number(left.user_id) - Number(right.user_id);
}

function formatWaitTime(player, now) {
  const startedAt = getQueueStartedAt(player);
  if (startedAt === Number.MAX_SAFE_INTEGER) {
    const explicitMinutes = getExplicitWaitMinutes(player);
    return explicitMinutes === null ? '-' : `${Math.floor(explicitMinutes)} นาที`;
  }

  const minutes = Math.max(0, Math.floor((now - startedAt) / 60000));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) return `${hours} ชม. ${remainingMinutes} นาที`;
  return `${minutes} นาที`;
}

function formatElapsedHoursMinutes(startedAt, now) {
  const timestamp = parsePlayerTime(startedAt);
  if (timestamp === Number.MAX_SAFE_INTEGER) return '-';
  const totalMinutes = Math.max(0, Math.floor((now - timestamp) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours} ชม. ${minutes} นาที` : `${minutes} นาที`;
}

function getRelationCount(relationMap, leftPlayer, rightPlayer) {
  const leftId = Number(leftPlayer?.user_id);
  const rightId = Number(rightPlayer?.user_id);

  if (!Number.isInteger(leftId) || !Number.isInteger(rightId)) return 0;

  return Number(
    relationMap?.[`${leftId}-${rightId}`] ??
    relationMap?.[`${rightId}-${leftId}`] ??
    0
  ) || 0;
}

function getRelationHistory(relationMap, player, playerNameById) {
  const playerId = Number(player?.user_id);
  if (!Number.isInteger(playerId)) return [];

  const historyByPlayerId = new Map();
  Object.entries(relationMap || {}).forEach(([key, rawCount]) => {
    const [leftId, rightId] = String(key).split('-').map(Number);
    if (!Number.isInteger(leftId) || !Number.isInteger(rightId)) return;
    if (leftId !== playerId && rightId !== playerId) return;

    const otherId = leftId === playerId ? rightId : leftId;
    if (otherId === playerId) return;
    const count = Math.max(0, Number(rawCount) || 0);
    // opponentMap อาจมีทั้ง A-B และ B-A จึงใช้ค่าสูงสุด ไม่บวกซ้ำ
    historyByPlayerId.set(
      otherId,
      Math.max(historyByPlayerId.get(otherId) || 0, count)
    );
  });

  return [...historyByPlayerId.entries()]
    .map(([userId, count]) => ({
      userId,
      count,
      name: playerNameById.get(userId) || `ID ${userId}`
    }))
    .filter(item => item.count > 0)
    .sort((left, right) => right.count - left.count ||
      left.name.localeCompare(right.name, 'th'));
}

function formatRelationHistory(history) {
  if (!Array.isArray(history) || history.length === 0) return '-';
  return history
    .map(item => item.count > 1 ? `${item.name} (${item.count})` : item.name)
    .join(', ');
}

function normalizeDropdownPlayer(player) {
  if (!player) return player;

  let partners = [];
  if (Array.isArray(player.partners)) {
    partners = player.partners;
  } else if (typeof player.partners === 'string') {
    partners = player.partners
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  } else if (player.partners && typeof player.partners === 'object') {
    partners = Object.values(player.partners)
      .filter(Boolean)
      .map(item => String(item).trim())
      .filter(Boolean);
  }

  return { ...player, partners };
}

function normalizeDropdownPlayers(players) {
  return Array.isArray(players)
    ? players.map(normalizeDropdownPlayer)
    : [];
}

async function postMatchmakingJson(path, payload) {
  const response = await apiFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    const requestError = new Error(
      responseData.message ||
      responseData.error ||
      `HTTP ${response.status}`
    );
    requestError.data = responseData;
    requestError.status = response.status;
    throw requestError;
  }

  return responseData;
}

function formatApproveRelations(source) {
  const conflict = source?.matchRuleConflicts || source || {};
  const relations = Array.isArray(conflict.relationDetails)
    ? conflict.relationDetails
    : [];
  const lines = [];

  relations.forEach(pair => {
    if (pair.protectedPair) return;
    const names = `${pair.nameA || `Player ${pair.userA}`} ↔ ${pair.nameB || `Player ${pair.userB}`}`;
    if (Number(pair.teammateCount || 0) > 0) {
      lines.push(`👥 เคยคู่กัน: ${names} — ${Number(pair.teammateCount)} รอบ`);
    }
    if (Number(pair.opponentCount || 0) > 0) {
      lines.push(`⚔️ เคยอยู่ฝั่งตรงข้าม: ${names} — ${Number(pair.opponentCount)} รอบ`);
    }
  });

  if (lines.length === 0) {
    const pairs = Array.isArray(source?.recentEncounterPairs)
      ? source.recentEncounterPairs
      : Array.isArray(source?.repeatPartnerPairs)
        ? source.repeatPartnerPairs
        : Array.isArray(conflict.teammateConflicts)
          ? conflict.teammateConflicts
          : [];
    pairs.forEach(pair => {
      const label = pair.relation === 'OPPONENT' ? '⚔️ เคยอยู่ฝั่งตรงข้าม' : '👥 เคยคู่กัน';
      const count = Number(pair.count || pair.teammateCount || pair.opponentCount || 0);
      lines.push(`${label}: ${pair.nameA} ↔ ${pair.nameB}${count ? ` — ${count} รอบ` : ''}`);
    });
  }

  return [...new Set(lines)];
}

function buildApproveHistoryText(source, fallback = 'ไม่พบรายละเอียดรายคู่') {
  const lines = formatApproveRelations(source);
  return lines.length ? `รายละเอียดประวัติผู้เล่น:\n${lines.join('\n')}` : fallback;
}

function RandomMatchPanel({
  groupId,
  allPlayers = [],
  lockedPairs = [],
  pairMap = {},
  opponentMap = {},
  showWaitingAlert = true,
  onCreated,
  displayMode = 'full'
}) {
  const isMonitorAi = displayMode === 'monitor-ai';
  const [mode, setMode] = useState('PICK_ONE');
  const [playerScope, setPlayerScope] = useState('ACTIVE');
  const [priority, setPriority] = useState('WAIT_LONGEST');
  const [pointRange, setPointRange] = useState(100);
  const [playStyle, setPlayStyle] = useState('ANY');
  const [matchCount, setMatchCount] = useState(1);
  const [approvePlaying, setApprovePlaying] = useState(false);
  const [priorityKeyword, setPriorityKeyword] = useState('');
  const [pickedPlayer, setPickedPlayer] = useState(null);
  const [secondPickedPlayer, setSecondPickedPlayer] = useState(null);
  const [pickOnePlayers, setPickOnePlayers] = useState([]);
  const [manualPlayers, setManualPlayers] = useState([
    null,
    null,
    null,
    null
  ]);
  const [manualHistoryApproved, setManualHistoryApproved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewControlsOpen, setPreviewControlsOpen] = useState(true);
  const [previewType, setPreviewType] = useState('ACTIVE_RANK');
  const [previewMatches, setPreviewMatches] = useState([]);
  const [selectedPreviewKeys, setSelectedPreviewKeys] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCreatingIndex, setPreviewCreatingIndex] = useState(null);
  const [previewAlert, setPreviewAlert] = useState(null);
  const [previewHistoryByUserId, setPreviewHistoryByUserId] = useState({});
  const [editingPreviewSlot, setEditingPreviewSlot] = useState(null);
  const [previewEditSearch, setPreviewEditSearch] = useState('');
  const [previewManuallyEdited, setPreviewManuallyEdited] = useState(false);
  const [selectedPreviewRanks, setSelectedPreviewRanks] = useState([]);
  const [perfectMatchOpen, setPerfectMatchOpen] = useState(false);
  const [perfectMatchIntensity, setPerfectMatchIntensity] = useState('MEDIUM');
  const [perfectMatchGender, setPerfectMatchGender] = useState('ANY');
  const [perfectMatchResults, setPerfectMatchResults] = useState([]);
  const [perfectMatchLoading, setPerfectMatchLoading] = useState(false);
  const [perfectMatchCreating, setPerfectMatchCreating] = useState(null);
  const [perfectMatchMessage, setPerfectMatchMessage] = useState(null);
  const [perfectEditingSlot, setPerfectEditingSlot] = useState(null);
  const [perfectEditSearch, setPerfectEditSearch] = useState('');
  const waitingAlertLimit = 5;
  const [waitingAlertMinutes, setWaitingAlertMinutes] = useState(() => {
    const stored = Number(localStorage.getItem('match_waiting_alert_minutes_v2'));
    return [5, 10, 15, 20].includes(stored) ? stored : WAITING_ALERT_MINUTES;
  });
  const [waitingAlertVisible, setWaitingAlertVisible] = useState(false);
  const [waitingAlertPage, setWaitingAlertPage] = useState(0);
  const [playerAlertMode, setPlayerAlertMode] = useState('ALL');
  const [waitingAlertPosition, setWaitingAlertPosition] = useState(() => {
    try { return JSON.parse(localStorage.getItem('match_waiting_alert_position_v5') || 'null'); }
    catch { return null; }
  });
  const [waitingAlertPanelPosition, setWaitingAlertPanelPosition] = useState(null);
  const [previewRealtimeVersion, setPreviewRealtimeVersion] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [dailyStyleByUserId, setDailyStyleByUserId] = useState({});
  const [aiAutoEnabled, setAiAutoEnabled] = useState(false);
  const [aiAutoPanelOpen, setAiAutoPanelOpen] = useState(false);
  const [pickOneAutoAction, setPickOneAutoAction] = useState('PREVIEW');
  const [aiAutoLoading, setAiAutoLoading] = useState(false);
  const [aiAutomationStatus, setAiAutomationStatus] = useState('');
  const [aiActivityLog, setAiActivityLog] = useState([]);
  const [, setAiRecommendation] = useState(null);
  const [aiAutomationCycle, setAiAutomationCycle] = useState(0);
  const [expandedInfoPanel, setExpandedInfoPanel] = useState(null);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [pickAnchorOpen, setPickAnchorOpen] = useState(true);
  const [nearbyPanelOpen, setNearbyPanelOpen] = useState(true);
  const [statusSearchKeyword, setStatusSearchKeyword] = useState('');
  const [nearbySearchKeyword, setNearbySearchKeyword] = useState('');
  const [nearbyGameSort, setNearbyGameSort] = useState('ASC');
  const [nearbyIntensityFilter, setNearbyIntensityFilter] = useState('ALL');
  const [nearbyStatusFilter, setNearbyStatusFilter] = useState('ALL');
  const [selectedNearbyRanks, setSelectedNearbyRanks] = useState([]);
  const [nearbyRankSelectionMode, setNearbyRankSelectionMode] = useState('AUTO');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState(null);
  const [holdManagerOpen, setHoldManagerOpen] = useState(false);
  const [recommendationVisible] = useState(() => {
    try {
      return window.localStorage.getItem(
        RECOMMENDATION_VISIBLE_STORAGE_KEY
      ) !== 'false';
    } catch {
      return true;
    }
  });
  const aiRequestInFlightRef = useRef(false);
  const aiProcessedSignatureRef = useRef('');
  const pickOneAttemptedIdsRef = useRef(new Set());
  const onCreatedRef = useRef(onCreated);
  const previewRealtimeHandledRef = useRef(0);
  const waitingAlertDragRef = useRef(null);
  const waitingAlertMovedRef = useRef(false);

  useEffect(() => {
    if (waitingAlertPosition) localStorage.setItem('match_waiting_alert_position_v5', JSON.stringify(waitingAlertPosition));
  }, [waitingAlertPosition]);

  const moveWaitingAlert = event => {
    const drag = waitingAlertDragRef.current;
    if (!drag) return;
    if (Math.abs(event.clientX - drag.pointerX) + Math.abs(event.clientY - drag.pointerY) > 5) waitingAlertMovedRef.current = true;
    const next = {
      x: Math.max(8, Math.min(window.innerWidth - drag.width - 8, drag.left + event.clientX - drag.pointerX)),
      y: Math.max(8, Math.min(window.innerHeight - drag.height - 8, drag.top + event.clientY - drag.pointerY))
    };
    if (drag.kind === 'panel') setWaitingAlertPanelPosition(next);
    else setWaitingAlertPosition(next);
  };

  const finishWaitingAlertDrag = event => {
    if (!waitingAlertDragRef.current) return;
    waitingAlertDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const appendAiActivity = useCallback((message, type = 'info') => {
    const time = new Date().toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    setAiActivityLog(current => [
      { id: `${Date.now()}-${Math.random()}`, time, message, type },
      ...current
    ].slice(0, 8));
  }, []);

  const canApprovePlaying =
    mode === 'PICK_ONE' || mode === 'MANUAL_4' || mode === 'AI_RANDOM';
  const usesManualSelection = mode === 'MANUAL_4';
  const supportsMultipleMatches = mode === 'AI_RANDOM';
  const usesIndividualPointRange = mode === 'PICK_ONE';
  const usesPointRange = usesIndividualPointRange;

  useEffect(() => {
    onCreatedRef.current = onCreated;
  }, [onCreated]);

  useEffect(() => {
    if (!previewOpen && !perfectMatchOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [previewOpen, perfectMatchOpen]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        RECOMMENDATION_VISIBLE_STORAGE_KEY,
        String(recommendationVisible)
      );
    } catch {
      // หาก localStorage ใช้ไม่ได้ ปุ่มยังเปิด/ปิดได้ในรอบปัจจุบัน
    }
  }, [recommendationVisible]);

  const loadDailyStyles = useCallback(async () => {
    if (!groupId) {
      setDailyStyleByUserId({});
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/users/daily-play-style/${groupId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      const nextStyleMap = {};

      rows.forEach(row => {
        const userId = Number(row.user_id);
        const style = normalizePlayStyleValue(
          row.daily_play_style ?? row.play_style
        );

        if (Number.isInteger(userId) && userId > 0 && style) {
          nextStyleMap[userId] = style;
        }
      });

      setDailyStyleByUserId(nextStyleMap);
    } catch (loadError) {
      console.error('LOAD DAILY PLAY STYLES ERROR:', loadError);
    }
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return undefined;

    const isCurrentGroupEvent = (payload = {}) => {
      const eventGroupId =
        payload.groupId ??
        payload.group_id;

      // ถ้า Backend ส่ง groupId มา ให้ตรวจว่าเป็น Group ปัจจุบัน
      if (eventGroupId !== undefined && eventGroupId !== null) {
        return Number(eventGroupId) === Number(groupId);
      }

      // รองรับกรณี Backend ส่ง groupIds เป็น array
      const eventGroupIds = Array.isArray(payload.groupIds)
        ? payload.groupIds.map(Number)
        : [];

      // ถ้า event ไม่มี groupId ให้ refresh Group ปัจจุบัน
      return (
        eventGroupIds.length === 0 ||
        eventGroupIds.includes(Number(groupId))
      );
    };

    const refreshPlayers = async (payload = {}) => {
      if (!isCurrentGroupEvent(payload)) return;
      setPreviewRealtimeVersion(current => current + 1);

      // โหลด Mood ใหม่
      loadDailyStyles().catch(error => {
        console.error(
          'Realtime daily style refresh error:',
          error
        );
      });

      // GameMatch owns player/queue/match realtime refresh. This child only
      // updates local preview/style state to avoid duplicate parent reloads.
    };

    const joinGroupAndRefresh = () => {
      console.log(
        '🔌 MATCHMAKING JOIN GROUP:',
        Number(groupId)
      );

      // สำคัญ: ต้องเข้า Socket room ของ Group
      socket.emit(
        'join-group',
        Number(groupId)
      );

      // โหลดข้อมูลทันทีหลัง connect
      loadDailyStyles().catch(error => {
        console.error(
          'Initial daily style refresh error:',
          error
        );
      });

    };

    // Socket reconnect
    socket.on(
      'connect',
      joinGroupAndRefresh
    );

    // ถ้า Socket connect อยู่แล้ว
    if (socket.connected) {
      joinGroupAndRefresh();
    }

    // =====================================
    // REALTIME PLAYER EVENTS
    // =====================================

    socket.on(
      'player-added',
      refreshPlayers
    );

    socket.on(
      'member-joined',
      refreshPlayers
    );

    socket.on(
      'member-updated',
      refreshPlayers
    );

    socket.on(
      'player-style-updated',
      refreshPlayers
    );

    // สำคัญสำหรับ ACTIVE / QUEUE / PLAY
    socket.on(
      'player-status-updated',
      refreshPlayers
    );

    // สำคัญสำหรับข้อมูลผู้เล่นที่เปลี่ยน
    socket.on(
      'player-updated',
      refreshPlayers
    );

    socket.on('queue-updated', refreshPlayers);
    socket.on('match-generated', refreshPlayers);
    socket.on('match-updated', refreshPlayers);
    socket.on('match-started', refreshPlayers);
    socket.on('match-ended', refreshPlayers);

    // =====================================
    // POLLING BACKUP
    // =====================================

    const pollingId = window.setInterval(
      () => {
        if (document.visibilityState !== 'visible') return;
        loadDailyStyles().catch(() => { });
      },
      60000
    );

    // เมื่อกลับมาหน้า Browser
    const handleFocus = () => {
      socket.emit(
        'join-group',
        Number(groupId)
      );

      refreshPlayers({
        groupId: Number(groupId)
      });
    };

    window.addEventListener(
      'focus',
      handleFocus
    );

    return () => {
      window.clearInterval(pollingId);

      window.removeEventListener(
        'focus',
        handleFocus
      );

      socket.off(
        'connect',
        joinGroupAndRefresh
      );

      socket.off(
        'player-added',
        refreshPlayers
      );

      socket.off(
        'member-joined',
        refreshPlayers
      );

      socket.off(
        'member-updated',
        refreshPlayers
      );

      socket.off(
        'player-style-updated',
        refreshPlayers
      );

      socket.off(
        'player-status-updated',
        refreshPlayers
      );

      socket.off(
        'player-updated',
        refreshPlayers
      );

      socket.off('queue-updated', refreshPlayers);
      socket.off('match-generated', refreshPlayers);
      socket.off('match-updated', refreshPlayers);
      socket.off('match-started', refreshPlayers);
      socket.off('match-ended', refreshPlayers);
    };
  }, [
    groupId,
    loadDailyStyles
  ]);

  const normalizedPlayers = useMemo(
    () => allPlayers.map(player => {

      const userId =
        Number(player.user_id);

      const dailyPlayStyle =
        normalizePlayStyleValue(
          dailyStyleByUserId[userId] ??
          player.daily_play_style ??
          player.play_style ??
          player.dailyPlayStyle
        );


      /* =================================================
         NORMALIZE PARTNERS
         Backend บางครั้งส่ง Array
         บางครั้งส่ง string จาก GROUP_CONCAT
      ================================================= */

      let partners = [];

      if (Array.isArray(player.partners)) {

        partners =
          player.partners
            .filter(Boolean)
            .map(item =>
              String(item).trim()
            )
            .filter(Boolean);

      } else if (
        typeof player.partners === 'string'
      ) {

        partners =
          player.partners
            .split(',')
            .map(item =>
              item.trim()
            )
            .filter(Boolean);

      } else {

        partners = [];

      }


      return normalizeDropdownPlayer({
        ...player,

        partners,

        daily_play_style:
          dailyPlayStyle,

        status:
          getStatus(player),

        memberStatus:
          getStatus(player)
      });

    }),

    [
      allPlayers,
      dailyStyleByUserId
    ]
  );
  const playerNameById = useMemo(() => new Map(
    normalizedPlayers.map(player => [
      Number(player.user_id),
      String(player.name || `ID ${player.user_id}`)
    ])
  ), [normalizedPlayers]);

  useEffect(() => {
    if (
      !previewOpen ||
      previewMatches.length === 0 ||
      previewManuallyEdited
    ) return;

    const latestPlayerById = new Map(
      normalizedPlayers.map(player => [Number(player.user_id), player])
    );
    const nextMatches = previewMatches.filter(match =>
      [...(match.team1 || []), ...(match.team2 || [])].every(previewPlayer => {
        const latestPlayer = latestPlayerById.get(Number(previewPlayer.user_id));
        // ผล Preview เพิ่งมาจาก Backend จึงใหม่กว่า allPlayers ของ parent ซึ่ง
        // อาจยังไม่ทัน socket; ห้ามลบ Match เพียงเพราะหา player ใน state เก่าไม่พบ
        if (!latestPlayer) return true;
        const previewStatus = getStatus(previewPlayer);
        const effectiveStatus = previewStatus || getStatus(latestPlayer);
        if (effectiveStatus === 'HOLD') return false;
        if (getQueueMatchId(latestPlayer)) return false;
        return previewType !== 'ACTIVE_RANK' ||
          ['ACTIVE', 'PLAY'].includes(effectiveStatus);
      })
    );

    if (nextMatches.length !== previewMatches.length) {
      setPreviewMatches(nextMatches);
      setPreviewAlert({
        type: 'info',
        text: 'อัปเดต Preview แล้ว: นำ Match ที่มีผู้เล่นซึ่งถูกจัด Queue ออก'
      });
    }
  }, [
    normalizedPlayers,
    previewMatches,
    previewOpen,
    previewType,
    previewManuallyEdited
  ]);

  const activeStyleCounts = useMemo(() => {
    const counts = {
      FAST_POWER_MIX: 0,
      TIGHT_MEDIUM_HEAVY: 0,
      ALL_ROUND_READY: 0,
      ENJOY_FUN: 0
    };
    let totalActive = 0;
    let missingStyle = 0;

    normalizedPlayers.forEach(player => {
      if (getStatus(player) !== 'ACTIVE') return;
      totalActive += 1;

      if (player.daily_play_style && counts[player.daily_play_style] != null) {
        counts[player.daily_play_style] += 1;
      } else {
        missingStyle += 1;
      }
    });

    return { counts, totalActive, missingStyle };
  }, [normalizedPlayers]);

  const minimumStylePlayers = supportsMultipleMatches
    ? matchCount * 4
    : 4;

  const styleMatchingPlayers = useMemo(
    () => playStyle === 'ANY'
      ? normalizedPlayers
      : normalizedPlayers.filter(
        player => player.daily_play_style === playStyle
      ),
    [normalizedPlayers, playStyle]
  );

  const styleMatchingActiveCount = useMemo(
    () => styleMatchingPlayers.filter(
      player => getStatus(player) === 'ACTIVE'
    ).length,
    [styleMatchingPlayers]
  );

  const styleEligiblePlayers = useMemo(
    () => (
      mode === 'AI_RANDOM' || playStyle === 'ANY'
        ? normalizedPlayers
        : styleMatchingPlayers
    ),
    [
      mode,
      normalizedPlayers,
      playStyle,
      styleMatchingPlayers
    ]
  );

  const activePlayers = useMemo(
    () => styleEligiblePlayers.filter(
      player => getStatus(player) === 'ACTIVE'
    ),
    [styleEligiblePlayers]
  );

  const aiOrderedActivePlayers = useMemo(
    () => normalizedPlayers
      .filter(player => getStatus(player) === 'ACTIVE')
      .sort((left, right) =>
        comparePlayersByPriority(left, right, 'FIFO')
      ),
    [normalizedPlayers]
  );

  const aiEligiblePlayingPlayers = useMemo(
    () => normalizedPlayers.filter(player => {
      if (getStatus(player) !== 'PLAY') return false;
      const startedAt = getQueueStartedAt(player);
      return (
        Number.isFinite(startedAt) &&
        startedAt !== Number.MAX_SAFE_INTEGER &&
        now - startedAt >= 10 * 60 * 1000
      );
    }),
    [normalizedPlayers, now]
  );

  const aiActiveSignature = useMemo(
    () => [...aiOrderedActivePlayers, ...aiEligiblePlayingPlayers].map(player => [
      Number(player.user_id),
      getStatus(player),
      Number(player.point || 0),
      player.level_rank || '',
      player.daily_play_style || '',
      player.active_since ||
      player.last_finished_at ||
      player.checkin_time ||
      ''
    ].join(':')).join('|'),
    [aiEligiblePlayingPlayers, aiOrderedActivePlayers]
  );

  const aiLockSignature = useMemo(
    () => lockedPairs
      .map(lock => [
        Number(lock.user_a),
        Number(lock.user_b)
      ].sort((left, right) => left - right).join('-'))
      .sort()
      .join('|'),
    [lockedPairs]
  );

  const pickOneAutoSignature = mode === 'PICK_ONE'
    ? [
      Number(pickedPlayer?.user_id || 0),
      ...pickOnePlayers.map(player => Number(player.user_id)),
      pointRange,
      priority,
      approvePlaying ? 1 : 0
    ].join(':')
    : '';
  const aiMatchmakingSignature =
    `${mode}::${aiActiveSignature}::${aiLockSignature}::${matchCount}::${pickOneAutoSignature}` +
    `::approve-play-${approvePlaying ? 1 : 0}::` +
    (approvePlaying
      ? normalizedPlayers
        .filter(player => getStatus(player) === 'PLAY')
        .map(player => Number(player.user_id))
        .sort((left, right) => left - right)
        .join('-')
      : '');

  const availableMatchCount = Math.min(
    15,
    Math.floor(activePlayers.length / 4)
  );
  const maxMatchCount =
    mode === 'AI_RANDOM'
      ? 15
      : Math.max(1, availableMatchCount);

  const requiredPlayerCount = supportsMultipleMatches
    ? matchCount * 4
    : 4;

  const selectablePlayers = useMemo(() => {
    if (playerScope === 'ACTIVE' && !approvePlaying) return activePlayers;

    // เมื่อเปิด Approve ให้ช่องค้นหาเห็น ACTIVE, PLAY และ QUEUE
    // แต่ HOLD/CHECKOUT ยังไม่ใช่รายชื่อพร้อมเลือกจาก Search
    return styleEligiblePlayers.filter(
      player => !['HOLD', 'CHECKOUT'].includes(getStatus(player))
    );
  }, [
    activePlayers,
    approvePlaying,
    playerScope,
    styleEligiblePlayers
  ]);

  const strictStyleAvailableCount =
    canApprovePlaying && approvePlaying
      ? selectablePlayers.length
      : activePlayers.length;
  const lacksRequiredStylePlayers =
    playStyle !== 'ANY' &&
    strictStyleAvailableCount < minimumStylePlayers;

  const orderedSelectablePlayers = useMemo(
    () => [...selectablePlayers].sort(
      (left, right) => (
        mode === 'PICK_ONE'
          ? getPickOneStatusPriority(left) - getPickOneStatusPriority(right) ||
          getPlayerGames(left) - getPlayerGames(right) ||
          comparePlayersByPriority(left, right, 'WAIT_LONGEST')
          : comparePlayersByPriority(left, right, priority)
      )
    ),
    [mode, priority, selectablePlayers]
  );

  /* =================================================
     PICK ONE ANCHOR
  
     ผู้เล่นหลักต้อง:
     - ACTIVE
     - ยังไม่อยู่ Match Queue
     - ไม่ใช่ PLAY
     - ไม่เกี่ยวกับ Approve
  
     Approve ใช้กับ "ผู้เล่นที่เลือกเพิ่ม" เท่านั้น
  ================================================= */

  const pickOneAnchorPlayers = useMemo(() => {

    return normalizedPlayers
      .filter(player => {

        const status = getStatus(player);

        const queueMatchNo =
          Number(
            player.queueMatchNo ??
            player.queue_match_no ??
            0
          );

        const queueMatchId =
          Number(
            player.queueMatchId ??
            player.queue_match_id ??
            0
          );

        return (
          (approvePlaying || playerScope === 'ALL' || status === 'ACTIVE') &&
          !['HOLD', 'CHECKOUT'].includes(status) &&
          (approvePlaying || (!queueMatchNo && !queueMatchId))
        );

      })
      .sort((left, right) => {
        const hasReadyLock = player => {
          const playerId = Number(player.user_id);
          const lock = lockedPairs.find(item =>
            Number(item.user_a) === playerId || Number(item.user_b) === playerId
          );
          if (!lock) return false;
          const partnerId = Number(lock.user_a) === playerId
            ? Number(lock.user_b)
            : Number(lock.user_a);
          const partner = normalizedPlayers.find(item => Number(item.user_id) === partnerId);
          return Boolean(partner && getStatus(partner) !== 'HOLD' && !getQueueMatchId(partner));
        };
        const lockPriority = Number(hasReadyLock(right)) - Number(hasReadyLock(left));
        if (lockPriority !== 0) return lockPriority;
        // Pick One Auto: รอนานสุด > มาก่อน > ลงเล่นน้อยสุด
        const waitDiff = getQueueStartedAt(left) - getQueueStartedAt(right);
        if (waitDiff !== 0) return waitDiff;
        const arrivalDiff = getArrivalTime(left) - getArrivalTime(right);
        if (arrivalDiff !== 0) return arrivalDiff;
        const gameDiff = getPlayerGames(left) - getPlayerGames(right);
        if (gameDiff !== 0) return gameDiff;
        return Number(left.user_id) - Number(right.user_id);

      });

  }, [approvePlaying, lockedPairs, normalizedPlayers, playerScope]);


  /*
   * รายชื่อสำหรับเลือกเพิ่ม
   * ยังใช้ Approve ได้ตามเดิม
   */
  const pickOneSelectablePlayers = useMemo(
    () => orderedSelectablePlayers,
    [orderedSelectablePlayers]
  );
  function getAvatarSource(player) {

    const avatarUrl =
      player?.avatar_url ||
      player?.avatarUrl ||
      player?.avatar ||
      '';

    if (!avatarUrl) {
      return '';
    }

    const value =
      String(avatarUrl).trim();

    return resolveAvatarSource(value);
  }

  function PlayerAvatar({
    player,
    size = 'normal'
  }) {

    const avatarSource =
      getAvatarSource(player);

    const firstLetter =
      String(
        player?.name || '🏸'
      )
        .trim()
        .charAt(0)
        .toUpperCase();

    return (
      <span
        className={`rmp-player-avatar ${size}`}
      >

        {avatarSource ? (
          <img
            src={avatarSource}
            alt={player?.name || 'Player'}
            onError={event => {
              event.currentTarget.style.display =
                'none';

              const fallback =
                event.currentTarget.nextElementSibling;

              if (fallback) {
                fallback.style.display =
                  'grid';
              }
            }}
          />
        ) : null}

        <span
          className="rmp-player-avatar-fallback"
          style={{
            display:
              avatarSource
                ? 'none'
                : 'grid'
          }}
        >
          {firstLetter || '🏸'}
        </span>

      </span>
    );
  }
  const statusSearchResults = useMemo(() => {
    const keyword = statusSearchKeyword.trim();
    if (!keyword) return [];

    return normalizedPlayers
      .filter(player =>
        matchesSearch(player.name, keyword)
      )
      .sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      )
      .slice(0, 12);
  }, [normalizedPlayers, statusSearchKeyword]);

  const heldPlayers = useMemo(
    () => normalizedPlayers
      .filter(player => getStatus(player) === 'HOLD')
      .sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      ),
    [normalizedPlayers]
  );

  const visibleStatusPlayers = statusSearchKeyword.trim()
    ? statusSearchResults
    : heldPlayers;

  const updateAvailabilityStatus = async (player, nextStatus) => {
    const userId = Number(player?.user_id);
    if (!Number.isInteger(userId) || statusUpdatingId) return;

    setStatusUpdatingId(userId);
    setStatusUpdateMessage(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/users/${userId}/group/${Number(groupId)}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: nextStatus })
        }
      );
      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP ${response.status}`);
      }

      setStatusUpdateMessage({
        type: 'success',
        text: `${player.name} เปลี่ยนเป็น ${nextStatus} แล้ว`
      });

      if (onCreatedRef.current) {
        await onCreatedRef.current(responseData);
      }
    } catch (statusError) {
      setStatusUpdateMessage({
        type: 'error',
        text: statusError.message || 'อัปเดตสถานะไม่สำเร็จ'
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // Pick One ใช้ผู้เล่นคนแรกของลิสต์เป็นค่าเริ่มต้นเสมอ เมื่อคนเดิม
  // เปลี่ยนเป็น QUEUE/PLAY หรือไม่อยู่ในเงื่อนไขที่เลือกได้ ระบบจะเลื่อนไปคนถัดไป
  /* =================================================
   PICK ONE AUTO ADVANCE
================================================= */

  useEffect(() => {

    if (mode !== 'PICK_ONE') {
      return;
    }

    const currentId =
      Number(pickedPlayer?.user_id || 0);


    /* =================================================
       เช็กว่าผู้เล่นหลักปัจจุบันยังเป็น Anchor ที่ถูกต้องไหม
    ================================================= */

    const currentPlayer =
      pickOneAnchorPlayers.find(
        player =>
          Number(player.user_id) === currentId
      );


    /* =================================================
       ถ้ายัง ACTIVE และไม่อยู่ Queue
       ให้ใช้คนเดิม
    ================================================= */

    if (currentPlayer) {

      /*
       * ตรวจรายชื่อที่เลือกเพิ่มด้วย
       */
      const selectableIds =
        new Set(
          pickOneSelectablePlayers.map(
            player =>
              Number(player.user_id)
          )
        );


      const validAdditionalPlayers =
        pickOnePlayers.filter(
          player =>
            selectableIds.has(
              Number(player.user_id)
            )
        );


      if (
        validAdditionalPlayers.length !==
        pickOnePlayers.length
      ) {

        setPickOnePlayers(
          validAdditionalPlayers
        );

        setError('');

        setAiRecommendation(null);

        aiProcessedSignatureRef.current = '';

      }


      return;
    }


    /* =================================================
       คนเดิมไม่พร้อมแล้ว
       เช่น ACTIVE -> QUEUE
       เลื่อนไปคนใหม่ทันที
    ================================================= */

    const nextPlayer =
      pickOneAnchorPlayers[0] || null;


    // Effect นี้พึ่งพา state ชุดนี้อยู่ จึงต้องไม่สร้าง state ใหม่หากค่าเดิม
    // เพื่อป้องกัน render loop เมื่อไม่มีผู้เล่นหลักที่เลือกได้
    if (Number(pickedPlayer?.user_id || 0) !== Number(nextPlayer?.user_id || 0)) {
      setPickedPlayer(nextPlayer);
    }

    setPickOnePlayers(current => (current.length > 0 ? [] : current));
    setNearbySearchKeyword(current => (current ? '' : current));
    setError(current => (current ? '' : current));
    setAiRecommendation(current => (current === null ? current : null));

    aiProcessedSignatureRef.current = '';

    pickOneAttemptedIdsRef.current.clear();


    const nextAutomationStatus = nextPlayer
      ? `✅ เลื่อนไปผู้เล่นหลักคนถัดไป: ${nextPlayer.name}`
      : 'กำลังรอผู้เล่น ACTIVE คนถัดไป';

    setAiAutomationStatus(current =>
      current === nextAutomationStatus ? current : nextAutomationStatus
    );

  }, [
    mode,
    pickedPlayer,
    pickOnePlayers,
    pickOneAnchorPlayers,
    pickOneSelectablePlayers
  ]);

  const advancePickOneAfterQueue = queuedPlayers => {
    const queuedIds = new Set(
      (queuedPlayers || [])
        .map(player => Number(player?.user_id ?? player))
        .filter(Number.isFinite)
    );
    const currentId = Number(pickedPlayer?.user_id || 0);
    const currentWasQueued = queuedIds.has(currentId);

    // Preview อาจใช้ข้อมูล parent ก่อน socket รอบล่าสุด จึงตัดผู้เล่นที่เพิ่ง
    // สร้าง Queue ออกจาก candidate โดยตรง เพื่อให้ Anchor เปลี่ยนแน่นอนทันที
    const nextPlayer = pickOneAnchorPlayers.find(player => {
      const playerId = Number(player.user_id);
      return playerId !== currentId && !queuedIds.has(playerId);
    }) || null;

    if (currentWasQueued) {
      setPickedPlayer(nextPlayer);
      setSecondPickedPlayer(null);
      setPickOnePlayers([]);
      setNearbySearchKeyword('');
      setAiRecommendation(null);
      aiProcessedSignatureRef.current = '';
      pickOneAttemptedIdsRef.current.clear();
      setAiAutomationStatus(
        nextPlayer
          ? `สร้าง Queue สำเร็จ • เปลี่ยนผู้เล่นหลักเป็น ${nextPlayer.name}`
          : 'สร้าง Queue สำเร็จ • กำลังรอผู้เล่นหลักคนใหม่ที่พร้อมลงสนาม'
      );
    }

    return nextPlayer;
  };
  const priorityPlayers = useMemo(() => {
    const search = priorityKeyword.trim();
    if (!search) return orderedSelectablePlayers;

    return orderedSelectablePlayers.filter(player => [
      player.name,
      player.point,
      player.level_rank,
      getPlayStyleLabel(player.daily_play_style),
      getPlayerGames(player)
    ]
      .filter(value => value !== null && value !== undefined)
      .some(value => matchesSearch(value, search)));
  }, [orderedSelectablePlayers, priorityKeyword]);

  const selectedManualPlayers = manualPlayers.filter(Boolean);
  const isProtectedPair = useCallback((left, right) => lockedPairs.some(pair => {
    const pairIds = new Set([Number(pair.user_a), Number(pair.user_b)]);
    return pairIds.has(Number(left?.user_id)) && pairIds.has(Number(right?.user_id));
  }), [lockedPairs]);
  const manualRelations = useMemo(() => {
    if (manualPlayers.some(player => !player)) return [];
    const [a, b, c, d] = manualPlayers;
    return [
      { type: 'TEAMMATE', names: `${a.name} + ${b.name}`, count: getRelationCount(pairMap, a, b), protectedPair: isProtectedPair(a, b) },
      { type: 'TEAMMATE', names: `${c.name} + ${d.name}`, count: getRelationCount(pairMap, c, d), protectedPair: isProtectedPair(c, d) },
      ...[[a,c],[a,d],[b,c],[b,d]].map(([left, right]) => ({
        type: 'OPPONENT', names: `${left.name} ↔ ${right.name}`, count: getRelationCount(opponentMap, left, right), protectedPair: isProtectedPair(left, right)
      }))
    ];
  }, [manualPlayers, opponentMap, pairMap, isProtectedPair]);
  const manualRepeatAlerts = manualRelations.filter(item => item.count > 0 && !item.protectedPair);
  const manualApprovalAlerts = manualRelations.filter(item => item.count >= 2 && !item.protectedPair);
  const manualPlayerSignature = manualPlayers
    .map(player => Number(player?.user_id || 0))
    .join('-');
  const selectedPriorityIdSet = new Set(
    selectedManualPlayers.map(player => Number(player.user_id))
  );
  const orderedPrioritySelections = orderedSelectablePlayers.filter(
    player => selectedPriorityIdSet.has(Number(player.user_id))
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setManualHistoryApproved(false);
  }, [manualPlayerSignature]);

  useEffect(() => {
    setApprovePlaying(false);
    setPriority('WAIT_LONGEST');
    setPointRange(100);
    setPlayStyle('ANY');
    setMatchCount(1);
    setPriorityKeyword('');
    setPickedPlayer(null);
    setSecondPickedPlayer(null);
    setPickOnePlayers([]);
    setManualPlayers([null, null, null, null]);
    setLastResult(null);
    setError('');
    setAiAutoEnabled(false);
    setAiAutoLoading(false);
    setAiAutomationStatus('');
    setAiRecommendation(null);
    setAdvancedSettingsOpen(false);
    setPickAnchorOpen(true);
    setNearbyPanelOpen(true);
    setExpandedInfoPanel(null);
    aiProcessedSignatureRef.current = '';
    pickOneAttemptedIdsRef.current.clear();
  }, [groupId]);

  useEffect(() => {
    if (!supportsMultipleMatches) {
      setMatchCount(1);
      return;
    }

    setMatchCount(current => Math.min(current, maxMatchCount));
  }, [maxMatchCount, supportsMultipleMatches]);

  const resetInputs = () => {
    setPriorityKeyword('');
    setPickedPlayer(null);
    setSecondPickedPlayer(null);
    setPickOnePlayers([]);
    setManualPlayers([null, null, null, null]);
  };

  const togglePriorityPlayer = player => {
    const playerId = Number(player.user_id);
    const lockedPartner = getLockedPartner(player);
    const partnerStatus = getStatus(lockedPartner);

    if (lockedPartner && partnerStatus !== 'ACTIVE') {
      setError(
        `คู่ล็อก ${lockedPartner.name} อยู่สถานะ ${partnerStatus || 'ไม่พร้อม'} ` +
        'จึงยังเลือกคู่นี้ไม่ได้'
      );
      return;
    }

    const selectionUnit = [player, lockedPartner]
      .filter(Boolean)
      .filter((item, index, items) =>
        items.findIndex(
          candidate =>
            Number(candidate.user_id) === Number(item.user_id)
        ) === index
      );
    const selectionIds = new Set(
      selectionUnit.map(item => Number(item.user_id))
    );
    const alreadySelected = selectedManualPlayers.some(
      item => Number(item.user_id) === playerId
    );
    const addingPlayers = selectionUnit.filter(
      item => !selectedManualPlayers.some(
        selected =>
          Number(selected.user_id) === Number(item.user_id)
      )
    );

    if (
      !alreadySelected &&
      selectedManualPlayers.length + addingPlayers.length > 4
    ) {
      setError(
        lockedPartner
          ? `ต้องเลือก ${player.name} พร้อมคู่ล็อก ${lockedPartner.name} ` +
          'แต่จำนวนผู้เล่นจะเกิน 4 คน'
          : 'เลือกผู้เล่นได้สูงสุด 4 คน กรุณาเอาคนเดิมออกก่อน'
      );
      return;
    }

    setError('');
    setManualPlayers(currentPlayers => {
      const currentSelected = currentPlayers.filter(Boolean);

      if (alreadySelected) {
        const remaining = currentSelected.filter(
          item => !selectionIds.has(Number(item.user_id))
        );
        return [...remaining, ...Array(4 - remaining.length).fill(null)];
      }

      const nextSelected = [...currentSelected, ...addingPlayers];
      return [...nextSelected, ...Array(4 - nextSelected.length).fill(null)];
    });
  };

  const handleApproveChange = enabled => {
    setApprovePlaying(enabled);
    setError('');

    if (!enabled) {
      if (pickedPlayer && getStatus(pickedPlayer) !== 'ACTIVE') {
        setPickedPlayer(null);
        setSecondPickedPlayer(null);
        setPickOnePlayers([]);
      }

      setManualPlayers(currentPlayers =>
        currentPlayers.map(player =>
          player && getStatus(player) !== 'ACTIVE' ? null : player
        )
      );
    }
  };

  const getLockedPartner = useCallback(player => {
    if (!player) return null;

    const playerId = Number(player.user_id);
    const lock = lockedPairs.find(item =>
      Number(item.user_a) === playerId ||
      Number(item.user_b) === playerId
    );

    if (!lock) return null;

    const partnerId =
      Number(lock.user_a) === playerId
        ? Number(lock.user_b)
        : Number(lock.user_a);

    return normalizedPlayers.find(
      item => Number(item.user_id) === partnerId
    ) || null;
  }, [lockedPairs, normalizedPlayers]);

  const pickedLockedPartner = getLockedPartner(pickedPlayer);
  const automaticNearbyRanks = useMemo(() => {
    if (!pickedPlayer) return [];

    const anchorRank = normalizePlayerRank(
      pickedPlayer.level_rank,
      pickedPlayer.point
    );
    const anchorIndex = RANK_ORDER.indexOf(anchorRank);
    if (anchorIndex < 0) return [];

    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(secondPickedPlayer?.user_id),
      Number(pickedLockedPartner?.user_id)
    ].filter(Number.isFinite));
    const activeCountByRank = new Map(RANK_ORDER.map(rank => [rank, 0]));

    normalizedPlayers.forEach(player => {
      if (fixedIds.has(Number(player.user_id)) || getStatus(player) !== 'ACTIVE') return;
      const rank = normalizePlayerRank(player.level_rank, player.point);
      activeCountByRank.set(rank, Number(activeCountByRank.get(rank) || 0) + 1);
    });

    const selected = [anchorRank];
    const rankCandidates = RANK_ORDER
      .filter(rank => rank !== anchorRank)
      .map(rank => ({
        rank,
        distance: Math.abs(RANK_ORDER.indexOf(rank) - anchorIndex),
        activeCount: Number(activeCountByRank.get(rank) || 0)
      }));

    // เติม Rank ที่มี ACTIVE ก่อน โดยไล่จากระดับที่ใกล้ผู้เล่นหลักที่สุด
    rankCandidates
      .filter(item => item.activeCount > 0)
      .sort((left, right) =>
        left.distance - right.distance ||
        right.activeCount - left.activeCount ||
        RANK_ORDER.indexOf(left.rank) - RANK_ORDER.indexOf(right.rank)
      )
      .forEach(item => {
        if (selected.length < 4) selected.push(item.rank);
      });

    // หาก Rank ที่มี ACTIVE ไม่ครบ 4 ให้เติม Rank ใกล้เคียงที่เหลือจนเต็ม
    rankCandidates
      .filter(item => !selected.includes(item.rank))
      .sort((left, right) =>
        left.distance - right.distance ||
        RANK_ORDER.indexOf(left.rank) - RANK_ORDER.indexOf(right.rank)
      )
      .forEach(item => {
        if (selected.length < 4) selected.push(item.rank);
      });

    return selected;
  }, [normalizedPlayers, pickedLockedPartner, pickedPlayer, secondPickedPlayer]);

  useEffect(() => {
    if (mode !== 'PICK_ONE' || nearbyRankSelectionMode !== 'AUTO') return;
    setSelectedNearbyRanks(current =>
      current.length === automaticNearbyRanks.length &&
      current.every((rank, index) => rank === automaticNearbyRanks[index])
        ? current
        : automaticNearbyRanks
    );
  }, [automaticNearbyRanks, mode, nearbyRankSelectionMode]);
  const pickOneOrderedPlayers = useMemo(() => {
    const ordered = [
      pickedPlayer,
      secondPickedPlayer,
      pickedLockedPartner,
      ...pickOnePlayers
    ]
      .filter(Boolean);

    return ordered.filter((player, index, players) =>
      players.findIndex(
        item => Number(item.user_id) === Number(player.user_id)
      ) === index
    );
  }, [pickedLockedPartner, pickedPlayer, secondPickedPlayer, pickOnePlayers]);

  const pickOneNearbyCandidates = useMemo(() => {
    if (!pickedPlayer) return [];

    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(secondPickedPlayer?.user_id),
      Number(pickedLockedPartner?.user_id)
    ].filter(Number.isFinite));

    const keyword = nearbySearchKeyword.trim();
    return normalizedPlayers
      .filter(player => !fixedIds.has(Number(player.user_id)))
      .filter(player =>
        selectedNearbyRanks.length === 0 ||
        selectedNearbyRanks.includes(
          normalizePlayerRank(player.level_rank, player.point)
        )
      )
      .filter(player =>
        nearbyIntensityFilter === 'ALL' ||
        String(player.play_intensity || 'ANY').toUpperCase() === nearbyIntensityFilter
      )
      .filter(player =>
        nearbyStatusFilter === 'ALL' || getStatus(player) === nearbyStatusFilter
      )
      .filter(player =>
        !keyword || matchesSearch(player.name, keyword)
      )
      .sort((left, right) => {
        const rankDistanceDiff =
          getRankDistance(pickedPlayer, left) -
          getRankDistance(pickedPlayer, right);
        if (rankDistanceDiff !== 0) return rankDistanceDiff;

        const rankOrderDiff =
          RANK_ORDER.indexOf(normalizePlayerRank(left.level_rank, left.point)) -
          RANK_ORDER.indexOf(normalizePlayerRank(right.level_rank, right.point));
        if (rankOrderDiff !== 0) return rankOrderDiff;

        const intensityDiff =
          getNearbyIntensityPriority(pickedPlayer, left) -
          getNearbyIntensityPriority(pickedPlayer, right);
        if (intensityDiff !== 0) return intensityDiff;

        const statusDiff =
          getPickOneStatusPriority(left) - getPickOneStatusPriority(right);
        if (statusDiff !== 0) return statusDiff;

        const gameDiff = getPlayerGames(left) - getPlayerGames(right);
        if (gameDiff !== 0) {
          return nearbyGameSort === 'DESC' ? -gameDiff : gameDiff;
        }

        return (
          Math.abs(Number(left.point || 0) - Number(pickedPlayer.point || 0)) -
          Math.abs(Number(right.point || 0) - Number(pickedPlayer.point || 0)) ||
          comparePlayersByPriority(left, right, priority)
        );
      });
  }, [
    nearbyGameSort,
    nearbyIntensityFilter,
    nearbyStatusFilter,
    nearbySearchKeyword,
    normalizedPlayers,
    pickedLockedPartner,
    selectedNearbyRanks,
    secondPickedPlayer,
    pickedPlayer,
    priority
  ]);

  const pickOneNearbyPlayers = pickOneNearbyCandidates;

  useEffect(() => {
    setNearbySearchKeyword('');
  }, [pickedPlayer?.user_id]);

  const toggleNearbyRank = rank => {
    setNearbyRankSelectionMode('MANUAL');
    setSelectedNearbyRanks(current => {
      if (current.includes(rank)) {
        return current.filter(item => item !== rank);
      }
      if (current.length >= 4) {
        setError('เลือก Rank ผู้เล่นใกล้เคียงได้สูงสุด 4 Rank');
        return current;
      }
      setError('');
      return [...current, rank];
    });
  };
  const togglePickOneNearbyPlayer = player => {
    if (!pickedPlayer) return;

    const playerId = Number(player.user_id);
    const candidatePartner = getLockedPartner(player);
    const anchorPartner = getLockedPartner(pickedPlayer);
    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(secondPickedPlayer?.user_id),
      Number(anchorPartner?.user_id)
    ].filter(Number.isFinite));
    const selectionUnit = [player, candidatePartner]
      .filter(Boolean)
      .filter(item => !fixedIds.has(Number(item.user_id)))
      .filter((item, index, items) =>
        items.findIndex(
          candidate => Number(candidate.user_id) === Number(item.user_id)
        ) === index
      );
    const unitIds = new Set(
      selectionUnit.map(item => Number(item.user_id))
    );
    const alreadySelected = pickOnePlayers.some(
      item => Number(item.user_id) === playerId
    );

    if (alreadySelected) {
      setPickOnePlayers(current => current.filter(
        item => !unitIds.has(Number(item.user_id))
      ));
      setError('');
      return;
    }

    const unavailablePlayer = selectionUnit.find(
      item => getStatus(item) !== 'ACTIVE'
    );
    if (unavailablePlayer && !approvePlaying) {
      setError(
        `${unavailablePlayer.name} อยู่สถานะ ${getStatus(unavailablePlayer)} ` +
        'กรุณาเปิด Approve สถานะอื่นเพื่อจัด Match ล่วงหน้า'
      );
      return;
    }

    const currentIds = new Set(
      pickOnePlayers.map(item => Number(item.user_id))
    );
    const addingPlayers = selectionUnit.filter(
      item => !currentIds.has(Number(item.user_id))
    );
    const maximumAdditionalPlayers = 4 - fixedIds.size;

    if (pickOnePlayers.length + addingPlayers.length > maximumAdditionalPlayers) {
      setError(
        `Pick One เลือกเพิ่มได้อีกไม่เกิน ${maximumAdditionalPlayers} คน ` +
        '(รวม Locked Pair ต้องมีผู้เล่นไม่เกิน 4 คน)'
      );
      return;
    }

    setPickOnePlayers(current => [...current, ...addingPlayers]);
    setError('');
  };

  const selectedNonActivePlayers = () => {
    if (mode === 'PICK_ONE') {
      const lockedPartner = getLockedPartner(pickedPlayer);
      return [pickedPlayer, lockedPartner, ...pickOnePlayers]
        .filter(Boolean)
        .filter(player => getStatus(player) !== 'ACTIVE')
        .filter((player, index, players) =>
          players.findIndex(
            item => Number(item.user_id) === Number(player.user_id)
          ) === index
        );
    }

    if (usesManualSelection) {
      return manualPlayers.filter(
        player => player && getStatus(player) !== 'ACTIVE'
      );
    }

    return [];
  };

  const validate = () => {
    if (!groupId) return 'ไม่พบ Group';

    if (
      AUTOMATIC_MODES.has(mode) &&
      activePlayers.length < requiredPlayerCount
    ) {
      return (
        `มีผู้เล่น ACTIVE ${activePlayers.length} คน ` +
        `แต่การสร้าง ${matchCount} Match ต้องใช้ ${requiredPlayerCount} คน`
      );
    }

    if (mode === 'PICK_ONE' && !pickedPlayer) {
      return 'กรุณาเลือกผู้เล่นที่ต้องการล็อก';
    }

    if (mode === 'PICK_ONE') {
      const pickedId = Number(pickedPlayer.user_id);
      const lockedPartner = getLockedPartner(pickedPlayer);
      const pickedLock = lockedPairs.find(item =>
        Number(item.user_a) === pickedId ||
        Number(item.user_b) === pickedId
      );

      if (pickedLock && !lockedPartner) {
        return (
          `ไม่พบคู่ล็อกของ ${pickedPlayer.name} ในรายชื่อผู้เล่นที่ใช้งานได้`
        );
      }

      if (lockedPartner) {
        const partnerStatus = getStatus(lockedPartner);

        if (partnerStatus !== 'ACTIVE' && !approvePlaying) {
          return (
            `คู่ล็อก ${lockedPartner.name} อยู่สถานะ ${partnerStatus} ` +
            'กรุณาเปิด Approve สถานะอื่น'
          );
        }

      }

      const approvedPickIds = new Set(
        pickOneOrderedPlayers.map(player => Number(player.user_id))
      );
      const allowedPlayers = normalizedPlayers.filter(player => {
        const status = getStatus(player);
        return (
          status === 'ACTIVE' ||
          (
            approvePlaying &&
            approvedPickIds.has(Number(player.user_id))
          )
        );
      });

      if (allowedPlayers.length < 4) {
        return (
          `ผู้เล่นที่อยู่ในสถานะพร้อมมีเพียง ${allowedPlayers.length} คน ` +
          'ต้องมีอย่างน้อย 4 คน'
        );
      }

      // approvedPickIds มีผู้เล่นหลัก, Locked Pair และผู้เล่นที่เลือกเพิ่ม
      // ครบอยู่แล้ว ห้ามบวก pickOnePlayers ซ้ำอีกครั้ง
      if (approvedPickIds.size > 4) {
        return 'Pick One มีผู้เล่นที่เลือกและ Locked Pair รวมเกิน 4 คน';
      }
    }

    if (mode === 'PRIORITY_SELECT') {
      if (activePlayers.length < 4) {
        return (
          `มีผู้เล่น ACTIVE ${activePlayers.length} คน ` +
          'ต้องมีอย่างน้อย 4 คนจึงจะ Random ได้'
        );
      }

      if (selectedManualPlayers.length < 1) {
        return 'กรุณาเลือกผู้เล่นอย่างน้อย 1 คนก่อนกด Random';
      }
    }

    if (mode === 'MANUAL_4') {
      if (manualPlayers.some(player => !player)) {
        return 'กรุณาเลือกผู้เล่นให้ครบ 4 คน';
      }

      const ids = manualPlayers.map(player => Number(player.user_id));
      if (new Set(ids).size !== 4) return 'พบผู้เล่นซ้ำ';

      const team1Ids = new Set(ids.slice(0, 2));
      const team2Ids = new Set(ids.slice(2, 4));
      const separatedPair = lockedPairs.find(pair => {
        const userA = Number(pair.user_a);
        const userB = Number(pair.user_b);
        const selectedA = team1Ids.has(userA) || team2Ids.has(userA);
        const selectedB = team1Ids.has(userB) || team2Ids.has(userB);
        if (!selectedA && !selectedB) return false;
        return !((team1Ids.has(userA) && team1Ids.has(userB)) || (team2Ids.has(userA) && team2Ids.has(userB)));
      });
      if (separatedPair) {
        const playerA = normalizedPlayers.find(player => Number(player.user_id) === Number(separatedPair.user_a));
        const playerB = normalizedPlayers.find(player => Number(player.user_id) === Number(separatedPair.user_b));
        return `ห้ามแยก ${separatedPair.pair_source === 'WAITING' ? 'Waiting Pair' : 'Locked Pair'} ${playerA?.name || separatedPair.user_a} + ${playerB?.name || separatedPair.user_b} กรุณาปลดล็อกก่อน`;
      }

      if (manualApprovalAlerts.length > 0 && !manualHistoryApproved) {
        return 'กรุณาติ๊กยืนยันประวัติคู่กันหรือตรงข้ามกันตั้งแต่ 3 รอบก่อนสร้าง Match';
      }
    }

    const nonActiveSelections = selectedNonActivePlayers();
    if (nonActiveSelections.length > 0 && !approvePlaying) {
      return 'กรุณาเปิด Approve สถานะอื่นก่อนเลือกผู้เล่นที่ไม่ใช่ ACTIVE';
    }

    return '';
  };

  const loadPreviewHistory = async () => {
    const response = await fetch(
      `${API_BASE}/api/game-match/preview-history/${Number(groupId)}`
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || 'โหลดประวัติ 2 Match ล่าสุดไม่สำเร็จ');
    }
    const history = payload.history || {};
    setPreviewHistoryByUserId(history);
    return history;
  };

  const hasEncounterWithinTwoMatches = (players, history) => {
    const ids = players.map(player => Number(player.user_id));
    return ids.some((userId, index) => {
      const recentMatches = history?.[userId]?.recentMatches || [];
      const encounteredIds = new Set(
        recentMatches.flatMap(match => match.encounteredUserIds || [])
          .map(Number)
      );
      return ids.slice(index + 1).some(otherId => {
        const left = players.find(player => Number(player.user_id) === userId);
        const right = players.find(player => Number(player.user_id) === otherId);
        return !isProtectedPair(left, right) && encounteredIds.has(otherId);
      });
    });
  };

  const getFourPlayerRepeatConflicts = players => {
    const conflicts = [];
    for (let leftIndex = 0; leftIndex < players.length - 1; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < players.length; rightIndex += 1) {
        const left = players[leftIndex];
        const right = players[rightIndex];
        if (isProtectedPair(left, right)) continue;
        const teammateCount = getRelationCount(pairMap, left, right);
        const opponentCount = getRelationCount(opponentMap, left, right);
        if (teammateCount + opponentCount < 2) continue;
        conflicts.push({
          left,
          right,
          teammateCount,
          opponentCount,
          totalCount: teammateCount + opponentCount
        });
      }
    }
    return conflicts;
  };

  const togglePreviewRank = rank => {
    setSelectedPreviewRanks(current => {
      if (current.includes(rank)) {
        setPreviewAlert(null);
        return current.filter(item => item !== rank);
      }
      if (current.length >= 4) {
        setError('เลือก Rank สำหรับ Preview ได้สูงสุด 4 Rank');
        setPreviewAlert({
          type: 'error',
          text: 'เลือก Rank ได้สูงสุด 4 Rank'
        });
        return current;
      }
      setError('');
      setPreviewAlert(null);
      return [...current, rank];
    });
  };

  const isPreviewRankAllowed = player =>
    selectedPreviewRanks.length === 0 ||
    selectedPreviewRanks.includes(
      normalizePlayerRank(player.level_rank, player.point)
    );

  const loadQueuePreviews = async () => {
    if (previewLoading) {
      setPreviewOpen(true);
      return;
    }

    // เปิด Modal ทุกครั้งที่กด เพื่อไม่ให้ดูเหมือนปุ่มไม่ทำงาน แม้ข้อมูล
    // allPlayers จาก parent กำลังตามหลัง socket อยู่หนึ่งจังหวะก็ตาม
    setPreviewOpen(true);

    const previewBaseReadyPlayers = normalizedPlayers.filter(player =>
      (playerScope === 'ALL'
        ? ['ACTIVE', 'PLAY'].includes(getStatus(player))
        : getStatus(player) === 'ACTIVE') &&
      !getQueueMatchId(player)
    );
    const rankFilteredPreviewPlayers = previewBaseReadyPlayers.filter(isPreviewRankAllowed);
    const previewReadyPlayers = rankFilteredPreviewPlayers.length >= 4
      ? rankFilteredPreviewPlayers
      : previewBaseReadyPlayers;
    try {
      setPreviewLoading(true);
      setPreviewManuallyEdited(false);
      setError('');
      setPreviewMatches([]);
      setPreviewAlert(null);
      setPreviewType('ACTIVE_RANK');
      // เปิด Modal ทันที เพื่อให้เห็นสถานะ Loading และไม่ดูเหมือนปุ่มไม่ทำงาน
      setPreviewOpen(true);
      previewRealtimeHandledRef.current = previewRealtimeVersion;
      const previewHistory = await loadPreviewHistory();

      // ขอหลาย seed แล้วตัด Match ซ้ำ เพื่อแสดงตัวเลือกได้สูงสุด 5 Match
      const requests = Array.from({ length: 20 }, (_, index) =>
        postMatchmakingJson('/api/game-match/random', {
          groupId: Number(groupId),
          mode: 'AI_RANDOM',
          priority: 'FIFO',
          playStyle: 'ANY',
          matchCount: 1,
          approvePlaying: playerScope === 'ALL',
          seed: `preview-${Date.now()}-${index}`,
          allowRangeOverride: false,
          allowRepeatPartnerOverride: false,
          allowRecentEncounterOverride: false
        }).catch(() => null)
      );
      const results = await Promise.all(requests);
      const uniqueMatches = [];
      const signatures = new Set();

      results.filter(Boolean).forEach(result => {
        const players = [...(result.team1 || []), ...(result.team2 || [])];
        if (players.length !== 4) return;
        if (players.some(player => playerScope === 'ALL'
          ? !['ACTIVE', 'PLAY'].includes(getStatus(player))
          : getStatus(player) !== 'ACTIVE'
        )) return;
        if (players.some(player => !isPreviewRankAllowed(player))) return;
        const requiresRecentEncounterApproval =
          hasEncounterWithinTwoMatches(players, previewHistory);
        const repeatLimitConflicts = getFourPlayerRepeatConflicts(players);

        const rankIndexes = players.map(player =>
          RANK_ORDER.indexOf(normalizePlayerRank(player.level_rank, player.point))
        );
        const rankSpread = Math.max(...rankIndexes) - Math.min(...rankIndexes);
        // Rank ใกล้กันหนึ่งระดับยังถือเป็นกลุ่มระดับเดียวกันสำหรับการจัดทีม
        if (selectedPreviewRanks.length === 0 && rankSpread > 1) return;

        const signature = players
          .map(player => Number(player.user_id))
          .sort((a, b) => a - b)
          .join('-');
        if (signatures.has(signature)) return;

        signatures.add(signature);
        const rankLabels = [...new Set(players.map(player =>
          normalizePlayerRank(player.level_rank, player.point)
        ))];
        uniqueMatches.push({
          ...result,
          previewRank: rankLabels.join(' / '),
          rankSpread,
          requiresRecentEncounterApproval:
            requiresRecentEncounterApproval || repeatLimitConflicts.length > 0,
          repeatLimitConflicts
        });
      });

      // Backend อาจคืนชุดเดิมทุก seed หรือไม่มีชุด Rank เดียวกันเป๊ะ
      // สร้างตัวเลือกสำรองจาก ACTIVE โดยเลือก Rank ใกล้ที่สุดและทีมคะแนนสูสี
      if (uniqueMatches.length < 5) {
        const pool = [...previewReadyPlayers]
          .sort((left, right) => {
            const rankDiff = RANK_ORDER.indexOf(
              normalizePlayerRank(left.level_rank, left.point)
            ) - RANK_ORDER.indexOf(
              normalizePlayerRank(right.level_rank, right.point)
            );
            return rankDiff || Number(left.point || 0) - Number(right.point || 0);
          })
          .slice(0, 24);
        const localCandidates = [];

        for (let a = 0; a < pool.length - 3; a += 1) {
          for (let b = a + 1; b < pool.length - 2; b += 1) {
            for (let c = b + 1; c < pool.length - 1; c += 1) {
              for (let d = c + 1; d < pool.length; d += 1) {
                const group = [pool[a], pool[b], pool[c], pool[d]];
                const requiresRecentEncounterApproval =
                  hasEncounterWithinTwoMatches(group, previewHistory);
                const repeatLimitConflicts = getFourPlayerRepeatConflicts(group);
                const selectedIds = new Set(
                  group.map(player => Number(player.user_id))
                );
                const respectsLockedSelection = lockedPairs.every(lock =>
                  selectedIds.has(Number(lock.user_a)) ===
                  selectedIds.has(Number(lock.user_b))
                );
                if (!respectsLockedSelection) continue;
                const rankIndexes = group.map(player => RANK_ORDER.indexOf(
                  normalizePlayerRank(player.level_rank, player.point)
                ));
                const rankSpread = Math.max(...rankIndexes) - Math.min(...rankIndexes);
                const layouts = [
                  [[group[0], group[1]], [group[2], group[3]]],
                  [[group[0], group[2]], [group[1], group[3]]],
                  [[group[0], group[3]], [group[1], group[2]]]
                ];
                const validLayouts = layouts.filter(([team1, team2]) => {
                  const team1Ids = new Set(team1.map(player => Number(player.user_id)));
                  const team2Ids = new Set(team2.map(player => Number(player.user_id)));
                  return lockedPairs.every(lock => {
                    const userA = Number(lock.user_a);
                    const userB = Number(lock.user_b);
                    if (!selectedIds.has(userA)) return true;
                    return (
                      (team1Ids.has(userA) && team1Ids.has(userB)) ||
                      (team2Ids.has(userA) && team2Ids.has(userB))
                    );
                  });
                });
                if (validLayouts.length === 0) continue;
                const scoredLayouts = validLayouts
                  .map(([team1, team2]) => ({
                    team1,
                    team2,
                    teamGap: Math.abs(
                      team1.reduce((sum, player) => sum + Number(player.point || 0), 0) -
                      team2.reduce((sum, player) => sum + Number(player.point || 0), 0)
                    )
                  }))
                  .sort((left, right) => left.teamGap - right.teamGap);
                const bestLayout = scoredLayouts[0];

                localCandidates.push({
                  ...bestLayout,
                  rankSpread,
                  previewRank: [...new Set(group.map(player =>
                    normalizePlayerRank(player.level_rank, player.point)
                  ))].join(' / '),
                  allowedLayouts: scoredLayouts,
                  hasLockedPair: lockedPairs.some(lock =>
                    selectedIds.has(Number(lock.user_a))
                  ),
                  analysis: { teamGap: bestLayout.teamGap },
                  requiresRecentEncounterApproval:
                    requiresRecentEncounterApproval || repeatLimitConflicts.length > 0,
                  repeatLimitConflicts,
                  reason: 'เลือกผู้เล่น ACTIVE Rank ใกล้กันที่สุดและจัดทีมตามคะแนน'
                });
              }
            }
          }
        }

        localCandidates
          .sort((left, right) =>
            getPreviewConditionPenalty(left) - getPreviewConditionPenalty(right) ||
            getPreviewWaitPriority(left).oldest - getPreviewWaitPriority(right).oldest ||
            getPreviewWaitPriority(left).total - getPreviewWaitPriority(right).total ||
            Number(right.hasLockedPair) - Number(left.hasLockedPair) ||
            left.teamGap - right.teamGap ||
            left.rankSpread - right.rankSpread
          )
          .forEach(candidate => {
            if (uniqueMatches.length >= 5) return;
            const signature = [...candidate.team1, ...candidate.team2]
              .map(player => Number(player.user_id))
              .sort((left, right) => left - right)
              .join('-');
            if (signatures.has(signature)) return;
            signatures.add(signature);
            uniqueMatches.push(candidate);
          });
      }

      uniqueMatches.sort((left, right) =>
        getPreviewConditionPenalty(left) - getPreviewConditionPenalty(right) ||
        getPreviewWaitPriority(left).oldest - getPreviewWaitPriority(right).oldest ||
        getPreviewWaitPriority(left).total - getPreviewWaitPriority(right).total ||
        Number(right.hasLockedPair) - Number(left.hasLockedPair) ||
        Number(left.analysis?.teamGap || left.teamGap || 0) -
          Number(right.analysis?.teamGap || right.teamGap || 0) ||
        Number(left.rankSpread || 0) - Number(right.rankSpread || 0)
      );
      setPreviewMatches(uniqueMatches.slice(0, 5));
      if (uniqueMatches.length === 0) {
        setError('ยังจัด Preview ไม่ได้ กรุณาตรวจสอบผู้เล่น ACTIVE');
      }
    } catch (previewError) {
      setError(previewError.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadFixedPlayerPreviews = async (playerOverride = null) => {
    // React ส่ง SyntheticEvent เข้ามาเมื่อผูก function กับ onClick โดยตรง
    // รับ override เฉพาะ object ผู้เล่นจริง เพื่อไม่ให้ DOM/Fiber หลุดเข้า JSON.stringify
    const safePlayerOverride = playerOverride && Number(playerOverride.user_id) > 0
      ? playerOverride
      : null;
    const fixedPickedPlayer = safePlayerOverride || pickedPlayer;
    const fixedLockedPartner = getLockedPartner(fixedPickedPlayer);
    if (previewLoading) {
      setPreviewOpen(true);
      return;
    }
    if (!fixedPickedPlayer) {
      setError('กรุณาเลือกผู้เล่นหลักในโหมด Pick One ก่อน');
      return;
    }

    const anchorPlayers = [fixedPickedPlayer, safePlayerOverride ? null : secondPickedPlayer].filter(Boolean);
    const queuedAnchor = anchorPlayers.find(player => getQueueMatchId(player));
    if (queuedAnchor) {
      setError(`${queuedAnchor.name} อยู่ใน Match Queue แล้ว กรุณาเลือกผู้เล่นหลักคนอื่น`);
      setPreviewAlert({
        type: 'error',
        text: `${queuedAnchor.name} อยู่ใน Match Queue แล้ว จึงไม่สามารถใช้สร้าง Preview ใหม่ได้`
      });
      return;
    }
    const anchorIds = new Set(anchorPlayers.map(player => Number(player.user_id)));

    // Locked Pair ของผู้เล่นหลักต้องเข้ามาใน Match และอยู่ทีมเดียวกันเสมอ
    for (const lock of lockedPairs) {
      const userA = Number(lock.user_a);
      const userB = Number(lock.user_b);
      const hasA = anchorIds.has(userA);
      const hasB = anchorIds.has(userB);
      if (hasA === hasB) continue;

      const partnerId = hasA ? userB : userA;
      const partner = normalizedPlayers.find(
        player => Number(player.user_id) === partnerId
      );
      if (!partner || getStatus(partner) === 'HOLD') {
        setError('Locked Pair ของผู้เล่นหลักไม่พร้อมลง Match');
        return;
      }
      anchorPlayers.push(partner);
      anchorIds.add(partnerId);
    }

    if (anchorPlayers.length > 2) {
      setError('ผู้เล่นหลัก 2 คนมี Locked Pair เพิ่ม ทำให้ทีมมีผู้เล่นเกิน 2 คน');
      return;
    }

    const sortFixedCandidates = players => [...players]
      .sort((left, right) => {
        const statusDiff =
          Number(getStatus(right) === 'ACTIVE') -
          Number(getStatus(left) === 'ACTIVE');
        const rankDiff = getRankDistance(fixedPickedPlayer, left) -
          getRankDistance(fixedPickedPlayer, right);
        const pointDiff = Math.abs(Number(fixedPickedPlayer.point) - Number(left.point)) -
          Math.abs(Number(fixedPickedPlayer.point) - Number(right.point));
        return statusDiff || rankDiff || pointDiff;
      })
      .slice(0, 24);
    const baseCandidates = normalizedPlayers
      .filter(player =>
        !anchorIds.has(Number(player.user_id)) &&
        (playerScope === 'ALL'
          ? !['HOLD', 'CHECKOUT'].includes(getStatus(player))
          : getStatus(player) === 'ACTIVE') &&
        !getQueueMatchId(player)
      );
    let candidates = sortFixedCandidates(
      baseCandidates.filter(isPreviewRankAllowed)
    );

    const requiredCandidateCount = 4 - anchorPlayers.length;
    // ถ้า Rank ที่เลือกแคบเกินไป ให้ขยายไป Rank ใกล้ที่สุดแทนการไม่แสดง Preview
    if (candidates.length < requiredCandidateCount) {
      candidates = sortFixedCandidates(baseCandidates);
    }
    if (candidates.length < requiredCandidateCount) {
      setPreviewOpen(true);
      setPreviewAlert({
        type: 'error',
        text: `มีผู้เล่นพร้อมเพียง ${candidates.length + anchorPlayers.length}/4 คน ต้องเพิ่มผู้เล่น ACTIVE ที่ไม่อยู่ Queue`
      });
      setError(`ต้องมีผู้เล่นพร้อมทั้งหมด 4 คน (ไม่รวม HOLD, CHECKOUT และ QUEUE)`);
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewManuallyEdited(false);
      setError('');
      setPreviewType('FIXED_PLAYER');
      setPreviewMatches([]);
      setPreviewAlert(null);
      setPreviewOpen(true);
      previewRealtimeHandledRef.current = previewRealtimeVersion;
      const previewHistory = await loadPreviewHistory();

      const choose = (items, count, start = 0, selected = [], result = []) => {
        if (selected.length === count) {
          result.push([...selected]);
          return result;
        }
        for (let index = start; index < items.length; index += 1) {
          selected.push(items[index]);
          choose(items, count, index + 1, selected, result);
          selected.pop();
        }
        return result;
      };
      const options = [];
      const groups = choose(candidates, requiredCandidateCount);

      groups.forEach(selectedCandidates => {
            const group = [...anchorPlayers, ...selectedCandidates];
            if (aiAutoEnabled && !isPickOneAutoIntensityCompatible(group)) return;
            const requiresRecentEncounterApproval =
              hasEncounterWithinTwoMatches(group, previewHistory);
            const repeatLimitConflicts = getFourPlayerRepeatConflicts(group);
            const selectedIds = new Set(group.map(player => Number(player.user_id)));
            const respectsLockedSelection = lockedPairs.every(lock => {
              const hasA = selectedIds.has(Number(lock.user_a));
              const hasB = selectedIds.has(Number(lock.user_b));
              return hasA === hasB;
            });
            if (!respectsLockedSelection) return;

            const layouts = [
              [[group[0], group[1]], [group[2], group[3]]],
              [[group[0], group[2]], [group[1], group[3]]],
              [[group[0], group[3]], [group[1], group[2]]]
            ];
            const validLayouts = layouts.filter(([team1, team2]) => {
              const team1Ids = new Set(team1.map(player => Number(player.user_id)));
              const team2Ids = new Set(team2.map(player => Number(player.user_id)));
              const anchorsTogether = anchorPlayers.every(player =>
                team1Ids.has(Number(player.user_id))
              ) || anchorPlayers.every(player =>
                team2Ids.has(Number(player.user_id))
              );
              const locksTogether = lockedPairs.every(lock => {
                const userA = Number(lock.user_a);
                const userB = Number(lock.user_b);
                if (!selectedIds.has(userA)) return true;
                return (
                  (team1Ids.has(userA) && team1Ids.has(userB)) ||
                  (team2Ids.has(userA) && team2Ids.has(userB))
                );
              });
              return anchorsTogether && locksTogether;
            });
            if (validLayouts.length === 0) return;

            const scoredLayouts = validLayouts
              .map(([team1, team2]) => ({
                team1,
                team2,
                teamGap: Math.abs(
                  team1.reduce((sum, player) => sum + Number(player.point || 0), 0) -
                  team2.reduce((sum, player) => sum + Number(player.point || 0), 0)
                )
              }))
              .sort((left, right) => left.teamGap - right.teamGap);
            const bestLayout = scoredLayouts[0];
            // Preview ต้องมีผลลัพธ์: ผลรวมเกิน 50 ยังแสดงได้ แต่ติดป้ายให้ Admin ตรวจสอบ
            const rankSpread = Math.max(...group.map(player =>
              RANK_ORDER.indexOf(normalizePlayerRank(player.level_rank, player.point))
            )) - Math.min(...group.map(player =>
              RANK_ORDER.indexOf(normalizePlayerRank(player.level_rank, player.point))
            ));
            const pointValues = group.map(player => Number(player.point || 0));
            const pointSpread = Math.max(...pointValues) - Math.min(...pointValues);
            const autoPreference = aiAutoEnabled
              ? getPickOneAutoPreference(group, fixedPickedPlayer)
              : { rankMismatch: 0, intensity: 0 };

            options.push({
              ...bestLayout,
              rankSpread,
              previewRank: [...new Set(group.map(player =>
                normalizePlayerRank(player.level_rank, player.point)
              ))].join(' / '),
              allowedLayouts: scoredLayouts,
              hasLockedPair: lockedPairs.some(lock =>
                selectedIds.has(Number(lock.user_a))
              ),
              analysis: { teamGap: bestLayout.teamGap },
              pointSpread,
              pickOneAutoRankMismatch: autoPreference.rankMismatch,
              pickOneAutoIntensityPreference: autoPreference.intensity,
              rangeExpandedForLockedPair: Boolean(
                fixedLockedPartner && (bestLayout.teamGap > 50 || pointSpread > 100)
              ),
              requiresRecentEncounterApproval:
                requiresRecentEncounterApproval || repeatLimitConflicts.length > 0,
              repeatLimitConflicts,
              reason: `ยึด ${anchorPlayers.map(player => player.name).join(' + ')} อยู่ทีมเดียวกัน`
            });
      });

      options.sort((left, right) =>
        getPreviewConditionPenalty(left) - getPreviewConditionPenalty(right) ||
        getPreviewWaitPriority(left).oldest - getPreviewWaitPriority(right).oldest ||
        getPreviewWaitPriority(left).total - getPreviewWaitPriority(right).total ||
        (aiAutoEnabled
          ? left.pickOneAutoRankMismatch - right.pickOneAutoRankMismatch ||
            left.pickOneAutoIntensityPreference - right.pickOneAutoIntensityPreference
          : 0) ||
        Number(left.requiresRecentEncounterApproval) -
          Number(right.requiresRecentEncounterApproval) ||
        Number(right.hasLockedPair) - Number(left.hasLockedPair) ||
        left.teamGap - right.teamGap || left.rankSpread - right.rankSpread
      );
      const recommended = options.slice(0, 2).map((match, index) => ({
        ...match,
        previewCategory: 'RECOMMENDED',
        previewLabel: `Match แนะนำ ${index + 1}`
      }));
      const recommendedKeys = new Set(recommended.map(getPreviewMatchKey));
      const pointRangeMatches = options
        .filter(match => match.pointSpread <= 100 && !recommendedKeys.has(getPreviewMatchKey(match)))
        .slice(0, 2)
        .map((match, index) => ({
          ...match,
          previewCategory: 'POINT_100',
          previewLabel: `Match คะแนนใกล้กันไม่เกิน 100 (${index + 1})`
        }));
      const selectedKeys = new Set(
        [...recommended, ...pointRangeMatches].map(getPreviewMatchKey)
      );
      const lockedPairFallback = fixedLockedPartner
        ? options
          .filter(match => !selectedKeys.has(getPreviewMatchKey(match)))
          .slice(0, Math.max(0, 4 - recommended.length - pointRangeMatches.length))
          .map((match, index) => ({
            ...match,
            previewCategory: 'LOCKED_EXPANDED',
            previewLabel: `Locked Pair • ขยายกติกา (${index + 1})`,
            rangeExpandedForLockedPair: true
          }))
        : [];
      const previewSignatures = new Set();
      const previewOptions = [...recommended, ...pointRangeMatches, ...lockedPairFallback].filter(match => {
        const signature = [...(match.team1 || []), ...(match.team2 || [])]
          .map(player => Number(player.user_id))
          .sort((left, right) => left - right)
          .join('-');
        if (!signature || previewSignatures.has(signature)) return false;
        previewSignatures.add(signature);
        return true;
      });
      setPreviewMatches(previewOptions);
      if (previewOptions.length < 4) {
        setPreviewAlert({
          type: 'error',
          text: `พบ Match ${previewOptions.length}/4 Match: ระบบเลือกคะแนนใกล้ที่สุดแล้ว${fixedLockedPartner ? ' และขยายกติกาเพื่อให้ Locked Pair ได้ลงด้วยกัน' : ''}`
        });
      } else {
        setPreviewAlert({
          type: 'success',
          text: 'จัด Match แนะนำ 2 Match และ Match คะแนนใกล้กันไม่เกิน 100 อีก 2 Match • ผลรวมทีมต่างกันไม่เกิน 50 คะแนน'
        });
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const refreshCurrentPreview = () => {
    setPreviewManuallyEdited(false);
    setPreviewAlert(null);
    if (previewType === 'FIXED_PLAYER') {
      loadFixedPlayerPreviews();
      return;
    }
    loadQueuePreviews();
  };

  const refreshCurrentPreviewRef = useRef(refreshCurrentPreview);
  refreshCurrentPreviewRef.current = refreshCurrentPreview;
  const loadFixedPlayerPreviewsRef = useRef(loadFixedPlayerPreviews);
  loadFixedPlayerPreviewsRef.current = loadFixedPlayerPreviews;

  useEffect(() => {
    if (!previewOpen) {
      previewRealtimeHandledRef.current = previewRealtimeVersion;
      return undefined;
    }
    if (previewRealtimeHandledRef.current === previewRealtimeVersion) {
      return undefined;
    }

    previewRealtimeHandledRef.current = previewRealtimeVersion;
    const timerId = window.setTimeout(() => {
      if (
        !previewManuallyEdited &&
        !previewLoading &&
        previewCreatingIndex === null
      ) {
        refreshCurrentPreviewRef.current();
        setPreviewAlert({
          type: 'info',
          text: 'อัปเดต Preview แบบ Realtime ตามสถานะล่าสุดแล้ว'
        });
      }
    }, 300);

    return () => window.clearTimeout(timerId);
  }, [
    previewRealtimeVersion,
    previewOpen,
    previewManuallyEdited,
    previewLoading,
    previewCreatingIndex
  ]);

  const swapPreviewPair = matchIndex => {
    setPreviewMatches(current => current.map((match, index) => {
      if (index !== matchIndex) return match;

      const [a, b] = match.team1;
      const [c, d] = match.team2;
      const swapStep = Number(match.swapStep || 0);
      const defaultLayouts = [
        [[a, b], [c, d]],
        [[a, c], [b, d]],
        [[a, d], [b, c]]
      ];
      const teamLayouts = Array.isArray(match.allowedLayouts) &&
        match.allowedLayouts.length > 0
        ? match.allowedLayouts.map(layout => [layout.team1, layout.team2])
        : defaultLayouts;
      const nextStep = (swapStep + 1) % teamLayouts.length;

      return {
        ...match,
        team1: teamLayouts[nextStep][0],
        team2: teamLayouts[nextStep][1],
        swapStep: nextStep,
        analysis: {
          ...match.analysis,
          teamGap: Math.abs(
            teamLayouts[nextStep][0].reduce(
              (sum, player) => sum + Number(player.point || 0), 0
            ) - teamLayouts[nextStep][1].reduce(
              (sum, player) => sum + Number(player.point || 0), 0
            )
          )
        }
      };
    }));
  };

  const editPreviewPlayer = (matchIndex, teamKey, playerIndex, userId) => {
    const replacement = normalizedPlayers.find(
      player => Number(player.user_id) === Number(userId)
    );
    if (!replacement) return;
    setPreviewManuallyEdited(true);

    setPreviewMatches(current => current.map((match, index) => {
      if (index !== matchIndex) return match;

      const existingIds = [...(match.team1 || []), ...(match.team2 || [])]
        .map(player => Number(player.user_id));
      const replacedPlayer = match?.[teamKey]?.[playerIndex];
      if (
        existingIds.includes(Number(replacement.user_id)) &&
        Number(replacedPlayer?.user_id) !== Number(replacement.user_id)
      ) {
        setPreviewAlert({ type: 'error', text: 'ผู้เล่นคนนี้อยู่ใน Match แล้ว' });
        return match;
      }

      const nextTeam = [...(match[teamKey] || [])];
      nextTeam[playerIndex] = replacement;
      const nextTeam1 = teamKey === 'team1' ? nextTeam : match.team1;
      const nextTeam2 = teamKey === 'team2' ? nextTeam : match.team2;
      const teamGap = Math.abs(
        nextTeam1.reduce((sum, player) => sum + Number(player.point || 0), 0) -
        nextTeam2.reduce((sum, player) => sum + Number(player.point || 0), 0)
      );

      setPreviewAlert({
        type: 'info',
        text: `เปลี่ยนเฉพาะผู้เล่นเป็น ${replacement.name} แล้ว • คงคู่และ Match เดิม • Team gap ${teamGap}`
      });
      setEditingPreviewSlot(null);
      return {
        ...match,
        [teamKey]: nextTeam,
        allowedLayouts: null,
        previewRank: [...new Set([...nextTeam1, ...nextTeam2].map(player =>
          normalizePlayerRank(player.level_rank, player.point)
        ))].join(' / '),
        analysis: { ...match.analysis, teamGap }
      };
    }));
  };

  const getPreviewMatchKey = match => [
    ...(match?.team1 || []),
    ...(match?.team2 || [])
  ].map(player => Number(player.user_id)).sort((a, b) => a - b).join('-');

  const getPreviewRuleViolations = match => {
    const players = [...(match?.team1 || []), ...(match?.team2 || [])];
    const points = players.map(player => Number(player.point || 0));
    const pointSpread = points.length > 0 ? Math.max(...points) - Math.min(...points) : 0;
    const teamGap = Number(match?.analysis?.teamGap ?? match?.teamGap ?? 0);
    const violations = [];
    const allPlayerPairs = [];
    for (let leftIndex = 0; leftIndex < players.length - 1; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < players.length; rightIndex += 1) {
        allPlayerPairs.push({ left: players[leftIndex], right: players[rightIndex] });
      }
    }
    allPlayerPairs.forEach(item => {
      if (isProtectedPair(item.left, item.right)) return;
      const teammateCount = getRelationCount(pairMap, item.left, item.right);
      const opponentCount = getRelationCount(opponentMap, item.left, item.right);
      const count = teammateCount + opponentCount;
      if (count < 2) return;
      violations.push(
        `🔁 ผู้เล่นซ้ำ: ${item.left.name} ↔ ${item.right.name} เคยพบรวม ${count} รอบ ` +
        `(คู่ทีม ${teammateCount} / ตรงข้าม ${opponentCount}) · Match นี้จะเป็นรอบที่ ${count + 1} (ต้อง Approve)`
      );
    });
    if (match?.requiresRecentEncounterApproval) {
      violations.push('มีผู้เล่นที่เพิ่งพบกันภายใน 2 Match ล่าสุด');
    }
    if (teamGap > 50) violations.push(`คะแนนรวมสองทีมต่างกัน ${teamGap} คะแนน (กำหนดไม่เกิน 50)`);
    if (match?.previewCategory === 'POINT_100' && pointSpread > 100) {
      violations.push(`คะแนนผู้เล่นสูงสุด–ต่ำสุดต่างกัน ${pointSpread} คะแนน (กำหนดไม่เกิน 100)`);
    }
    return violations;
  };

  const PERFECT_MATCH_STATUS = new Set(['ACTIVE', 'PLAY', 'QUEUE']);
  const PERFECT_INTENSITY_RULES = {
    HEAVY: { label: '🔴 หนัก เจอ หนัก', values: ['HEAVY'], relaxed: false },
    MEDIUM: { label: '🟡 กลาง เจอ กลาง', values: ['MEDIUM'], relaxed: false },
    LIGHT: { label: '🟢 เบา เจอ เบา', values: ['LIGHT'], relaxed: false },
    HEAVY_MEDIUM: { label: '🔴🟡 หนัก–กลาง (ผ่อนกฎ)', values: ['HEAVY', 'MEDIUM'], relaxed: true },
    MEDIUM_LIGHT: { label: '🟡🟢 กลาง–เบา (ผ่อนกฎ)', values: ['MEDIUM', 'LIGHT'], relaxed: true }
  };
  const PERFECT_GENDER_RULES = {
    ANY: { label: 'ทุกเพศ', values: [] },
    FEMALE: { label: '♀ หญิง–หญิง', values: ['FEMALE'] },
    MALE: { label: '♂ ชาย–ชาย', values: ['MALE'] },
    BEAUTIFUL: { label: '🌈 สวยที่สุด–สวยที่สุด', values: ['BEAUTIFUL'] }
  };

  const loadPerfectMatches = async () => {
    if (perfectMatchLoading) return;

    const intensityRule = PERFECT_INTENSITY_RULES[perfectMatchIntensity] || PERFECT_INTENSITY_RULES.MEDIUM;
    const allowedIntensitySet = new Set(intensityRule.values);
    const genderRule = PERFECT_GENDER_RULES[perfectMatchGender] || PERFECT_GENDER_RULES.ANY;
    const allowedGenderSet = new Set(genderRule.values);

    const eligiblePlayers = normalizedPlayers.filter(player =>
      PERFECT_MATCH_STATUS.has(getStatus(player)) &&
      allowedIntensitySet.has(String(player.play_intensity || '').trim().toUpperCase()) &&
      (allowedGenderSet.size === 0 || allowedGenderSet.has(String(player.gender || '').trim().toUpperCase()))
    );

    setPerfectMatchOpen(true);
    setPerfectMatchResults([]);
    setPerfectMatchMessage(null);

    if (eligiblePlayers.length < 4) {
      setPerfectMatchMessage({
        type: 'error',
        text: `มีผู้เล่นตามกฎ ${intensityRule.label} • ${genderRule.label} ที่สถานะ ACTIVE / PLAY / QUEUE เพียง ${eligiblePlayers.length} คน ต้องมีอย่างน้อย 4 คน`
      });
      return;
    }

    try {
      setPerfectMatchLoading(true);
      const selectedPlayerIds = eligiblePlayers.map(player => Number(player.user_id));
      const requestCandidates = async allowRepeatRules => {
        try {
          const response = await postMatchmakingJson('/api/game-match/random-multiple', {
          groupId: Number(groupId),
          mode: 'AI_RANDOM',
          priority: 'FIFO',
          playStyle: 'ANY',
          matchCount: Math.min(15, Math.floor(eligiblePlayers.length / 4)),
          approvePlaying: true,
          selectedPlayerIds,
          allowedIntensities: intensityRule.values,
          allowedGenders: genderRule.values,
          perfectMatchOnly: true,
          seed: `perfect-${perfectMatchIntensity}-${allowRepeatRules ? 'repeat-last' : 'strict'}-${Date.now()}`,
          allowRangeOverride: false,
          allowRepeatPartnerOverride: allowRepeatRules,
          allowRecentEncounterOverride: allowRepeatRules
          });
          return Array.isArray(response?.matches) ? response.matches : [];
        } catch (requestError) {
          if (!allowRepeatRules && (requestError.data?.requiresRecentEncounterApproval || requestError.data?.requiresRepeatPartnerApproval)) return [];
          throw requestError;
        }
      };

      const filterCandidates = (responses, allowRepeatRules) => {
        const signatures = new Set();
        return responses.filter(Boolean).filter(match => {
        const players = [...(match.team1 || []), ...(match.team2 || [])];
        if (players.length !== 4) return false;
        if (players.some(player =>
          !PERFECT_MATCH_STATUS.has(getStatus(player)) ||
          !allowedIntensitySet.has(String(player.play_intensity || '').trim().toUpperCase()) ||
          (allowedGenderSet.size > 0 && !allowedGenderSet.has(String(player.gender || '').trim().toUpperCase()))
        )) return false;
        const violations = getPreviewRuleViolations(match);
        if (!allowRepeatRules && violations.length > 0) return false;
        if (allowRepeatRules && violations.some(violation =>
          !violation.includes('คู่ทีมเดิม') &&
          !violation.includes('คู่ตรงข้ามเดิม') &&
          !violation.includes('ผู้เล่นซ้ำ') &&
          !violation.includes('เพิ่งพบกัน') &&
          !violation.includes('เคยคู่กัน') &&
          !violation.includes('เคยเป็นคู่แข่ง')
        )) return false;
        const signature = getPreviewMatchKey(match);
        if (!signature || signatures.has(signature)) return false;
        signatures.add(signature);
        return true;
        }).slice(0, 15).map(match => ({
          ...match,
          repeatRuleRelaxed: allowRepeatRules,
          repeatRuleDetails: allowRepeatRules ? getPreviewRuleViolations(match) : [],
          perfectAllowedGenders: genderRule.values
        }));
      };

      const strictResponses = await requestCandidates(false);
      let matches = filterCandidates(strictResponses, false);

      // ผ่อนกฎผู้เล่นซ้ำเป็นเงื่อนไขสุดท้ายเท่านั้น
      if (matches.length === 0) {
        const relaxedResponses = await requestCandidates(true);
        matches = filterCandidates(relaxedResponses, true);
      }

      setPerfectMatchResults(matches);
      setPerfectMatchMessage(matches.length > 0
        ? matches[0]?.repeatRuleRelaxed
          ? { type: 'warning', text: `ไม่พบ Match ที่ไม่ซ้ำ จึงแสดง ${matches.length} ตัวเลือกแบบผ่อนกฎผู้เล่นซ้ำเป็นลำดับสุดท้าย` }
          : { type: 'success', text: `พบ ${matches.length} ตัวเลือกตามกฎ ${intensityRule.label} • ${genderRule.label} โดยไม่ผ่อนกฎผู้เล่นซ้ำ` }
        : { type: 'error', text: `ไม่พบ Match ตามกฎ ${intensityRule.label} • ${genderRule.label} แม้ลองผ่อนกฎผู้เล่นซ้ำเป็นเงื่อนไขสุดท้ายแล้ว` }
      );
    } catch (perfectError) {
      setPerfectMatchMessage({ type: 'error', text: perfectError.message });
    } finally {
      setPerfectMatchLoading(false);
    }
  };

  const createPerfectMatchQueue = async (match, index) => {
    if (!match || perfectMatchCreating !== null) return;
    if (match.repeatRuleRelaxed) {
      const details = match.repeatRuleDetails?.length
        ? match.repeatRuleDetails.join('\n')
        : 'พบผู้เล่นที่เคยเป็นคู่หรือคู่แข่งกันมาก่อน';
      const approved = window.confirm(
        `ตัวเลือกนี้เป็นเงื่อนไขสุดท้ายและต้องผ่อนกฎผู้เล่นซ้ำ:\n${details}\n\nต้องการ Approve และสร้าง Queue หรือไม่?`
      );
      if (!approved) return;
    }
    try {
      setPerfectMatchCreating(index);
      setPerfectMatchMessage(null);
      const response = await postMatchmakingJson('/api/game-match/queue', {
        groupId: Number(groupId),
        mode: 'PICK_ONE',
        team1: match.team1,
        team2: match.team2,
        approveAllStatuses: true,
        perfectMatchOnly: true,
        allowedGenders: match.perfectAllowedGenders || [],
        approveRecentEncounter: Boolean(match.repeatRuleRelaxed)
      });
      setLastResult({ ...response, matches: [{ ...match, ...response }] });
      setPerfectMatchResults(current => current.filter((_, matchIndex) => matchIndex !== index));
      setPerfectMatchMessage({ type: 'success', text: 'สร้าง Match Queue สำเร็จ' });
      if (onCreatedRef.current) await onCreatedRef.current(response);
      advancePickOneAfterQueue([...(match.team1 || []), ...(match.team2 || [])]);
    } catch (perfectError) {
      const details = perfectError.data?.requiresRecentEncounterApproval
        ? buildApproveHistoryText(perfectError.data)
        : perfectError.message;
      setPerfectMatchMessage({
        type: 'error',
        text: `สร้างไม่ได้เพราะข้อมูลผู้เล่นเปลี่ยนหรือผิดกฎผู้เล่นซ้ำ: ${details}`
      });
    } finally {
      setPerfectMatchCreating(null);
    }
  };

  const editPerfectMatchPlayer = (matchIndex, teamKey, playerIndex, candidate) => {
    setPerfectMatchResults(current => current.map((match, index) => {
      if (index !== matchIndex) return match;
      const usedIds = new Set([...match.team1, ...match.team2].map(player => Number(player.user_id)));
      usedIds.delete(Number(match[teamKey][playerIndex].user_id));
      if (usedIds.has(Number(candidate.user_id))) return match;
      const nextTeam = match[teamKey].map((player, slotIndex) => slotIndex === playerIndex ? candidate : player);
      const nextMatch = { ...match, [teamKey]: nextTeam };
      const team1Total = nextMatch.team1.reduce((sum, player) => sum + Number(player.point || 0), 0);
      const team2Total = nextMatch.team2.reduce((sum, player) => sum + Number(player.point || 0), 0);
      const repeatRuleDetails = getPreviewRuleViolations(nextMatch);
      return {
        ...nextMatch,
        analysis: { ...(match.analysis || {}), team1Total, team2Total, teamGap: Math.abs(team1Total - team2Total) },
        repeatRuleRelaxed: repeatRuleDetails.length > 0,
        repeatRuleDetails,
        manuallyEdited: true
      };
    }));
    setPerfectEditingSlot(null);
    setPerfectEditSearch('');
    setPerfectMatchMessage({ type: 'info', text: 'แก้ไขผู้เล่นแล้ว • ระบบจะตรวจทุกกฎอีกครั้งก่อนสร้าง Queue' });
  };

  const getPerfectPlayerHistory = player => {
    const rows = normalizedPlayers
      .filter(other => Number(other.user_id) !== Number(player.user_id))
      .map(other => ({
        name: other.name,
        teammateCount: getRelationCount(pairMap, player, other),
        opponentCount: getRelationCount(opponentMap, player, other)
      }));
    return {
      teammates: rows.filter(row => row.teammateCount > 0).sort((a, b) => b.teammateCount - a.teammateCount),
      opponents: rows.filter(row => row.opponentCount > 0).sort((a, b) => b.opponentCount - a.opponentCount)
    };
  };

  const togglePreviewMatch = match => {
    const key = getPreviewMatchKey(match);
    setSelectedPreviewKeys(current => {
      if (current.includes(key)) return current.filter(item => item !== key);

      const selectedPlayerIds = new Set(
        previewMatches
          .filter(candidate => current.includes(getPreviewMatchKey(candidate)))
          .flatMap(candidate => [...(candidate.team1 || []), ...(candidate.team2 || [])])
          .map(player => Number(player.user_id))
      );
      const duplicatedPlayer = [...(match.team1 || []), ...(match.team2 || [])]
        .find(player => selectedPlayerIds.has(Number(player.user_id)));
      if (duplicatedPlayer) {
        setPreviewAlert({
          type: 'error',
          text: `${duplicatedPlayer.name} ถูกเลือกอยู่ใน Match อื่นแล้ว ไม่สามารถสร้างพร้อมกันได้`
        });
        return current;
      }
      setPreviewAlert(null);
      return [...current, key];
    });
  };

  const selectAllCompatiblePreviewMatches = () => {
    const usedPlayerIds = new Set();
    const compatibleKeys = [];
    previewMatches.forEach(match => {
      const playerIds = [...(match.team1 || []), ...(match.team2 || [])]
        .map(player => Number(player.user_id));
      if (playerIds.some(id => usedPlayerIds.has(id))) return;
      playerIds.forEach(id => usedPlayerIds.add(id));
      compatibleKeys.push(getPreviewMatchKey(match));
    });
    setSelectedPreviewKeys(compatibleKeys);
    setPreviewAlert({
      type: 'info',
      text: `เลือก Match ที่ผู้เล่นไม่ซ้ำกันได้ ${compatibleKeys.length} Match`
    });
  };

  const createSelectedPreviewQueues = async () => {
    if (previewCreatingIndex !== null || selectedPreviewKeys.length === 0) return;
    const selectedMatches = previewMatches.filter(match =>
      selectedPreviewKeys.includes(getPreviewMatchKey(match))
    );
    const created = [];
    const queuedPlayerIds = new Set();

    try {
      setPreviewCreatingIndex(-1);
      setError('');
      for (const match of selectedMatches) {
        const payload = {
          groupId: Number(groupId),
          mode: 'PICK_ONE',
          team1: match.team1,
          team2: match.team2,
          approveAllStatuses: true,
          approveRecentEncounter: false
        };
        let response;
        try {
          response = await postMatchmakingJson('/api/game-match/queue', payload);
        } catch (queueError) {
          if (!queueError.data?.requiresRecentEncounterApproval) throw queueError;
          const approved = window.confirm(
            `Match ${created.length + 1} ต้องตรวจสอบประวัติ:\n${buildApproveHistoryText(queueError.data, getPreviewRuleViolations(match).join('\n') || 'มีผู้เล่นพบกันใน Match ล่าสุด')}\n\nต้องการ Approve และสร้าง Queue หรือไม่?`
          );
          if (!approved) throw new Error('ยกเลิกการสร้าง Match ที่ต้อง Approve');
          response = await postMatchmakingJson('/api/game-match/queue', {
            ...payload,
            approveRecentEncounter: true
          });
        }
        created.push({ ...match, ...response });
        [...match.team1, ...match.team2].forEach(player =>
          queuedPlayerIds.add(Number(player.user_id))
        );
      }

      setLastResult({ matches: created, matchCount: created.length });
      setPreviewMatches(current => current.filter(candidate =>
        [...candidate.team1, ...candidate.team2].every(
          player => !queuedPlayerIds.has(Number(player.user_id))
        )
      ));
      setSelectedPreviewKeys([]);
      setPreviewAlert({
        type: 'success',
        text: `สร้าง Queue พร้อมกันสำเร็จ ${created.length} Match`
      });
      if (onCreatedRef.current) await onCreatedRef.current({ matches: created });
      advancePickOneAfterQueue([...queuedPlayerIds]);
    } catch (queueError) {
      setError(queueError.message);
      setPreviewAlert({ type: 'error', text: queueError.message });
    } finally {
      setPreviewCreatingIndex(null);
    }
  };

  const createQueueFromPreview = async matchIndex => {
    if (previewCreatingIndex !== null) return;
    const match = previewMatches[matchIndex];
    if (!match) return;

    const payload = {
      groupId: Number(groupId),
      mode: 'PICK_ONE',
      team1: match.team1,
      team2: match.team2,
      approveAllStatuses: true,
      approveRecentEncounter: false
    };

    try {
      setPreviewCreatingIndex(matchIndex);
      setError('');
      let response;

      try {
        response = await postMatchmakingJson('/api/game-match/queue', payload);
      } catch (queueError) {
        if (!queueError.data?.requiresRecentEncounterApproval) throw queueError;

        const approved = window.confirm(
          `Match นี้ต้องตรวจสอบประวัติ:\n${buildApproveHistoryText(queueError.data, getPreviewRuleViolations(match).join('\n') || 'มีผู้เล่นพบกันใน Match ล่าสุด')}\n\nต้องการ Approve และสร้าง Queue หรือไม่?`
        );
        if (!approved) return;

        response = await postMatchmakingJson('/api/game-match/queue', {
          ...payload,
          approveRecentEncounter: true
        });
      }

      setLastResult({
        ...response,
        matches: [{ ...match, ...response }]
      });
      const queuedIds = new Set(
        [...match.team1, ...match.team2].map(player => Number(player.user_id))
      );
      setPreviewMatches(current => current.filter(candidate =>
        [...candidate.team1, ...candidate.team2].every(
          player => !queuedIds.has(Number(player.user_id))
        )
      ));
      setSelectedPreviewKeys([]);
      setPreviewAlert({
        type: 'success',
        text:
          `สร้าง Queue สำเร็จ และอัปเดตตัวเลือกอื่นที่มี ` +
          `${[...match.team1, ...match.team2].map(player => player.name).join(', ')} แล้ว`
      });

      if (onCreatedRef.current) await onCreatedRef.current(response);
      advancePickOneAfterQueue([...match.team1, ...match.team2]);
    } catch (queueError) {
      setError(queueError.message);
      setPreviewAlert({ type: 'error', text: queueError.message });
    } finally {
      setPreviewCreatingIndex(null);
    }
  };

  const createQueueFromSelectedPlayers = async () => {
    if (loading || pickOneOrderedPlayers.length !== 4) return;

    const team1 = pickOneOrderedPlayers.slice(0, 2);
    const team2 = pickOneOrderedPlayers.slice(2, 4);
    const payload = {
      groupId: Number(groupId),
      mode: 'PICK_ONE',
      team1,
      team2,
      approveAllStatuses: true,
      approveRecentEncounter: false
    };

    try {
      setLoading(true);
      setError('');
      let response;

      try {
        response = await postMatchmakingJson('/api/game-match/queue', payload);
      } catch (queueError) {
        if (!queueError.data?.requiresRecentEncounterApproval) throw queueError;
        const approved = window.confirm(
          `ผู้เล่นที่เลือกมีประวัติซ้ำ\n\n${buildApproveHistoryText(queueError.data)}\n\nต้องการ Approve และสร้าง Queue หรือไม่?`
        );
        if (!approved) return;
        response = await postMatchmakingJson('/api/game-match/queue', {
          ...payload,
          approveRecentEncounter: true
        });
      }

      setLastResult({
        ...response,
        matches: [{ ...response, team1, team2, mode: 'PICK_ONE' }]
      });
      setPickedPlayer(null);
      setSecondPickedPlayer(null);
      setPickOnePlayers([]);
      setNearbySearchKeyword('');
      if (onCreatedRef.current) await onCreatedRef.current(response);
    } catch (queueError) {
      setError(queueError.message);
    } finally {
      setLoading(false);
    }
  };

  const generateQueue = async () => {
    if (loading || aiAutoLoading) return;

    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const body = {
        groupId: Number(groupId),
        mode,
        priority: mode === 'AI_RANDOM' ? 'FIFO' : priority,
        pointRange,
        playStyle: mode === 'AI_RANDOM' ? 'ANY' : playStyle,
        matchCount: supportsMultipleMatches ? matchCount : 1,
        approvePlaying: canApprovePlaying && approvePlaying,
        fixedUserId:
          mode === 'PICK_ONE' ? Number(pickedPlayer.user_id) : null,
        selectedPlayerIds:
          mode === 'PICK_ONE'
            ? pickOnePlayers.map(player => Number(player.user_id))
            : usesManualSelection
              ? (
                mode === 'PRIORITY_SELECT'
                  ? orderedPrioritySelections
                  : selectedManualPlayers
              ).map(
                player => Number(player.user_id)
              )
              : []
      };

      const requestQueue = async payload => {
        const response = await fetch(
          `${API_BASE}/api/game-match/generate-queue`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );
        const responseData = await response.json().catch(() => ({}));

        return { response, responseData };
      };

      let requestBody = {
        ...body,
        allowRangeOverride: mode === 'PICK_ONE' && Boolean(pickedLockedPartner),
        allowRepeatPartnerOverride:
          mode === 'MANUAL_4' && manualApprovalAlerts.length > 0 && manualHistoryApproved,
        allowRecentEncounterOverride:
          mode === 'MANUAL_4' && manualApprovalAlerts.length > 0 && manualHistoryApproved
      };
      let res;
      let data;

      // อาจต้องยืนยันมากกว่า 1 ชั้น เช่น คะแนนเกินช่วง แล้วจึงพบคู่ทีมซ้ำ
      for (let confirmationRound = 0; confirmationRound < 3; confirmationRound += 1) {
        ({ response: res, responseData: data } = await requestQueue(
          requestBody
        ));

        if (res.ok) break;

        if (
          data.confirmationType === 'RANGE' &&
          usesIndividualPointRange &&
          !requestBody.allowRangeOverride
        ) {
          const gaps = Array.isArray(data.individualGaps)
            ? data.individualGaps.join(' / ')
            : '-';
          const teammateGaps = Array.isArray(data.teammateGaps)
            ? data.teammateGaps.join(' / ')
            : '-';
          const requiredRange = data.requiredRange ?? gaps;
          const confirmed = window.confirm(
            `⚠️ ไม่พบทีมที่ผ่านช่วง ${pointRange} คะแนน\n` +
            `คะแนนห่างข้ามทีม: ${gaps}\n` +
            `คะแนนห่างภายในทีม T1 / T2: ${teammateGaps}\n` +
            `ต้องใช้ช่วงสูงสุด ${requiredRange} คะแนน\n` +
            'ระบบยังคงรักษา Locked Pair ให้อยู่ทีมเดียวกัน\n\n' +
            'ต้องการ Approve คะแนนห่างและสร้าง Match ด้วยคู่นี้หรือไม่?'
          );

          if (!confirmed) {
            setError('ยกเลิกการสร้าง Match เพราะคะแนนเกินช่วงที่กำหนด');
            return;
          }

          requestBody = {
            ...requestBody,
            allowRangeOverride: true
          };
          continue;
        }

        if (
          (
            data.confirmationType === 'RECENT_ENCOUNTER' ||
            data.confirmationType === 'REPEAT_PARTNER'
          ) &&
          !requestBody.allowRecentEncounterOverride
        ) {
          const pairNames = buildApproveHistoryText(data, 'ผู้เล่นที่เพิ่งพบกัน แต่ระบบไม่พบรายละเอียดรายคู่');
          const expansionNote =
            mode === 'PICK_ONE' || mode === 'AI_RANDOM'
              ? 'ระบบลองขยับช่วงคะแนนและ Rank เพื่อหาชุดที่ใกล้ที่สุดแล้ว แต่'
              : '';
          const confirmed = window.confirm(
            `⚠️ ไม่มีชุดผู้เล่นที่ผ่านกติกา 2 เกมล่าสุด\n${pairNames}\n\n` +
            `${expansionNote}รายชื่อข้างต้นยังเคยอยู่ใน Match เดียวกันภายใน 2 เกมล่าสุด ` +
            'ไม่ว่าจะเป็นทีมเดียวกันหรือคนละฝั่ง\n\n' +
            'ต้องการ Approve และสร้าง Match นี้หรือไม่?'
          );

          if (!confirmed) {
            setError('ยังไม่ได้สร้าง Match เพราะยังไม่ได้ Approve ผู้เล่นซ้ำใน 2 เกมล่าสุด');
            return;
          }

          requestBody = {
            ...requestBody,
            allowRepeatPartnerOverride: true,
            allowRecentEncounterOverride: true
          };
          continue;
        }

        break;
      }

      if (!res.ok) {
        throw new Error(
          data.message ||
          data.error ||
          `สร้าง Queue ไม่สำเร็จ (HTTP ${res.status})`
        );
      }

      setLastResult(data);


      /* =================================================
         โหลด Queue + Players ใหม่ก่อน
      ================================================= */

      if (onCreatedRef.current) {
        await onCreatedRef.current(data);
      }


      /* =================================================
         ค่อยเคลียร์ selection หลังข้อมูลใหม่มาแล้ว
      ================================================= */

      if (mode === 'PICK_ONE') {

        setPickedPlayer(null);

        setPickOnePlayers([]);

        setNearbySearchKeyword('');

        setAiRecommendation(null);

        aiProcessedSignatureRef.current = '';

        pickOneAttemptedIdsRef.current.clear();

      } else {

        resetInputs();

      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const supportsAiAuto =
      mode === 'AI_RANDOM' || mode === 'PICK_ONE';

    if (!supportsAiAuto) {
      setAiRecommendation(null);
      setAiAutomationStatus('');
      setAiAutoLoading(false);
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    // AI ต้องไม่วิเคราะห์หรือยิง request เองจนกว่า Admin จะสั่งเปิด
    if (!aiAutoEnabled) {
      setAiRecommendation(null);
      setAiAutomationStatus('AI ปิดอยู่ • กดเปิดเมื่อต้องการให้เริ่มทำงาน');
      setAiAutoLoading(false);
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    if (mode === 'PICK_ONE' && !pickedPlayer) {
      setAiRecommendation(null);
      setAiAutomationStatus('เลือกผู้เล่นหลักก่อน เพื่อให้ AI ค้นหาคะแนนใกล้เคียง');
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    const approvedAdvancePlayerCount =
      mode === 'PICK_ONE' && approvePlaying
        ? pickOneOrderedPlayers.filter(
          player => getStatus(player) !== 'ACTIVE'
        ).length
        : 0;
    const approvedAiPlayingCount =
      mode === 'AI_RANDOM' && approvePlaying
        ? normalizedPlayers.filter(
          player => getStatus(player) === 'PLAY' && !getQueueMatchId(player)
        ).length
        : aiEligiblePlayingPlayers.length;
    const availablePlayerCount =
      aiOrderedActivePlayers.length +
      (mode === 'AI_RANDOM' ? approvedAiPlayingCount : 0) +
      approvedAdvancePlayerCount;
    const missingPlayerCount = Math.max(
      0,
      requiredPlayerCount - availablePlayerCount
    );

    if (!groupId || missingPlayerCount > 0) {
      setAiAutomationStatus(
        `กำลังรอผู้เล่น ACTIVE อีก ${missingPlayerCount} คน`
      );
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    if (
      aiRequestInFlightRef.current ||
      aiProcessedSignatureRef.current === aiMatchmakingSignature
    ) {
      return undefined;
    }

    let cancelled = false;
    let requestStarted = false;
    const timerId = window.setTimeout(async () => {
      requestStarted = true;
      aiRequestInFlightRef.current = true;
      setAiAutoLoading(true);
      setError('');
      setAiAutomationStatus(
        mode === 'PICK_ONE'
          ? `AI กำลังค้นหาผู้เล่นคะแนนใกล้เคียง ${pickedPlayer.name}...`
          : 'AI กำลังวิเคราะห์ผู้เล่น ACTIVE ทั้งก๊วน...'
      );
      appendAiActivity(
        mode === 'PICK_ONE'
          ? `กำลังวิเคราะห์โดยยึด ${pickedPlayer.name} เป็นผู้เล่นหลัก`
          : `กำลังวิเคราะห์ผู้เล่นพร้อมลง ${availablePlayerCount} คน`
      );
      if (aiAutoEnabled) {
        appendAiActivity(
          'AI Auto เปิดอยู่ • อนุญาตผ่อนกฎประวัติซ้ำเมื่อจำเป็น'
        );
      }

      const body = {
        groupId: Number(groupId),
        mode,
        priority: mode === 'AI_RANDOM' ? 'FIFO' : priority,
        pointRange,
        playStyle: mode === 'AI_RANDOM' ? 'ANY' : playStyle,
        matchCount: mode === 'AI_RANDOM' ? matchCount : 1,
        approvePlaying:
          (mode === 'PICK_ONE' || mode === 'AI_RANDOM') && approvePlaying,
        fixedUserId:
          mode === 'PICK_ONE' ? Number(pickedPlayer.user_id) : null,
        selectedPlayerIds:
          mode === 'PICK_ONE'
            ? pickOnePlayers.map(player => Number(player.user_id))
            : [],
        pickOneAuto: mode === 'PICK_ONE' && aiAutoEnabled,
        // Pick One ห้ามขยายคะแนน/ข้ามโซนรุนแรงอัตโนมัติ
        // หากจำเป็น Backend จะขอให้ผู้ใช้กด Approve ก่อน
        allowRangeOverride: mode === 'PICK_ONE' && Boolean(pickedLockedPartner),
        // การพบกันตั้งแต่รอบที่ 3 ต้องให้ Admin เห็นรายละเอียดและ Approve
        // แม้เปิด AI Auto ระบบจะไม่อนุมัติประวัติซ้ำเอง
        allowRepeatPartnerOverride: false,
        allowRecentEncounterOverride: false
      };

      try {
        let repeatApproved = false;
        let repeatApprovalData = null;
        let preview;
        try {
          preview = await postMatchmakingJson('/api/game-match/random', body);
        } catch (previewError) {
          if (!previewError.data?.requiresRecentEncounterApproval && !previewError.data?.requiresRepeatPartnerApproval) throw previewError;
          // Preview แสดงได้ทันที การยืนยันประวัติซ้ำจะถามเฉพาะตอนกดสร้าง Queue
          repeatApproved = true;
          repeatApprovalData = previewError.data;
          preview = await postMatchmakingJson('/api/game-match/random', {
            ...body,
            allowRepeatPartnerOverride: true,
            allowRecentEncounterOverride: true
          });
        }

        if (cancelled) return;

        setAiRecommendation(preview);
        const previewPlayers = [
          ...(Array.isArray(preview.team1) ? preview.team1 : []),
          ...(Array.isArray(preview.team2) ? preview.team2 : [])
        ].map(player => player.name).filter(Boolean);
        appendAiActivity(
          `พบชุดที่เหมาะสม: ${previewPlayers.join(', ') || 'ผู้เล่น 4 คน'}`,
          'success'
        );
        if (!aiAutoEnabled) {
          aiProcessedSignatureRef.current = aiMatchmakingSignature;
        }
        setAiAutomationStatus(
          aiAutoEnabled
            ? mode === 'PICK_ONE'
              ? 'พบผู้เล่นคะแนนใกล้เคียงแล้ว กำลังสร้าง Queue อัตโนมัติ...'
              : `พบ Match แนะนำแล้ว กำลังสร้าง Queue ${matchCount} Match อัตโนมัติ...`
            : 'อัปเดต Match แนะนำแบบเรียลไทม์แล้ว'
        );

        if (aiAutoEnabled) {
          if (mode === 'PICK_ONE' && pickOneAutoAction === 'PREVIEW') {
            aiProcessedSignatureRef.current = aiMatchmakingSignature;
            setPreviewControlsOpen(true);
            setAiAutomationStatus('พบผู้เล่นหลักตามลำดับความสำคัญแล้ว • กำลังจัด Preview 4 Match');
            appendAiActivity('กำลังจัด Match แนะนำ 2 Match และช่วงคะแนนไม่เกิน 100 อีก 2 Match', 'success');
            await loadFixedPlayerPreviewsRef.current();
            if (!cancelled) {
              setAiAutomationStatus('จัด Preview แล้ว • รอเลือกและยืนยันสร้าง Queue');
            }
            return;
          }
          if (mode === 'PICK_ONE' && pickOneAutoAction === 'QUEUE') {
            if (repeatApproved) {
              const approved = window.confirm(
                `AI พบประวัติผู้เล่นซ้ำ\n\n${buildApproveHistoryText(repeatApprovalData || {})}\n\nต้องการ Approve และเพิ่ม Match นี้เข้า Queue หรือไม่?`
              );
              if (!approved) {
                aiProcessedSignatureRef.current = aiMatchmakingSignature;
                setAiAutomationStatus('ยกเลิกการสร้าง Queue เพราะยังไม่ได้ Approve คู่ซ้ำ');
                return;
              }
            }
            const created = await postMatchmakingJson(
              '/api/game-match/generate-queue',
              {
                ...body,
                allowRangeOverride: Boolean(pickedLockedPartner),
                allowRepeatPartnerOverride: repeatApproved,
                allowRecentEncounterOverride: repeatApproved
              }
            );
            if (cancelled) return;
            setLastResult(created);
            aiProcessedSignatureRef.current = aiMatchmakingSignature;
            setAiAutomationStatus('สร้าง Match Queue อัตโนมัติแล้ว');
            appendAiActivity('สร้าง Match Queue โดยไม่ผ่าน Preview สำเร็จ', 'success');
            if (onCreatedRef.current) await onCreatedRef.current(created);
            return;
          }
          const autoPreview = {
            ...preview,
            previewRank: [...new Set([
              ...(preview.team1 || []),
              ...(preview.team2 || [])
            ].map(player => normalizePlayerRank(player.level_rank, player.point)))].join(' / '),
            reason: preview.reason || 'AI Auto วิเคราะห์ชุดที่เหมาะสม'
          };
          const autoPreviewKey = [
            ...(autoPreview.team1 || []),
            ...(autoPreview.team2 || [])
          ].map(player => Number(player.user_id)).sort((a, b) => a - b).join('-');
          setPreviewType(mode === 'PICK_ONE' ? 'FIXED_PLAYER' : 'ACTIVE_RANK');
          setPreviewMatches(current => {
            const exists = current.some(match => [
              ...(match.team1 || []),
              ...(match.team2 || [])
            ].map(player => Number(player.user_id)).sort((a, b) => a - b).join('-') === autoPreviewKey);
            return exists ? current : [...current, autoPreview];
          });
          setSelectedPreviewKeys(current =>
            current.includes(autoPreviewKey) ? current : [...current, autoPreviewKey]
          );
          setPreviewControlsOpen(true);
          setPreviewOpen(true);
          aiProcessedSignatureRef.current = aiMatchmakingSignature;
          setAiAutomationStatus('พบ Match ที่เหมาะสมแล้ว • รอเลือกและยืนยันใน Preview');
          appendAiActivity('ส่ง Match เข้า Preview แล้ว • ยังไม่ได้สร้าง Queue', 'success');
          return;
        }

        if (!aiAutoEnabled) return;

        if (cancelled) return;

        const created = await postMatchmakingJson(
          '/api/game-match/generate-queue',
          body
        );

        if (cancelled) return;

        const createdRecommendation =
          Array.isArray(created.matches) && created.matches.length > 0
            ? created.matches[0]
            : created;

        setAiRecommendation(
          createdRecommendation
        );

        setLastResult(created);
        aiProcessedSignatureRef.current = aiMatchmakingSignature;

        const matchesToStart = Array.isArray(created.matches) && created.matches.length > 0
          ? created.matches
          : [created];
        appendAiActivity(
          `สร้าง Match Queue สำเร็จ ${matchesToStart.length} Match`,
          'success'
        );
        appendAiActivity('Match อยู่ใน Queue และรอ Admin กด Start', 'waiting');

        setAiAutomationStatus(
          `สร้าง Queue ${created.matchCount ||
          (mode === 'PICK_ONE' ? 1 : matchCount)
          } Match อัตโนมัติสำเร็จ`
        );


        /* =================================================
           REFRESH SERVER DATA ก่อน
        ================================================= */

        if (onCreatedRef.current) {

          await onCreatedRef.current(
            created
          );

        }


        /* =================================================
           PICK ONE -> ADVANCE
        ================================================= */

        if (mode === 'PICK_ONE') {
          const createdMatches = Array.isArray(created.matches)
            ? created.matches
            : [created];
          const queuedPlayerIds = new Set(
            createdMatches.flatMap(match => [
              ...(Array.isArray(match.team1) ? match.team1 : []),
              ...(Array.isArray(match.team2) ? match.team2 : [])
            ]).map(player => Number(player.user_id))
          );
          const nextPlayer = pickOneAnchorPlayers.find(player =>
            !queuedPlayerIds.has(Number(player.user_id)) &&
            Number(player.user_id) !== Number(pickedPlayer?.user_id)
          ) || null;

          setPickedPlayer(nextPlayer);

          setPickOnePlayers([]);

          setNearbySearchKeyword('');

          setAiRecommendation(null);

          aiProcessedSignatureRef.current = '';

          pickOneAttemptedIdsRef.current.clear();

          setAiAutomationStatus(
            nextPlayer
              ? `สร้าง Queue สำเร็จ • ทำต่อด้วยผู้เล่นหลัก ${nextPlayer.name}`
              : 'สร้าง Queue สำเร็จ • กำลังรอผู้เล่น ACTIVE ครบ 4 คน'
          );

        }
      } catch (requestError) {
        if (!cancelled) {
          if (mode === 'PICK_ONE' && aiAutoEnabled) {
            const currentId = Number(pickedPlayer?.user_id);
            pickOneAttemptedIdsRef.current.add(currentId);

            const nextPlayer = orderedSelectablePlayers.find(player => {
              const playerId = Number(player.user_id);
              return (
                playerId !== currentId &&
                !pickOneAttemptedIdsRef.current.has(playerId)
              );
            });

            if (nextPlayer) {
              setPickedPlayer(nextPlayer);
              setPickOnePlayers([]);
              setAiRecommendation(null);
              setError('');
              setAiAutomationStatus(
                `${pickedPlayer.name} จัดไม่ได้ • กำลังลองผู้เล่นถัดไป ${nextPlayer.name}`
              );
              aiProcessedSignatureRef.current = '';
              return;
            }
          }

          aiProcessedSignatureRef.current = aiMatchmakingSignature;
          setError(requestError.message);
          appendAiActivity(
            `ยังจัด Match ไม่ได้: ${requestError.message}`,
            'waiting'
          );
          if (
            requestError.data?.requiresRecentEncounterApproval ||
            requestError.data?.requiresRepeatPartnerApproval
          ) {
            setAiAutomationStatus(
              'ผู้เล่นชุดปัจจุบันติดกติกา Match ล่าสุด • AI ยังทำงานและกำลังรอชุดถัดไป'
            );
          }
          if (
            !requestError.data?.requiresRecentEncounterApproval &&
            !requestError.data?.requiresRepeatPartnerApproval
          ) {
            setAiAutomationStatus(
              'AI ยังทำงาน • รอผู้เล่นหรือสถานะเปลี่ยนเพื่อจัดรอบถัดไป'
            );
          }
        }
      } finally {
        aiRequestInFlightRef.current = false;
        setAiAutoLoading(false);
        setAiAutomationCycle(current => current + 1);
      }
    }, 500);

    return () => {
      // เมื่อ request เริ่มแล้ว ให้ทำขั้น Preview -> Create Queue ให้จบ
      // แม้ Socket จะ refresh state ระหว่างทาง
      if (!requestStarted) cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [
    aiAutoEnabled,
    appendAiActivity,
    aiAutomationCycle,
    aiMatchmakingSignature,
    aiEligiblePlayingPlayers.length,
    aiOrderedActivePlayers.length,
    approvePlaying,
    groupId,
    matchCount,
    mode,
    normalizedPlayers,
    orderedSelectablePlayers,
    pickOneAnchorPlayers,
    pickOnePlayers,
    pickOneOrderedPlayers,
    pickedLockedPartner,
    pickedPlayer,
    pickOneAutoAction,
    playStyle,
    pointRange,
    priority,
    requiredPlayerCount
  ]);

  const renderTeam = (title, players) => (
    <div className="rmp-result-team">
      <strong>{title}</strong>
      {players.map(player => (
        <div key={player.user_id} className="rmp-result-player">
          <span>
            <AdminPlayerName player={player}/>
            <small
              className={`rmp-inline-status status-${getStatus(player).toLowerCase()}`}
              style={{ marginLeft: 6 }}
            >
              {getStatus(player)}
            </small>
          </span>
          <span>
            {normalizePlayerRank(player.level_rank, player.point)} • {player.point}
          </span>
        </div>
      ))}
    </div>
  );

  const renderPreviewTeam = (title, players, matchIndex, teamKey, opponentPlayers = []) => {
    const playerNameById = new Map(
      normalizedPlayers.map(player => [Number(player.user_id), player.name])
    );
    const historyCount = (map, left, right) => Number(
      map[`${left.user_id}-${right.user_id}`] ||
      map[`${right.user_id}-${left.user_id}`] || 0
    );
    const teammateRepeat = players.length === 2
      ? { names: `${players[0].name} + ${players[1].name}`, count: historyCount(pairMap, players[0], players[1]) }
      : null;
    const opponentRepeats = players.flatMap(player => opponentPlayers.map(opponent => ({
      names: `${player.name} ↔ ${opponent.name}`,
      count: historyCount(opponentMap, player, opponent)
    }))).filter(item => item.count > 0);

    return (
      <div className="rmp-result-team">
        <strong>{title}</strong>
        {(teammateRepeat?.count > 0 || opponentRepeats.length > 0) && (
          <div className="rmp-repeat-summary">
            {teammateRepeat?.count > 0 && <span>👥 {teammateRepeat.names}: คู่กัน {teammateRepeat.count} ครั้ง</span>}
            {opponentRepeats.map((item, index) => <span key={`${item.names}-${index}`}>⚔️ {item.names}: ตรงข้าม {item.count} ครั้ง</span>)}
          </div>
        )}
        {players.map((player, playerIndex) => {
          const recentMatches = (
            previewHistoryByUserId?.[Number(player.user_id)]?.recentMatches || []
          ).slice(0, 3);
          const currentTeammateIds = new Set(
            players
              .filter(item => Number(item.user_id) !== Number(player.user_id))
              .map(item => Number(item.user_id))
          );
          const teammateHistory = recentMatches.map((match, index) => ({
            label: `M${index + 1}`,
            players: (match.teammates || []).map(id => ({
              id: Number(id),
              name: playerNameById.get(Number(id)) || `#${id}`,
              repeatedCurrentPartner: currentTeammateIds.has(Number(id))
            }))
          }));
          const opponentHistory = recentMatches.map((match, index) =>
            `M${index + 1}: ${(match.opponents || []).map(id =>
              playerNameById.get(Number(id)) || `#${id}`
            ).join(', ') || '-'}`
          );

          return (
            <div
              key={player.user_id}
              className="rmp-result-player"
              style={{ display: 'grid', gridTemplateColumns: '42px 1fr auto', gap: 8 }}
            >
              <PlayerAvatar player={player} size="normal" />
              <span>
                <b><AdminPlayerName player={player}/></b>{' '}
                <small className={`rmp-inline-status status-${getStatus(player).toLowerCase()}`}>
                  {getStatus(player)}
                </small>
                <small className={`rmp-preview-intensity intensity-${intensityClass(player)}`}>
                  {intensityText(player)}
                </small>
                {['QUEUE', 'PLAY'].includes(getStatus(player)) && (
                  <small style={{ display: 'block', color: '#1d4ed8', fontWeight: 700 }}>
                    {getPlayerMatchStatusLabel(player)}
                  </small>
                )}
                <small style={{ display: 'block', marginTop: 3 }}>
                  👥 คู่ 3 Match ล่าสุด:{' '}
                  {teammateHistory.length === 0 ? '-' : teammateHistory.map((history, historyIndex) => (
                    <Fragment key={`${player.user_id}-${history.label}`}>
                      {historyIndex > 0 && ' • '}
                      {history.label}:{' '}
                      {history.players.length === 0 ? '-' : history.players.map((historyPlayer, itemIndex) => (
                        <Fragment key={`${history.label}-${historyPlayer.id}`}>
                          {itemIndex > 0 && ', '}
                          <span className={historyPlayer.repeatedCurrentPartner ? 'rmp-repeated-partner-name' : ''}>
                            {historyPlayer.name}
                          </span>
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                </small>
                <small style={{ display: 'block' }}>
                  ⚔️ ตรงข้าม 3 Match ล่าสุด: {opponentHistory.join(' • ') || '-'}
                </small>
                <small style={{
                  display: 'block',
                  marginTop: 3,
                  color: '#dc2626',
                  fontWeight: 700
                }}>
                  ⏱ {getStatus(player) === 'PLAY' ? 'เริ่มนับใหม่ ' : 'รอ '}
                  {formatWaitTime(player, now)}
                </small>
                <button
                  type="button"
                  className={`rmp-preview-edit-button ${
                    editingPreviewSlot === `${matchIndex}-${teamKey}-${playerIndex}`
                      ? 'is-open'
                      : ''
                  }`}
                  onClick={() => {
                    setPreviewEditSearch('');
                    setEditingPreviewSlot(current =>
                      current === `${matchIndex}-${teamKey}-${playerIndex}`
                        ? null
                        : `${matchIndex}-${teamKey}-${playerIndex}`
                    );
                  }}
                >
                  {editingPreviewSlot === `${matchIndex}-${teamKey}-${playerIndex}`
                    ? '✕ ปิดการแก้ไข'
                    : '✏️ เปลี่ยนผู้เล่น'}
                </button>
                {editingPreviewSlot === `${matchIndex}-${teamKey}-${playerIndex}` && (
                  <div className="rmp-preview-edit-picker">
                    <div className="rmp-preview-edit-search">
                      <span aria-hidden="true">🔎</span>
                      <input
                        type="search"
                        autoFocus
                        value={previewEditSearch}
                        placeholder="ค้นหาชื่อผู้เล่น..."
                        onChange={event => setPreviewEditSearch(event.target.value)}
                      />
                      {previewEditSearch && (
                        <button
                          type="button"
                          onClick={() => setPreviewEditSearch('')}
                          aria-label="ล้างชื่อที่ค้นหา"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <small className="rmp-preview-edit-hint">
                      ● ACTIVE แสดงเป็นอันดับแรก • กดชื่อเพื่อเปลี่ยนทันที
                    </small>
                    <div className="rmp-preview-edit-options">
                      {normalizedPlayers
                      .filter(candidate => getStatus(candidate) !== 'HOLD')
                      .filter(candidate =>
                        selectedPreviewRanks.length === 0 ||
                        selectedPreviewRanks.includes(
                          normalizePlayerRank(candidate.level_rank, candidate.point)
                        ) || Number(candidate.user_id) === Number(player.user_id)
                      )
                      .filter(candidate =>
                        matchesSearch(candidate.name, previewEditSearch)
                      )
                      .sort((left, right) =>
                        Number(getStatus(right) === 'ACTIVE') -
                          Number(getStatus(left) === 'ACTIVE') ||
                        getRankDistance(player, left) - getRankDistance(player, right) ||
                        Math.abs(Number(left.point || 0) - Number(player.point || 0)) -
                          Math.abs(Number(right.point || 0) - Number(player.point || 0))
                      )
                      .map(candidate => (
                        <button
                          type="button"
                          key={candidate.user_id}
                          className={
                            Number(candidate.user_id) === Number(player.user_id)
                              ? 'is-current'
                              : ''
                          }
                          onClick={() => editPreviewPlayer(
                            matchIndex,
                            teamKey,
                            playerIndex,
                            candidate.user_id
                          )}
                        >
                          <span>
                            {getStatus(candidate) === 'ACTIVE' ? '● ' : ''}
                            <b>{candidate.name}</b>
                          </span>
                          <small>
                            {normalizePlayerRank(candidate.level_rank, candidate.point)}
                            {' '}• {candidate.point} คะแนน • {getStatus(candidate)}
                          </small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </span>
              <span>
                {normalizePlayerRank(player.level_rank, player.point)} • {player.point}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPreviewRankSelector = () => (
    <div className="rmp-preview-rank-selector">
      <div className="rmp-preview-rank-heading">
        <strong>เลือก Rank สำหรับ Preview</strong>
        <small>{selectedPreviewRanks.length}/4 Rank</small>
      </div>
      <div className="rmp-player-scope" role="group" aria-label="เลือกสถานะผู้เล่นสำหรับ Preview">
        <b>ผู้เล่น Preview</b>
        <button type="button" className={playerScope === 'ACTIVE' ? 'is-active' : ''} onClick={() => { setPlayerScope('ACTIVE'); setApprovePlaying(false); resetInputs(); }}>
          ACTIVE <span>{activePlayers.length}</span>
        </button>
        <button type="button" className={playerScope === 'ALL' ? 'is-active' : ''} onClick={() => { setPlayerScope('ALL'); setApprovePlaying(true); resetInputs(); }}>
          ทั้งหมด <span>{styleEligiblePlayers.filter(player => !['HOLD', 'CHECKOUT'].includes(getStatus(player))).length}</span>
        </button>
      </div>
      <div className="rmp-rank-filter-grid">
        {RANK_ORDER.map(rank => {
          const selected = selectedPreviewRanks.includes(rank);
          const playersInRank = normalizedPlayers.filter(player =>
            normalizePlayerRank(player.level_rank, player.point) === rank
          );
          const activeCount = playersInRank.filter(
            player => getStatus(player) === 'ACTIVE'
          ).length;
          const playCount = playersInRank.filter(
            player => getStatus(player) === 'PLAY'
          ).length;
          return (
            <button
              type="button"
              key={rank}
              data-rank={rank}
              className={`rmp-rank-filter-button${selected ? ' is-selected' : ''}`}
              aria-pressed={selected}
              onClick={() => togglePreviewRank(rank)}
            >
              {selected ? '✓ ' : ''}{rank} •{' '}
              <span style={{ color: selected ? '#dbeafe' : '#2563eb', fontWeight: 800 }}>
                A {activeCount}
              </span>
              {' '}•{' '}
              <span style={{ color: selected ? '#fee2e2' : '#dc2626', fontWeight: 800 }}>
                P {playCount}
              </span>
            </button>
          );
        })}
        {selectedPreviewRanks.length > 0 && (
          <button type="button" onClick={() => setSelectedPreviewRanks([])}>
            ล้าง • ใช้ทุก Rank
          </button>
        )}
      </div>
      <small>
        {selectedPreviewRanks.length === 0
          ? 'ยังไม่เลือก: ระบบใช้ทุก Rank'
          : `ใช้เฉพาะ ${selectedPreviewRanks.join(', ')}`}
        {' '}• A = ACTIVE • P = PLAY
      </small>
    </div>
  );

  const createdMatches = lastResult
    ? Array.isArray(lastResult.matches) && lastResult.matches.length > 0
      ? lastResult.matches
      : [lastResult]
    : [];

  const allLongestWaitingActivePlayers = normalizedPlayers
    .filter(player => {
      if (getStatus(player) !== 'ACTIVE') return false;
      const startedAt = getQueueStartedAt(player);
      const explicitWaitMinutes = getExplicitWaitMinutes(player);
      if (explicitWaitMinutes !== null) {
        return explicitWaitMinutes >= waitingAlertMinutes;
      }
      return Number.isFinite(startedAt) &&
        startedAt !== Number.MAX_SAFE_INTEGER &&
        now - startedAt >= waitingAlertMinutes * 60 * 1000;
    })
    .sort((left, right) => {
      const leftStartedAt = getQueueStartedAt(left) === Number.MAX_SAFE_INTEGER
        ? now - Number(getExplicitWaitMinutes(left) || 0) * 60000
        : getQueueStartedAt(left);
      const rightStartedAt = getQueueStartedAt(right) === Number.MAX_SAFE_INTEGER
        ? now - Number(getExplicitWaitMinutes(right) || 0) * 60000
        : getQueueStartedAt(right);
      return leftStartedAt - rightStartedAt || Number(left.user_id) - Number(right.user_id);
    });
  const activeSessionPlayers = normalizedPlayers.filter(
    player => getStatus(player) === 'ACTIVE'
  );
  const highestSessionGameCount = normalizedPlayers
    .filter(player => ['ACTIVE', 'QUEUE', 'PLAY'].includes(getStatus(player)))
    .reduce(
    (highest, player) => Math.max(highest, Number(player.sessionFinishedMatches || 0)),
    0
    );
  const lowGamePlayers = activeSessionPlayers
    .filter(player => {
      const games = Number(player.sessionFinishedMatches || 0);
      const checkedInAt = parsePlayerTime(player.checkin_time);
      if (games === 0) {
        return Number.isFinite(checkedInAt) && checkedInAt !== Number.MAX_SAFE_INTEGER &&
          now - checkedInAt >= NO_GAME_ALERT_MINUTES * 60 * 1000;
      }
      return highestSessionGameCount > games;
    })
    .sort((left, right) =>
      Number(left.sessionFinishedMatches || 0) - Number(right.sessionFinishedMatches || 0) ||
      getArrivalTime(left) - getArrivalTime(right) ||
      Number(left.user_id) - Number(right.user_id)
    );
  const firstCheckinPlayers = activeSessionPlayers
    .filter(player => {
      const checkedInAt = parsePlayerTime(player.checkin_time);
      return Number.isFinite(checkedInAt) && checkedInAt !== Number.MAX_SAFE_INTEGER;
    })
    .sort((left, right) =>
      parsePlayerTime(left.checkin_time) - parsePlayerTime(right.checkin_time) ||
      Number(left.user_id) - Number(right.user_id)
    );
  const waitingAlertPlayerIds = new Set(
    allLongestWaitingActivePlayers.map(player => Number(player.user_id))
  );
  const lowGamePlayerIds = new Set(
    lowGamePlayers.map(player => Number(player.user_id))
  );
  const lowGameOnlyPlayers = lowGamePlayers.filter(
    player => !waitingAlertPlayerIds.has(Number(player.user_id))
  );
  const allAlertPlayers = [];
  const combinedAlertLength = Math.max(
    allLongestWaitingActivePlayers.length,
    lowGameOnlyPlayers.length
  );
  for (let index = 0; index < combinedAlertLength; index += 1) {
    if (allLongestWaitingActivePlayers[index]) {
      allAlertPlayers.push(allLongestWaitingActivePlayers[index]);
    }
    if (lowGameOnlyPlayers[index]) {
      allAlertPlayers.push(lowGameOnlyPlayers[index]);
    }
  }
  const activeAlertPlayers = playerAlertMode === 'LOW_GAMES'
    ? lowGamePlayers
    : playerAlertMode === 'WAITING'
      ? allLongestWaitingActivePlayers
      : playerAlertMode === 'CHECKIN_FIRST'
        ? firstCheckinPlayers
      : allAlertPlayers;
  const waitingAlertPageCount = Math.max(1, Math.ceil(activeAlertPlayers.length / waitingAlertLimit));
  const visibleAlertPlayers = activeAlertPlayers.slice(
    waitingAlertPage * waitingAlertLimit,
    (waitingAlertPage + 1) * waitingAlertLimit
  );
  useEffect(() => {
    setWaitingAlertPage(current => Math.min(current, waitingAlertPageCount - 1));
  }, [waitingAlertPageCount]);

  useEffect(() => {
    setWaitingAlertPage(0);
  }, [playerAlertMode, waitingAlertMinutes]);

  useEffect(() => {
    localStorage.setItem('match_waiting_alert_minutes_v2', String(waitingAlertMinutes));
  }, [waitingAlertMinutes]);

  useEffect(() => {
    if (playerAlertMode === 'WAITING' && allLongestWaitingActivePlayers.length === 0 && lowGamePlayers.length > 0) {
      setPlayerAlertMode('LOW_GAMES');
    } else if (playerAlertMode === 'LOW_GAMES' && lowGamePlayers.length === 0 && allLongestWaitingActivePlayers.length > 0) {
      setPlayerAlertMode('WAITING');
    }
  }, [allLongestWaitingActivePlayers.length, lowGamePlayers.length, playerAlertMode]);

  const getGenerateButtonLabel = () => {
    if (loading || aiAutoLoading) {
      if (mode === 'AI_RANDOM') {
        return '🤖 AI กำลังวิเคราะห์และสร้าง Queue...';
      }

      return mode === 'PRIORITY_SELECT' &&
        selectedManualPlayers.length < 4
        ? 'กำลังสุ่มผู้เล่นที่เหลือ...'
        : `กำลังสร้าง ${matchCount} Match...`;
    }

    if (mode === 'PRIORITY_SELECT') {
      if (selectedManualPlayers.length === 0) {
        return '🎲 เลือกอย่างน้อย 1 คนเพื่อ Random';
      }

      if (selectedManualPlayers.length < 4) {
        return (
          `🎲 Random อีก ${4 - selectedManualPlayers.length} คน ` +
          'และสร้าง Queue'
        );
      }

      return '➕ สร้าง Queue จากผู้เล่น 4 คน';
    }

    if (mode === 'AI_RANDOM') {
      return aiAutoEnabled
        ? `🤖 AI รอสร้าง Queue อัตโนมัติครั้งละ ${matchCount} Match`
        : `🤖 สร้าง Queue ${matchCount} Match จาก AI`;
    }

    if (mode === 'PICK_ONE' && aiAutoEnabled) {
      return '🤖 Pick One AI Auto กำลังรอสร้าง Queue';
    }

    if (supportsMultipleMatches && matchCount > 1) {
      return `➕ สร้าง Queue ${matchCount} Match`;
    }

    return '➕ สร้าง Queue';
  };
  void getGenerateButtonLabel;

  const renderPreviewControls = () => (
    <section className={`rmp-preview-controls${previewControlsOpen ? ' is-open' : ''}`}>
      <button
        type="button"
        className="rmp-preview-controls-toggle"
        aria-expanded={previewControlsOpen}
        onClick={() => setPreviewControlsOpen(true)}
      >
        <span>
          <strong>ส่วน Preview Match</strong>
          <small>เลือก Rank และทดลองดู Match ก่อนสร้าง Queue</small>
        </span>
        <b>▲ เปิดตลอด</b>
      </button>

      {previewControlsOpen && (
        <div className="rmp-preview-controls-body">
          {mode !== 'PRIORITY_SELECT' && renderPreviewRankSelector()}
          <div className="rmp-preview-action-row">
            <button
              type="button"
              className={`rmp-generate-button ${mode === 'PRIORITY_SELECT' &&
                selectedManualPlayers.length > 0 &&
                selectedManualPlayers.length < 4 ? 'random-fill' : ''}`}
              disabled={
                loading || previewLoading || aiAutoLoading ||
                lacksRequiredStylePlayers ||
                (AUTOMATIC_MODES.has(mode) && activePlayers.length < requiredPlayerCount)
              }
              onClick={loadQueuePreviews}
            >
              {previewLoading ? 'กำลังสร้าง…' : '👀 Preview ก่อนสร้าง Queue'}
            </button>
            <button
              type="button"
              className="rmp-generate-button"
              disabled={loading || previewLoading || !pickedPlayer}
              onClick={() => loadFixedPlayerPreviews()}
            >
              🎯 Preview 3 Match
            </button>
            <button
              type="button"
              className="rmp-generate-button rmp-perfect-match-button"
              disabled={loading || previewLoading || perfectMatchLoading}
              onClick={() => {
                setPerfectMatchOpen(true);
                setPerfectMatchMessage(null);
              }}
            >
              💯 จัดแมท 100%
            </button>
          </div>
        </div>
      )}
    </section>
  );

  if (isMonitorAi) {
    const missingPlayers = Math.max(
      0,
      requiredPlayerCount - aiOrderedActivePlayers.length
    );

    return (
      <aside
        className={`rmp-monitor-ai ${aiAutoEnabled ? 'is-running' : 'is-stopped'}`}
        aria-label="AI Random อัตโนมัติ"
      >
        <div className="rmp-monitor-ai-title">
          <span className={`rmp-monitor-ai-dot ${aiAutoLoading ? 'is-loading' : ''}`} />
          <span>
            <strong>🤖 AI Random</strong>
            <small>
              ACTIVE {aiOrderedActivePlayers.length} คน
              {missingPlayers > 0 ? ` • รออีก ${missingPlayers}` : ' • พร้อมจัด'}
            </small>
          </span>
        </div>

        <div className="rmp-monitor-ai-count" aria-label="จำนวน Match ต่อรอบ">
          {[1, 3].map(count => (
            <button
              type="button"
              key={count}
              className={matchCount === count ? 'selected' : ''}
              disabled={aiAutoEnabled || aiAutoLoading}
              onClick={() => {
                setMatchCount(count);
                setError('');
                setAiAutomationStatus('');
                aiProcessedSignatureRef.current = '';
              }}
            >
              {count} Match
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`rmp-monitor-ai-toggle ${aiAutoEnabled ? 'stop' : 'start'}`}
          onClick={() => {
            const nextEnabled = !aiAutoEnabled;
            setAiAutoEnabled(nextEnabled);
            setError('');
            setAiAutomationStatus(
              nextEnabled
                ? `เริ่ม AI สร้างครั้งละ ${matchCount} Match`
                : 'หยุด AI Random แล้ว'
            );
            aiProcessedSignatureRef.current = '';
          }}
        >
          {aiAutoEnabled ? '■ หยุด' : '▶ เริ่ม'}
        </button>

        <span className="rmp-monitor-ai-status" title={aiAutomationStatus || undefined}>
          {aiAutoLoading
            ? 'กำลังวิเคราะห์...'
            : aiAutoEnabled
              ? aiAutomationStatus || 'กำลังทำงาน'
              : 'หยุดอยู่'}
        </span>

        {error && <span className="rmp-monitor-ai-error">⚠ {error}</span>}
      </aside>
    );
  }

  return (
    <section
      className="section-card random-match-panel"
      data-feature-version="daily-play-style-v3"
    >
      {showWaitingAlert && (waitingAlertVisible ? (
        <aside
          className="rmp-waiting-alert-panel"
          role="status"
          aria-label="ผู้เล่นที่รอนานที่สุด"
          onPointerDown={event => {
            if (event.target.closest('button') || !event.target.closest('.rmp-waiting-alert-header')) return;
            const rect = event.currentTarget.getBoundingClientRect();
            waitingAlertDragRef.current = { kind: 'panel', pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={moveWaitingAlert}
          onPointerUp={finishWaitingAlertDrag}
          onPointerCancel={finishWaitingAlertDrag}
          style={{
            position: 'fixed',
            top: waitingAlertPanelPosition ? waitingAlertPanelPosition.y : (window.innerWidth <= 700 ? 116 : 16),
            left: waitingAlertPanelPosition ? waitingAlertPanelPosition.x : (window.innerWidth <= 700 ? 8 : 202),
            right: 'auto',
            zIndex: 990,
            width: 'min(360px, calc(100vw - 32px))',
            padding: 14,
            border: playerAlertMode === 'LOW_GAMES' ? '1px solid #7dd3fc' : '1px solid #fdba74',
            borderRadius: 18,
            background: playerAlertMode === 'LOW_GAMES'
              ? 'linear-gradient(150deg, #e0f2fe 0%, #f8fafc 48%, #ffffff 100%)'
              : 'linear-gradient(150deg, #ffedd5 0%, #fff7ed 48%, #ffffff 100%)',
            boxShadow: playerAlertMode === 'LOW_GAMES' ? '0 18px 45px rgba(2,132,199,.2)' : '0 18px 45px rgba(234,88,12,.2)',
            touchAction: 'auto'
          }}
        >
          <div className="rmp-waiting-alert-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 10,
            cursor: 'grab',
            touchAction: 'none',
            userSelect: 'none'
          }}>
            <div style={{ minWidth: 0 }}>
              <strong style={{ color: playerAlertMode === 'LOW_GAMES' ? '#075985' : '#9a3412', fontSize: 15 }}>
                {playerAlertMode === 'ALL'
                  ? '⚠️ ผู้เล่นที่ควรได้ลงสนาม'
                  : playerAlertMode === 'CHECKIN_FIRST'
                  ? '✅ ลำดับผู้เล่น Check-in'
                  : playerAlertMode === 'LOW_GAMES'
                  ? '⚠️ ผู้เล่นที่ได้ลงเกมน้อย'
                  : `⚠️ ผู้เล่นรอเกิน ${waitingAlertMinutes} นาที`}
              </strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 7 }}>
                <button
                  type="button"
                  onClick={() => setPlayerAlertMode('ALL')}
                  style={{ border: '1px solid #a78bfa', borderRadius: 999, padding: '5px 9px', color: playerAlertMode === 'ALL' ? '#fff' : '#5b21b6', background: playerAlertMode === 'ALL' ? '#7c3aed' : '#f5f3ff', fontWeight: 800 }}
                >
                  ALL ({allAlertPlayers.length})
                </button>
                <button
                  type="button"
                  onClick={() => setPlayerAlertMode('WAITING')}
                  style={{ border: '1px solid #fdba74', borderRadius: 999, padding: '5px 9px', color: playerAlertMode === 'WAITING' ? '#fff' : '#9a3412', background: playerAlertMode === 'WAITING' ? '#f97316' : '#fff7ed', fontWeight: 800 }}
                >
                  รอนาน ({allLongestWaitingActivePlayers.length})
                </button>
                <button
                  type="button"
                  onClick={() => setPlayerAlertMode('LOW_GAMES')}
                  style={{ border: '1px solid #7dd3fc', borderRadius: 999, padding: '5px 9px', color: playerAlertMode === 'LOW_GAMES' ? '#fff' : '#075985', background: playerAlertMode === 'LOW_GAMES' ? '#0284c7' : '#f0f9ff', fontWeight: 800 }}
                >
                  เกมน้อย ({lowGamePlayers.length})
                </button>
                <button
                  type="button"
                  onClick={() => setPlayerAlertMode('CHECKIN_FIRST')}
                  style={{ border: '1px solid #86efac', borderRadius: 999, padding: '5px 9px', color: playerAlertMode === 'CHECKIN_FIRST' ? '#fff' : '#166534', background: playerAlertMode === 'CHECKIN_FIRST' ? '#16a34a' : '#f0fdf4', fontWeight: 800 }}
                >
                  ลำดับ Check-in ({firstCheckinPlayers.length})
                </button>
                {[5, 10, 15, 20].map(minutes => (
                  <button
                    type="button"
                    key={minutes}
                    onClick={() => setWaitingAlertMinutes(minutes)}
                    aria-pressed={waitingAlertMinutes === minutes}
                    style={{ border: '1px solid #cbd5e1', borderRadius: 999, padding: '5px 9px', color: waitingAlertMinutes === minutes ? '#fff' : '#334155', background: waitingAlertMinutes === minutes ? '#334155' : '#fff', fontWeight: 800 }}
                  >
                    {minutes} นาที
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              aria-label="ปิดรายการผู้เล่นที่รอนาน"
              onClick={() => setWaitingAlertVisible(false)}
              style={{ width: 30, height: 30, border: '1px solid #cbd5e1', borderRadius: 9, color: '#475569', background: '#fff', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>

          <ol style={{ margin: '12px 0 0', padding: '2px 4px 2px 30px', maxHeight: 'min(62vh, 610px)', overflowY: 'auto', scrollbarGutter: 'stable' }}>
            {activeAlertPlayers.length === 0 && (
              <li style={{ color: '#64748b' }}>
                {playerAlertMode === 'ALL'
                  ? 'ยังไม่มีผู้เล่นที่เข้าเกณฑ์รอนานหรือเกมน้อย'
                  : playerAlertMode === 'CHECKIN_FIRST'
                  ? 'ยังไม่มีผู้เล่น ACTIVE ที่มีเวลา Check-in'
                  : playerAlertMode === 'LOW_GAMES'
                  ? 'ยังไม่มีผู้เล่นที่เข้าเกณฑ์เกมน้อย'
                  : `ยังไม่มีผู้เล่น ACTIVE ที่รอเกิน ${waitingAlertMinutes} นาที`}
              </li>
            )}
            {visibleAlertPlayers.map(player => {
              const isCheckinFirst = playerAlertMode === 'CHECKIN_FIRST';
              const isWaitingAlert = !isCheckinFirst && waitingAlertPlayerIds.has(Number(player.user_id));
              const isLowGameAlert = !isCheckinFirst && lowGamePlayerIds.has(Number(player.user_id));
              const checkinOrder = playerAlertMode === 'CHECKIN_FIRST'
                ? firstCheckinPlayers.findIndex(item => Number(item.user_id) === Number(player.user_id)) + 1
                : 0;
              return (
              <li key={player.user_id} style={{ marginTop: 7, padding: '8px 8px 8px 4px', border: isCheckinFirst ? '1px solid #86efac' : isWaitingAlert ? '1px solid #fed7aa' : '1px solid #bae6fd', borderRadius: 11, background: '#ffffffd9' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span>
                    <b><AdminPlayerName player={player}/></b>{' '}
                    <span>Rank {normalizePlayerRank(player.level_rank, player.point)}</span>{' '}
                    {isWaitingAlert && (
                      <strong style={{ color: '#dc2626' }}>• รอ {formatWaitTime(player, now)}</strong>
                    )}{' '}
                    {isLowGameAlert && (
                      <strong style={{ color: '#0369a1' }}>
                        • รอบนี้ {Number(player.sessionFinishedMatches || 0)} เกม
                        {' / ทั้งหมด '}{getPlayerGames(player)} เกม
                        {Number(player.sessionFinishedMatches || 0) === 0
                          ? ` · เช็กอิน ${formatElapsedHoursMinutes(player.checkin_time, now)}`
                          : ''}
                      </strong>
                    )}
                    {checkinOrder > 0 && (
                      <strong style={{ display: 'block', color: '#15803d', marginTop: 3 }}>
                        • Check-in ลำดับ {checkinOrder}
                        {' · '}มาแล้ว {formatElapsedHoursMinutes(player.checkin_time, now)}
                      </strong>
                    )}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto', marginLeft: 'auto' }}>
                    <button
                      type="button"
                      disabled={previewLoading}
                      onClick={() => {
                        setMode('PICK_ONE');
                        setPickedPlayer(player);
                        setSecondPickedPlayer(null);
                        setWaitingAlertVisible(false);
                        loadFixedPlayerPreviews(player);
                      }}
                      style={{ color: '#fff', border: 0, borderRadius: 9, padding: '7px 9px', background: 'linear-gradient(135deg, #2563eb, #06b6d4)', boxShadow: '0 4px 10px #2563eb33', fontWeight: 800, cursor: 'pointer' }}
                    >
                      {previewLoading ? 'กำลังโหลด…' : '👀 Preview Match'}
                    </button>
                    <button
                      type="button"
                      disabled={statusUpdatingId === Number(player.user_id)}
                      onClick={() => updateAvailabilityStatus(player, 'HOLD')}
                      style={{ color: '#fff', border: 0, borderRadius: 9, padding: '7px 10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 4px 10px #d9770633', fontWeight: 800, cursor: 'pointer' }}
                    >
                      {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก…' : '⏸ HOLD'}
                    </button>
                  </span>
                </div>
              </li>
              );
            })}
          </ol>
          {activeAlertPlayers.length > waitingAlertLimit && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                disabled={waitingAlertPage === 0}
                onClick={() => setWaitingAlertPage(page => Math.max(0, page - 1))}
                style={{ border: '1px solid #cbd5e1', borderRadius: 9, padding: '6px 10px', background: '#fff', color: '#334155' }}
              >
                ← ก่อนหน้า
              </button>
              <b style={{ fontSize: 12 }}>หน้า {waitingAlertPage + 1}/{waitingAlertPageCount}</b>
              <button
                type="button"
                disabled={waitingAlertPage >= waitingAlertPageCount - 1}
                onClick={() => setWaitingAlertPage(page => Math.min(waitingAlertPageCount - 1, page + 1))}
                style={{ border: '1px solid #2563eb', borderRadius: 9, padding: '6px 10px', background: waitingAlertPage >= waitingAlertPageCount - 1 ? '#e2e8f0' : '#2563eb', color: waitingAlertPage >= waitingAlertPageCount - 1 ? '#94a3b8' : '#fff', fontWeight: 800 }}
              >
                ถัดไป →
              </button>
            </div>
          )}
        </aside>
      ) : (
        <button
          className={`rmp-waiting-alert-trigger${allLongestWaitingActivePlayers.length === 0 ? ' is-empty' : ''}`}
          type="button"
          onPointerDown={event => {
            const rect = event.currentTarget.getBoundingClientRect();
            waitingAlertMovedRef.current = false;
            waitingAlertDragRef.current = { kind: 'button', pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={moveWaitingAlert}
          onPointerUp={finishWaitingAlertDrag}
          onPointerCancel={finishWaitingAlertDrag}
          onClick={event => {
            if (waitingAlertMovedRef.current) { waitingAlertMovedRef.current = false; return; }
            setWaitingAlertPanelPosition(positionWaitingPanelBeside(event.currentTarget));
            setWaitingAlertVisible(true);
          }}
          style={{
            position: 'fixed',
            top: waitingAlertPosition ? waitingAlertPosition.y : 8,
            left: waitingAlertPosition ? waitingAlertPosition.x : 8,
            right: 'auto',
            zIndex: 990,
            minHeight: 46,
            padding: '6px 12px 6px 6px',
            border: '1px solid #fdba74',
            borderRadius: 999,
            color: '#9a3412',
            background: 'linear-gradient(135deg,#fff7ed,#ffedd5)',
            boxShadow: '0 8px 20px rgba(234,88,12,.2)',
            cursor: 'grab',
            touchAction: 'none'
          }}
        >
          <i aria-hidden="true">⚠️</i>
          <span>แจ้งเตือนผู้เล่น</span>
          {allLongestWaitingActivePlayers.length > 0 && <b>{allLongestWaitingActivePlayers.length}</b>}
        </button>
      ))}

      <div className="rmp-heading">
        <h3>Matchmaking</h3>
      </div>

      <div className="rmp-sticky-mode-bar">
      <div className="rmp-mode-grid">
        {MODES.map(item => (
          <label
            key={item.value}
            className={`rmp-mode-card ${mode === item.value ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="matchmaking-mode"
              value={item.value}
              checked={mode === item.value}
              onChange={() => {
                setMode(item.value);
                setApprovePlaying(item.value === 'AI_RANDOM');
                setAiAutoEnabled(false);
                setAiAutomationStatus('');
                setAiRecommendation(null);
                setAdvancedSettingsOpen(false);
                setExpandedInfoPanel(null);
                aiProcessedSignatureRef.current = '';
                pickOneAttemptedIdsRef.current.clear();
                if (item.value === 'PRIORITY_SELECT') {
                  setPriority('WAIT_LONGEST');
                }
                if (item.value === 'AI_RANDOM') {
                  setPriority('FIFO');
                  setPlayStyle('ANY');
                  setMatchCount(1);
                }
                if (item.value === 'PICK_ONE') {
                  setPickAnchorOpen(true);
                  setNearbyPanelOpen(true);
                  setPointRange(currentRange => Math.min(150, currentRange));
                }
                if (!AUTOMATIC_MODES.has(item.value)) setMatchCount(1);
                resetInputs();
                setLastResult(null);
                setError('');
              }}
            />
            <span>
              <strong className="rmp-mode-label-full">{item.label}</strong>
              <strong className="rmp-mode-label-short">{item.shortLabel}</strong>
              <small>{item.description}</small>
            </span>
          </label>
        ))}
      </div>
      </div>

      {(mode === 'AI_RANDOM' || mode === 'PICK_ONE') && (
        <div className={`rmp-ai-auto-panel${aiAutoPanelOpen ? '' : ' is-collapsed'}`}>
          <div className="rmp-ai-auto-heading">
            <div>
              <strong>
                {mode === 'PICK_ONE'
                  ? '🤖 Pick One AI Auto'
                  : '🤖 AI Match แนะนำแบบเรียลไทม์'}
              </strong>
              <small>
                {mode === 'PICK_ONE'
                  ? 'เลือกผู้เล่นหลัก 1 คน แล้ว AI จะค้นหาอีก 3 คนที่คะแนนใกล้เคียงที่สุด พร้อมจัดผลรวมทีมให้สูสี'
                  : 'ยึดผู้เล่น ACTIVE ที่มาถึงก่อน แล้วค้นหาจากผู้เล่นทั้งก๊วน โดยให้คะแนนรายบุคคลใกล้กันก่อน แล้วจึงให้ผลรวมทีมสูสี (คู่ล็อกคะแนนต่างกันจะยึดผลรวมทีมก่อน)'}
              </small>
            </div>

            <button
              type="button"
              className="rmp-ai-auto-collapse"
              aria-expanded={aiAutoPanelOpen}
              onClick={() => setAiAutoPanelOpen(open => !open)}
            >
              {aiAutoPanelOpen ? '▲ ย่อ' : '▼ เปิด'}
            </button>

            <label className="rmp-ai-auto-switch">
              <input
                type="checkbox"
                checked={aiAutoEnabled}
                onChange={event => {
                  setAiAutoEnabled(event.target.checked);
                  setAiAutomationStatus('');
                  setError('');
                  aiProcessedSignatureRef.current = '';
                  pickOneAttemptedIdsRef.current.clear();
                }}
              />
              <span>
                {mode === 'PICK_ONE'
                  ? `Pick One Auto: ${aiAutoEnabled ? 'เปิด' : 'ปิด'}`
                  : 'สร้าง Queue อัตโนมัติ (ค่าเริ่มต้นปิด)'}
              </span>
            </label>
          </div>

          {mode === 'PICK_ONE' && (
            <div className="rmp-pick-auto-action" role="radiogroup" aria-label="วิธีทำงานของ Pick One Auto">
              <button
                type="button"
                className={pickOneAutoAction === 'PREVIEW' ? 'is-selected' : ''}
                onClick={() => setPickOneAutoAction('PREVIEW')}
              >
                👀 Preview ก่อน
              </button>
              <button
                type="button"
                className={pickOneAutoAction === 'QUEUE' ? 'is-selected' : ''}
                onClick={() => setPickOneAutoAction('QUEUE')}
              >
                ⚡ สร้าง Match Queue ทันที
              </button>
            </div>
          )}

          <div
            className="rmp-ai-live-status"
            role="status"
            aria-live="polite"
          >
            <span className={aiAutoLoading ? 'is-live' : ''} />
            {aiAutomationStatus ||
              'ระบบพร้อมตรวจผู้เล่น ACTIVE แบบเรียลไทม์'}
          </div>

          {aiActivityLog.length > 0 && (
            <div className="rmp-ai-activity" aria-label="รายละเอียดการทำงานของ AI">
              <strong>รายละเอียดการทำงานล่าสุด</strong>
              <ol>
                {aiActivityLog.map(item => (
                  <li key={item.id} className={`is-${item.type}`}>
                    <time>{item.time}</time>
                    <span>{item.message}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]) && (
            <div className="rmp-ai-anchor">
              <span>
                {mode === 'PICK_ONE' ? 'ผู้เล่นหลัก Pick One' : 'มาก่อน ลำดับ #1'}
              </span>
              <strong>
                {(mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]).name}
              </strong>
              <span>
                {Number((mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]).point || 0)} คะแนน
              </span>
              {mode === 'PICK_ONE' && (
                <span>
                  Type: {USER_MATCH_TYPE_LABELS[pickedPlayer.match_type] || 'ยังไม่กำหนด (ใช้เงื่อนไขเดิม)'}
                </span>
              )}
              {mode === 'AI_RANDOM' && (
                <span>
                  รอ {formatWaitTime(aiOrderedActivePlayers[0], now)}
                </span>
              )}
            </div>
          )}

     
        </div>
      )}

      <button
        type="button"
        className={`rmp-advanced-settings-toggle ${advancedSettingsOpen ? 'is-open' : ''
          }`}
        aria-expanded={advancedSettingsOpen}
        onClick={() => {
          setAdvancedSettingsOpen(current => !current);
          if (advancedSettingsOpen) setExpandedInfoPanel(null);
        }}
      >
        <span>⚙️ ตั้งค่าเพิ่มเติม</span>
        <small>
          {priority === 'FIFO' ? 'มาก่อน' : priority === 'LEAST_GAMES' ? 'เล่นน้อย' : 'รอนาน'}
          {' • '}ช่วงคะแนน {pointRange}
        </small>
        <b>{advancedSettingsOpen ? '▲ ซ่อน' : '▼ เปิด'}</b>
      </button>

      {advancedSettingsOpen && (
        <div className="rmp-advanced-settings-panel">
          {mode !== 'MANUAL_4' && mode !== 'AI_RANDOM' && (
            <div className="rmp-field">
              <label htmlFor="matchmaking-priority">
                {mode === 'PRIORITY_SELECT'
                  ? 'เรียงรายชื่อผู้เล่น'
                  : 'ลำดับความสำคัญ'}
              </label>
              <select
                id="matchmaking-priority"
                value={priority}
                onChange={event => {
                  setPriority(event.target.value);
                  setError('');
                }}
              >
                {mode !== 'PRIORITY_SELECT' && (
                  <option value="FIFO">มาก่อน ได้เล่นก่อน (FIFO)</option>
                )}
                <option value="WAIT_LONGEST">
                  รอนานที่สุด → มาก่อน
                </option>
                <option value="LEAST_GAMES">
                  เล่นน้อยที่สุด → มาก่อน
                </option>
              </select>
            </div>
          )}

          <div
            className="rmp-detail-toggle-bar"
            role="group"
            aria-label="เปิดดูรายละเอียดเงื่อนไขจัด Match"
          >
            <button
              type="button"
              className={`rmp-detail-icon-button ${expandedInfoPanel === 'mood' ? 'is-open' : ''
                }`}
              aria-expanded={expandedInfoPanel === 'mood'}
              aria-controls="rmp-mood-detail"
              title={`Mood: ${getPlayStyleLabel(playStyle)} • กดเพื่อดูหรือเปลี่ยน`}
              onClick={() => setExpandedInfoPanel(current => (
                current === 'mood' ? null : 'mood'
              ))}
            >
              <span className="rmp-detail-icon" aria-hidden="true">🎭</span>
              <small>Mood</small>
            </button>

            {mode !== 'MANUAL_4' && (
              <>
                <button
                  type="button"
                  className={`rmp-detail-icon-button ${expandedInfoPanel === 'rank' ? 'is-open' : ''
                    }`}
                  aria-expanded={expandedInfoPanel === 'rank'}
                  aria-controls="rmp-rank-detail"
                  title="กดดูขั้นการขยาย Rank และช่วงคะแนน"
                  onClick={() => setExpandedInfoPanel(current => (
                    current === 'rank' ? null : 'rank'
                  ))}
                >
                  <span className="rmp-detail-icon" aria-hidden="true">🎯</span>
                  <small>Rank</small>
                </button>

                <button
                  type="button"
                  className={`rmp-detail-icon-button ${expandedInfoPanel === 'repeat' ? 'is-open' : ''
                    }`}
                  aria-expanded={expandedInfoPanel === 'repeat'}
                  aria-controls="rmp-repeat-detail"
                  title="กดดูเงื่อนไขป้องกันคู่ซ้ำ"
                  onClick={() => setExpandedInfoPanel(current => (
                    current === 'repeat' ? null : 'repeat'
                  ))}
                >
                  <span className="rmp-detail-icon" aria-hidden="true">🔁</span>
                  <small>คู่ซ้ำ</small>
                </button>
              </>
            )}
          </div>

          {/* แสดงตัวเลือก Mood เมื่อกดไอคอน โดยค่าที่เลือกจะไม่หายตอนย่อ */}
          <div
            id="rmp-mood-detail"
            className="rmp-play-style-panel rmp-collapsible-detail"
            data-testid="daily-play-style-selector"
            hidden={expandedInfoPanel !== 'mood'}
          >
            <div className="rmp-option-heading">
              <strong>Mood ของวันนี้ที่ต้องการตีแบด</strong>
              <small>
                {mode === 'AI_RANDOM'
                  ? 'AI RANDOM ค้นหาจาก ACTIVE ทั้งหมด แต่จะจัดผู้เล่น Mood เดียวกันให้ครบ 4 คนก่อน'
                  : mode === 'MANUAL_4' || mode === 'PRIORITY_SELECT'
                    ? 'โหมดนี้อนุญาตให้เลือกผู้เล่นต่าง Mood และอนุญาตให้มือ S/P เล่นร่วมกับ BG ได้'
                    : (
                      <>
                        เมื่อเลือก “ทุก Mood” ระบบจะจัดผู้เล่น Mood เดียวกันก่อน
                        และไม่ให้มือ S/P อยู่ Match เดียวกับ BG
                      </>
                    )}
              </small>
            </div>

            <div className="rmp-play-style-options">
              {PLAY_STYLE_OPTIONS.map(option => {
                const styleActiveCount = option.value === 'ANY'
                  ? activeStyleCounts.totalActive
                  : activeStyleCounts.counts[option.value] || 0;

                return (
                  <button
                    type="button"
                    key={option.value}
                    className={playStyle === option.value ? 'selected' : ''}
                    aria-pressed={playStyle === option.value}
                    disabled={
                      mode === 'AI_RANDOM' && option.value !== 'ANY'
                    }
                    onClick={() => {
                      setPlayStyle(option.value);
                      resetInputs();
                      setLastResult(null);
                      setError('');
                    }}
                  >
                    <span>{option.icon}</span>
                    <span>
                      <strong>{option.label}</strong>
                      <small>{option.description}</small>
                      <em className="rmp-style-active-badge">
                        ACTIVE {styleActiveCount}
                      </em>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="rmp-style-eligible-count">
              {mode === 'AI_RANDOM'
                ? `AI ใช้ ACTIVE ทั้งหมด ${activePlayers.length} คน • จัด Mood เดียวกันก่อน`
                : mode === 'MANUAL_4' || mode === 'PRIORITY_SELECT'
                  ? `ข้อยกเว้น: เลือกต่าง Mood และเลือก S/P ร่วมกับ BG ได้`
                  : `ผู้เล่นตรง Mood: ACTIVE ${styleMatchingActiveCount} คน`}
              {playStyle !== 'ANY' && (
                <span>
                  {styleMatchingActiveCount < minimumStylePlayers
                    ? (
                      <>
                        {' '}• ต้องมีอย่างน้อย {minimumStylePlayers} คน
                        จึงจะสร้าง Match ได้
                      </>
                    )
                    : (
                      <>
                        {' '}• แสดงเฉพาะผู้เล่นที่เลือก Mood นี้
                      </>
                    )}
                </span>
              )}
              {playStyle === 'ANY' && (
                <span>
                  {' '}• แสดง ACTIVE ทั้งหมด {activePlayers.length} คน
                </span>
              )}
              {activeStyleCounts.missingStyle > 0 && (
                <span className="rmp-style-missing-count">
                  {' '}• ยังไม่เลือก Mood วันนี้{' '}
                  {activeStyleCounts.missingStyle} คน
                </span>
              )}
            </div>
          </div>

          {mode !== 'MANUAL_4' && (
            <>
              <div
                id="rmp-rank-detail"
                className="rmp-progressive-rule rmp-collapsible-detail"
                hidden={expandedInfoPanel !== 'rank'}
              >
                <div>
                  <strong>🎯 จับ Rank เดียวกันและคะแนนใกล้กันก่อน</strong>
                  <small>
                    หากยังไม่ครบ 4 คน ระบบจะค่อย ๆ ขยาย Rank แล้วไล่ช่วงคะแนนตามลำดับ
                    โดยไม่บังคับสร้าง Match ที่คะแนนทั้งกลุ่มห่างเกิน 300
                  </small>
                </div>
                <div className="rmp-progressive-steps" aria-label="ลำดับช่วงคะแนน">
                  {[20, 40, 80, 100, 150, 200, 250, 300].map(step => (
                    <span key={step}>≤ {step}</span>
                  ))}
                </div>
              </div>

              <div
                id="rmp-repeat-detail"
                className="rmp-repeat-rule rmp-collapsible-detail"
                hidden={expandedInfoPanel !== 'repeat'}
              >
                <strong>🔁 ป้องกันคู่ซ้ำ</strong>
                <span>
                  Pick One จะค้นหาในกลุ่ม Rank ที่แนะนำก่อน แล้วจึงขยับไปโซนใกล้ที่สุด
                  โดยคะแนนยังห่างได้สูงสุด 100–150 เท่านั้น หากเกินช่วงหรือข้ามจาก
                  BG/BGM ไป N- ขึ้นไป ต้องกด Approve ส่วน Locked Pair อยู่ทีมเดียวกันซ้ำได้ตลอด
                </span>
              </div>
            </>
          )}

          {(supportsMultipleMatches || usesPointRange) && (
            <div className="rmp-settings-grid">
              {usesPointRange && (
                <div className="rmp-option-panel">
                  <div className="rmp-option-heading">
                    <strong>
                      {usesIndividualPointRange
                        ? 'เกณฑ์คะแนนข้ามทีมและภายในทีม'
                        : 'ช่วงคะแนนจากผู้เล่นคนแรก'}
                    </strong>
                    <small>
                      {usesIndividualPointRange
                        ? 'ทั้งคู่แข่งตำแหน่งเดียวกันข้ามทีม และผู้เล่น 2 คนภายในแต่ละทีม ต้องมีคะแนนห่างไม่เกินค่านี้'
                        : 'คนแรกเลือกตาม Priority ด้านบน ส่วนอีก 3 คนต้องมีคะแนนต่างจากคนแรกไม่เกินค่านี้'}
                    </small>
                  </div>

                  <div className="rmp-range-options">
                    {POINT_RANGES.filter(range => mode !== 'PICK_ONE' || range <= 150).map(range => (
                      <button
                        type="button"
                        key={range}
                        className={pointRange === range ? 'selected' : ''}
                        aria-pressed={pointRange === range}
                        onClick={() => {
                          setPointRange(range);
                          setLastResult(null);
                          setError('');
                        }}
                      >
                        ± ไม่เกิน {range}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {supportsMultipleMatches && (
                <div className="rmp-option-panel">
                  <div className="rmp-option-heading">
                    <strong>จำนวน Match ที่ต้องการสร้าง</strong>
                    <small>
                      {mode === 'AI_RANDOM'
                        ? 'กำหนดจำนวน Queue ต่อหนึ่งรอบของ AI ระบบจะรอจนมี ACTIVE ครบตามจำนวน'
                        : 'ผู้เล่นจะไม่ซ้ำกันระหว่าง Match ที่สร้างพร้อมกัน'}
                    </small>
                  </div>

                  {mode === 'AI_RANDOM' && (
                    <div className="rmp-ai-count-presets">
                      {[1, 5, 10, 15].map(count => (
                        <button
                          type="button"
                          key={count}
                          className={matchCount === count ? 'selected' : ''}
                          onClick={() => {
                            setMatchCount(count);
                            setError('');
                            setLastResult(null);
                            setAiAutomationStatus('');
                            aiProcessedSignatureRef.current = '';
                          }}
                        >
                          {count} Match
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="rmp-match-count-control">
                    <button
                      type="button"
                      aria-label="ลดจำนวน Match"
                      disabled={matchCount <= 1}
                      onClick={() => setMatchCount(current => Math.max(1, current - 1))}
                    >
                      −
                    </button>
                    <select
                      aria-label="จำนวน Match"
                      value={matchCount}
                      onChange={event => setMatchCount(Number(event.target.value))}
                    >
                      {Array.from({ length: maxMatchCount }, (_, index) => index + 1)
                        .map(count => (
                          <option value={count} key={count}>
                            {count} Match
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      aria-label="เพิ่มจำนวน Match"
                      disabled={matchCount >= maxMatchCount}
                      onClick={() => setMatchCount(
                        current => Math.min(maxMatchCount, current + 1)
                      )}
                    >
                      ＋
                    </button>
                  </div>

                  <div className="rmp-batch-hint">
                    ใช้ผู้เล่น {requiredPlayerCount} คน • สร้างได้สูงสุด{' '}
                    {availableMatchCount} Match
                    {' '}จากผู้เล่น ACTIVE ปัจจุบัน
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rmp-sticky-status-tools">
        <div className="rmp-sticky-status-row">
      {canApprovePlaying && (
        <label className={`rmp-approve ${approvePlaying ? 'enabled' : ''}`}>
          <input
            type="checkbox"
            checked={approvePlaying}
            onChange={event => handleApproveChange(event.target.checked)}
          />
          <span>
            <strong>Approve ทุกสถานะ</strong>
            <small>
              {mode === 'AI_RANDOM'
                ? 'ให้ AI เลือกชุดที่ดีที่สุดจากผู้เล่น ACTIVE และ PLAY ได้ทันที โดยผู้เล่น PLAY จะรอใน Queue จนพร้อมลงสนาม'
                : 'อนุญาตผู้เล่นทุกสถานะ เช่น PLAY, QUEUE และ CHECKOUT เพื่อจัด Match ล่วงหน้า รวมถึง Locked Pair แต่ไม่รวม HOLD'}
            </small>
          </span>
        </label>
      )}

          <button
            type="button"
            className={`rmp-hold-toggle ${holdManagerOpen ? 'is-open' : ''}`}
            onClick={() => setHoldManagerOpen(open => !open)}
          >
            ⏸ HOLD {heldPlayers.length} คน {holdManagerOpen ? '▲' : '▼'}
          </button>
        </div>

      {holdManagerOpen && (
      <section className="rmp-hold-manager">
        <div className="rmp-hold-heading">
          <div>
            <strong>⏸ พักผู้เล่นชั่วคราว (HOLD)</strong>
            <small>ค้นหาชื่อแล้วเปลี่ยน ACTIVE เป็น HOLD หรือเรียกกลับเป็น ACTIVE</small>
          </div>
          <span>HOLD {heldPlayers.length} คน</span>
        </div>

        <div className="rmp-hold-search-wrap">
          <span aria-hidden="true">🔎</span>
          <input
            type="search"
            value={statusSearchKeyword}
            placeholder="ค้นหาชื่อผู้เล่นเพื่อเปลี่ยนสถานะ HOLD / ACTIVE"
            onChange={event => {
              setStatusSearchKeyword(event.target.value);
              setStatusUpdateMessage(null);
            }}
          />
          {statusSearchKeyword && (
            <button
              type="button"
              className="rmp-hold-clear"
              onClick={() => {
                setStatusSearchKeyword('');
                setStatusUpdateMessage(null);
              }}
              aria-label="ล้างคำค้นหา"
            >
              ×
            </button>
          )}
        </div>

        <div className="rmp-hold-results">
          {visibleStatusPlayers.length === 0 && (
            <div className="rmp-hold-empty">
              {statusSearchKeyword.trim()
                ? 'ไม่พบชื่อผู้เล่น'
                : 'ยังไม่มีผู้เล่นสถานะ HOLD'}
            </div>
          )}

          {visibleStatusPlayers.map(player => {
            const currentStatus = getStatus(player);
            const canSetHold = currentStatus === 'ACTIVE';
            const canSetActive = currentStatus === 'HOLD';
            const lockedPartner = getLockedPartner(player);

            return (
              <div className="rmp-hold-player" key={player.user_id}>
                <div className="rmp-hold-player-info">
                  <strong><AdminPlayerName player={player}/></strong>
                  <span>
                    {normalizePlayerRank(player.level_rank, player.point)} • {Number(player.point || 0)} คะแนน • {intensityText(player)}
                  </span>
                  {lockedPartner && (
                    <small>🔒 Locked Pair: {lockedPartner.name}</small>
                  )}
                </div>

                <span className={`rmp-hold-current status-${currentStatus.toLowerCase()}`}>
                  {currentStatus}
                </span>

                {canSetHold && (
                  <button
                    type="button"
                    className="rmp-set-hold"
                    disabled={statusUpdatingId === Number(player.user_id)}
                    onClick={() => updateAvailabilityStatus(player, 'HOLD')}
                  >
                    {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก...' : '⏸ HOLD'}
                  </button>
                )}

                {canSetActive && (
                  <button
                    type="button"
                    className="rmp-set-active"
                    disabled={statusUpdatingId === Number(player.user_id)}
                    onClick={() => updateAvailabilityStatus(player, 'ACTIVE')}
                  >
                    {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก...' : '▶ ACTIVE'}
                  </button>
                )}

                {!canSetHold && !canSetActive && (
                  <small className="rmp-hold-blocked">
                    ต้องออกจาก {currentStatus} ก่อน
                  </small>
                )}
              </div>
            );
          })}
        </div>

        {statusUpdateMessage && (
          <div className={`rmp-hold-message ${statusUpdateMessage.type}`} role="status">
            {statusUpdateMessage.text}
          </div>
        )}
      </section>
      )}
      </div>

      <style>{`
        .rmp-sticky-mode-bar {
          position: sticky;
          top: 8px;
          z-index: 45;
          padding: 7px;
          border: 1px solid #dbeafe;
          border-radius: 14px;
          background: rgba(248, 250, 252, .96);
          box-shadow: 0 7px 20px rgba(15, 23, 42, .1);
          backdrop-filter: blur(8px);
        }
        .rmp-sticky-mode-bar .rmp-mode-grid {
          display: grid;
          grid-template-columns: repeat(${MODES.length}, minmax(150px, 1fr));
          gap: 7px;
          overflow-x: auto;
        }
        .rmp-sticky-mode-bar .rmp-mode-card { min-width: 150px; }
        .rmp-sticky-status-tools {
          position: sticky;
          top: 92px;
          z-index: 44;
          padding: 7px;
          border: 1px solid #fde68a;
          border-radius: 14px;
          background: rgba(255, 251, 235, .97);
          box-shadow: 0 7px 20px rgba(120, 53, 15, .1);
          backdrop-filter: blur(8px);
        }
        .rmp-sticky-status-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: stretch;
          gap: 8px;
        }
        .rmp-sticky-status-row .rmp-approve { margin: 0; }
        .rmp-hold-toggle {
          min-width: 145px;
          padding: 9px 12px;
          border: 1px solid #fbbf24;
          border-radius: 11px;
          color: #92400e;
          background: #fff7d6;
          font-weight: 900;
          cursor: pointer;
        }
        .rmp-hold-toggle.is-open { color: #fff; background: #d97706; }
        .rmp-sticky-status-tools .rmp-hold-manager { margin-top: 8px; max-height: 52vh; overflow: auto; }
        @media (max-width: 760px) {
          .rmp-sticky-mode-bar { top: 6px; padding: 5px; }
          .rmp-sticky-mode-bar .rmp-mode-grid {
            grid-template-columns: repeat(${MODES.length}, minmax(126px, 1fr));
          }
          .rmp-sticky-mode-bar .rmp-mode-card {
            min-width: 126px;
            min-height: 62px;
            padding: 8px;
          }
          .rmp-sticky-mode-bar .rmp-mode-card small { font-size: 10px; }
          .rmp-sticky-status-tools { top: 82px; padding: 5px; }
          .rmp-sticky-status-row { grid-template-columns: minmax(0, 1fr) auto; }
          .rmp-sticky-status-row .rmp-approve { padding: 8px; }
          .rmp-sticky-status-row .rmp-approve small { display: none; }
          .rmp-hold-toggle { min-width: 112px; padding: 8px 7px; font-size: 11px; }
          .rmp-preview-backdrop {
            place-items: center !important;
            width: 100vw !important;
            height: 100dvh !important;
            padding: 6px !important;
            overflow: auto;
            z-index: 30000 !important;
          }
          .rmp-preview-modal {
            width: calc(100vw - 12px) !important;
            max-width: 960px !important;
            height: auto !important;
            max-height: calc(100dvh - 12px) !important;
            padding: 10px 10px max(18px, env(safe-area-inset-bottom)) !important;
            border: 3px solid #2563eb !important;
            border-radius: 16px !important;
            box-sizing: border-box;
            overflow-x: hidden !important;
            overflow-y: auto !important;
            box-shadow: 0 24px 70px rgba(15,23,42,.42), 0 0 0 4px rgba(147,197,253,.5) !important;
          }
          .rmp-preview-heading {
            position: sticky;
            top: -10px;
            z-index: 8;
            margin: -10px -10px 9px !important;
            padding: 10px;
            background: rgba(255,255,255,.97);
            box-shadow: 0 3px 10px rgba(15,23,42,.08);
          }
          .rmp-preview-list {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 8px !important;
            width: 100%;
            padding-bottom: 7px;
            overflow-x: visible;
          }
          .rmp-preview-match {
            width: 100%;
            min-width: 0;
            padding: 6px !important;
            border-radius: 10px !important;
          }
          .rmp-preview-match > div:first-child { margin-bottom: 4px !important; font-size: 10px; }
          .rmp-preview-match .rmp-result-teams {
            display: grid !important;
            grid-template-columns: 1fr !important;
            align-items: stretch;
            gap: 3px !important;
          }
          .rmp-preview-match .rmp-result-team { min-width: 0; padding: 4px; }
          .rmp-preview-match .rmp-result-player {
            grid-template-columns: 25px minmax(0,1fr) auto !important;
            gap: 3px !important;
            padding: 2px 1px;
            font-size: 8px;
            line-height: 1.15;
          }
          .rmp-preview-match .rmp-player-avatar,
          .rmp-preview-match .rmp-player-avatar.normal {
            width: 25px;
            min-width: 25px;
            height: 25px;
          }
          .rmp-preview-match .rmp-preview-edit-button {
            margin-top: 2px;
            padding: 3px 6px;
            font-size: 8px;
          }
          .rmp-preview-match .rmp-vs { align-self: center; padding: 1px 0; text-align: center; }
          .rmp-preview-match > div:last-child { margin-top: 4px !important; gap: 4px !important; }
          .rmp-preview-match > div:last-child button { padding: 5px 7px; font-size: 8px; }
        }
      `}</style>

      {mode !== 'PICK_ONE' && mode !== 'PRIORITY_SELECT' && renderPreviewControls()}

      {mode === 'PICK_ONE' && (
        <div className="rmp-compact-tools-row">
          <button type="button" className="is-open" onClick={() => setPreviewControlsOpen(true)}>
            <strong>ส่วน Preview Match</strong><small>▲ เปิดตลอด</small>
          </button>
          <button type="button" className="is-open" onClick={() => setPickAnchorOpen(true)}>
            <strong>ผู้เล่นหลักที่ต้องอยู่ใน Match</strong><small>{pickedPlayer?.name || 'ยังไม่เลือก'} • เปิดตลอด</small>
          </button>
          <button type="button" className="is-open" onClick={() => setNearbyPanelOpen(true)}>
            <strong>ผู้เล่น Rank ใกล้เคียง</strong><small>{pickOnePlayers.length} คนที่เลือก • เปิดตลอด</small>
          </button>
        </div>
      )}

      {mode === 'PICK_ONE' && previewControlsOpen && renderPreviewControls()}

      {mode === 'PICK_ONE' && (pickAnchorOpen || (pickedPlayer && nearbyPanelOpen)) && (
        <div className={`rmp-pick-one-layout${pickAnchorOpen ? '' : ' is-anchor-collapsed'}`}>
          {pickAnchorOpen && <div className="rmp-field rmp-pick-anchor">
            <button
              type="button"
              className="rmp-pick-anchor-toggle"
              aria-expanded={pickAnchorOpen}
              onClick={() => setPickAnchorOpen(true)}
            >
              <span>
                <strong>ผู้เล่นหลักที่ต้องอยู่ใน Match</strong>
                <small>
                  {pickedPlayer
                    ? `${pickedPlayer.name}${secondPickedPlayer ? ` + ${secondPickedPlayer.name}` : ''}`
                    : 'ยังไม่ได้เลือกผู้เล่นหลัก'}
                </small>
              </span>
              <b>▲ เปิดตลอด</b>
            </button>
            <label>ผู้เล่นหลักที่ต้องอยู่ใน Match</label>
            <PlayerSearchDropdown
              value={normalizeDropdownPlayer(pickedPlayer)}
              allPlayers={normalizeDropdownPlayers(pickOneAnchorPlayers)}
              usedIds={[]}
              sortMode="ZERO_GAMES_WAIT"
              onChange={player => {
                const playerStatus = getStatus(player);

                if (playerStatus === 'HOLD') {
                  setError(`${player.name} อยู่สถานะ HOLD และยังไม่พร้อมลงเล่น`);
                  return;
                }

                if (playerStatus !== 'ACTIVE' && !approvePlaying) {
                  setError(
                    `${player.name} อยู่สถานะ ${playerStatus} — กรุณาเปิด Approve ทุกสถานะก่อนเลือก`
                  );
                  return;
                }

                setPickedPlayer(player);
                setNearbyRankSelectionMode('AUTO');
                setSecondPickedPlayer(null);
                setPickOnePlayers([]);
                setNearbySearchKeyword('');
                setLastResult(null);
                setError('');
                setAiRecommendation(null);
                setAiAutomationStatus('');
                aiProcessedSignatureRef.current = '';
                pickOneAttemptedIdsRef.current.clear();
              }}
            />

            {pickedPlayer && !pickedLockedPartner && (
              <div style={{ marginTop: 10 }}>
                <label>ผู้เล่นหลักคนที่ 2 (ไม่บังคับ • อยู่ Team เดียวกัน)</label>
                <PlayerSearchDropdown
                  value={normalizeDropdownPlayer(secondPickedPlayer)}
                  allPlayers={normalizeDropdownPlayers(
                    normalizedPlayers.filter(player =>
                      getStatus(player) !== 'HOLD' &&
                      Number(player.user_id) !== Number(pickedPlayer.user_id) &&
                      Number(player.user_id) !== Number(pickedLockedPartner?.user_id) &&
                      !getLockedPartner(player)
                    )
                  )}
                  usedIds={[pickedPlayer.user_id]}
                  sortMode="ZERO_GAMES_WAIT"
                  onChange={player => {
                    setSecondPickedPlayer(player);
                    setNearbyRankSelectionMode('AUTO');
                    setPickOnePlayers(current => current.slice(0, 2));
                    setError('');
                    setPreviewMatches([]);
                  }}
                />
                {secondPickedPlayer && (
                  <button
                    type="button"
                    onClick={() => {
                      setSecondPickedPlayer(null);
                      setNearbyRankSelectionMode('AUTO');
                    }}
                    style={{ marginTop: 6 }}
                  >
                    ใช้ผู้เล่นหลักเพียง 1 คน
                  </button>
                )}
              </div>
            )}

            {pickedPlayer && (
              <div className="rmp-pick-anchor-summary">

                <div className="rmp-pick-anchor-player">

                  <PlayerAvatar
                    player={pickedPlayer}
                    size="large"
                  />

                  <div className="rmp-pick-anchor-info">

                    <strong>
                      {pickedPlayer.name}
                    </strong>

                    <div className="rmp-pick-anchor-meta">

                      <span data-rank={normalizePlayerRank(pickedPlayer.level_rank, pickedPlayer.point)}>
                        Rank {normalizePlayerRank(
                          pickedPlayer.level_rank,
                          pickedPlayer.point
                        ) || '-'}
                      </span>

                      <span>
                        {Number(
                          pickedPlayer.point || 0
                        )} คะแนน
                      </span>

                      <span className="rmp-pick-position">
                        #1 • Team 1
                      </span>

                    </div>

                  </div>

                </div>


                {pickedLockedPartner && (
                  <span className="locked">
                    #2 • Team 1 • 🔒 คู่ล็อก{' '}
                    {pickedLockedPartner.name}
                  </span>
                )}

              </div>
            )}
          </div>}

          {pickedPlayer && nearbyPanelOpen && (
            <div className="rmp-pick-nearby-panel">
              <button
                type="button"
                className="rmp-pick-nearby-toggle"
                aria-expanded={nearbyPanelOpen}
                onClick={() => setNearbyPanelOpen(true)}
              >
                <span><strong>ผู้เล่น Rank ใกล้เคียง</strong><small>ค้นหาและเลือกผู้เล่นเพิ่มเติม</small></span>
                <b>▲ เปิดตลอด</b>
              </button>
              <div className="rmp-pick-nearby-heading">
                <div>
                  <strong>ผู้เล่น Rank ใกล้เคียง</strong>
                  <small>
                    แสดงทุกสถานะ • เรียง Rank ใกล้ผู้เล่นหลักก่อน
                    {' '}→ แยกระดับความหนัก → สถานะ → จำนวนเกม
                  </small>
                </div>
                <span>
                  เลือกเพิ่ม {pickOnePlayers.length}/
                  {Math.max(
                    0,
                    4 - 1 -
                    (secondPickedPlayer ? 1 : 0) -
                    (pickedLockedPartner ? 1 : 0)
                  )}
                </span>
              </div>

              <div className="rmp-pick-nearby-controls">
                <div className="rmp-pick-nearby-search">
                  <span aria-hidden="true">🔎</span>
                  <input
                    type="search"
                    value={nearbySearchKeyword}
                    placeholder="ค้นหาชื่อผู้เล่นทุก Rank"
                    onChange={event => setNearbySearchKeyword(event.target.value)}
                  />
                  {nearbySearchKeyword && (
                    <button
                      type="button"
                      onClick={() => setNearbySearchKeyword('')}
                      aria-label="ล้างคำค้นหาผู้เล่น Rank ใกล้เคียง"
                    >
                      ×
                    </button>
                  )}
                </div>
                <label className="rmp-pick-nearby-sort rmp-pick-nearby-status-filter">
                  <span>สถานะ</span>
                  <select
                    value={nearbyStatusFilter}
                    onChange={event => setNearbyStatusFilter(event.target.value)}
                    aria-label="กรองผู้เล่นตามสถานะ"
                  >
                    <option value="ALL">ทั้งหมด</option>
                    <option value="ACTIVE">ACTIVE</option>
                  </select>
                </label>
                <label className="rmp-pick-nearby-sort rmp-pick-nearby-intensity-filter">
                  <span>ความหนัก</span>
                  <select
                    value={nearbyIntensityFilter}
                    onChange={event => setNearbyIntensityFilter(event.target.value)}
                    aria-label="กรองผู้เล่นตามระดับความหนัก"
                  >
                    <option value="ALL">ทั้งหมด</option>
                    <option value="ANY">⚪ ได้ทุกระดับ</option>
                    <option value="LIGHT">🟢 เบา</option>
                    <option value="MEDIUM">🟡 กลาง</option>
                    <option value="HEAVY">🔴 หนัก</option>
                  </select>
                </label>
                <label className="rmp-pick-nearby-sort">
                  <span>เกมในกลุ่ม</span>
                  <select
                    value={nearbyGameSort}
                    onChange={event => setNearbyGameSort(event.target.value)}
                    aria-label="เรียงผู้เล่นตามจำนวนเกม"
                  >
                    <option value="ASC">น้อย → มาก</option>
                    <option value="DESC">มาก → น้อย</option>
                  </select>
                </label>
              </div>

              <div className="rmp-nearby-rank-section">
                <div className="rmp-nearby-rank-heading">
                  <div className="rmp-nearby-rank-copy">
                    <strong>เลือก Rank ผู้เล่นใกล้เคียง</strong>
                    <small className={nearbyRankSelectionMode === 'AUTO' ? 'is-auto' : 'is-manual'}>
                      {nearbyRankSelectionMode === 'AUTO'
                        ? `อัตโนมัติจาก Rank ${normalizePlayerRank(pickedPlayer.level_rank, pickedPlayer.point)} และจำนวน ACTIVE`
                        : 'Admin กำลังเลือก Rank เอง'}
                    </small>
                  </div>
                  <div className="rmp-nearby-rank-actions">
                    <button
                      type="button"
                      className={`rmp-rank-auto-button${nearbyRankSelectionMode === 'AUTO' ? ' is-active' : ''}`}
                      onClick={() => setNearbyRankSelectionMode('AUTO')}
                    >
                      <span aria-hidden="true">⚡</span> อัตโนมัติ
                    </button>
                    <small className="rmp-rank-count">{selectedNearbyRanks.length}/4</small>
                  </div>
                </div>
                <div className="rmp-rank-filter-grid">
                  {RANK_ORDER.map(rank => {
                    const selected = selectedNearbyRanks.includes(rank);
                    const playersInRank = normalizedPlayers.filter(player =>
                      normalizePlayerRank(player.level_rank, player.point) === rank
                    );
                    const activeCount = playersInRank.filter(
                      player => getStatus(player) === 'ACTIVE'
                    ).length;
                    const playCount = playersInRank.filter(
                      player => getStatus(player) === 'PLAY'
                    ).length;
                    return (
                      <button
                        type="button"
                        key={rank}
                        data-rank={rank}
                        className={`rmp-rank-filter-button${selected ? ' is-selected' : ''}`}
                        aria-pressed={selected}
                        onClick={() => toggleNearbyRank(rank)}
                      >
                        {selected ? '✓ ' : ''}{rank} •{' '}
                        <span style={{ color: selected ? '#dbeafe' : '#2563eb', fontWeight: 800 }}>
                          A {activeCount}
                        </span>
                        {' '}•{' '}
                        <span style={{ color: selected ? '#fee2e2' : '#dc2626', fontWeight: 800 }}>
                          P {playCount}
                        </span>
                      </button>
                    );
                  })}
                  {selectedNearbyRanks.length > 0 && (
                    <button type="button" className="rmp-rank-all-button" onClick={() => { setNearbyRankSelectionMode('MANUAL'); setSelectedNearbyRanks([]); }}>
                      <span aria-hidden="true">◎</span> ใช้ทุก Rank
                    </button>
                  )}
                </div>
                <small className="rmp-nearby-rank-help">
                  ระบบยึด Rank ผู้เล่นหลัก แล้วเลือก Rank ใกล้ที่สุดที่มี ACTIVE ต่อจนครบ 4 Rank • Admin กด Rank เพื่อเลือกเองได้
                </small>
              </div>

              <div className="rmp-pick-nearby-scroll">
                {pickOneNearbyPlayers.length === 0 && (
                  <div className="rmp-pick-nearby-empty">
                    {nearbySearchKeyword.trim()
                      ? 'ไม่พบชื่อผู้เล่น'
                      : 'ไม่พบผู้เล่น'}
                  </div>
                )}

                {pickOneNearbyPlayers.map(player => {

                  const selected =
                    pickOnePlayers.some(
                      item =>
                        Number(item.user_id) ===
                        Number(player.user_id)
                    );

                  const rankDistance =
                    getRankDistance(
                      pickedPlayer,
                      player
                    );

                  const pointGap =
                    Math.abs(
                      Number(player.point || 0) -
                      Number(pickedPlayer.point || 0)
                    );

                  const lockedPartner =
                    getLockedPartner(player);

                  const teammateHistory = getRelationHistory(
                    pairMap,
                    player,
                    playerNameById
                  );
                  const opponentHistory = getRelationHistory(
                    opponentMap,
                    player,
                    playerNameById
                  );

                  const queueMatchNo =
                    getQueueMatchNo(player);

                  const queueMatchId =
                    getQueueMatchId(player);

                  const holdBlocked =
                    getStatus(player) === 'HOLD' ||
                    getStatus(lockedPartner) === 'HOLD';
                  const selectedIndex = pickOneOrderedPlayers.findIndex(
                    item => Number(item.user_id) === Number(player.user_id)
                  );

                  return (
                    <button
                      type="button"
                      key={player.user_id}
                      className={`rmp-pick-nearby-card intensity-${intensityClass(player)} ${selected ? 'selected' : ''}`}
                      aria-pressed={selected}
                      disabled={holdBlocked}
                      onClick={() => togglePickOneNearbyPlayer(player)}
                    >
                      <div className="rmp-pick-nearby-player">

                        <PlayerAvatar
                          player={player}
                          size="normal"
                        />

                        <span className="rmp-pick-nearby-name">
                          <AdminPlayerName player={player}/>
                        </span>

                      </div>
                      <span className="rmp-pick-nearby-meta">

                        <b data-rank={normalizePlayerRank(player.level_rank, player.point)}>
                          {normalizePlayerRank(
                            player.level_rank,
                            player.point
                          ) || '-'}
                        </b>

                        <em className="rmp-pick-games">
                          🎮 {getPlayerGames(player)} เกม
                        </em>

                        <em className={`rmp-intensity-badge intensity-${intensityClass(player)}`}>
                          {intensityText(player)}
                        </em>

                        <i
                          className={`rmp-pick-status status-${getStatus(player).toLowerCase()}`}
                        >
                          {getStatus(player) || '-'}
                        </i>

                      </span>

                      {(queueMatchNo || queueMatchId) && (
                        <span className="rmp-queue-match-badge">
                          ⏳ Match Queue #{queueMatchNo || queueMatchId}
                        </span>
                      )}
                      <small>
                        {rankDistance === 0
                          ? 'Rank เดียวกัน'
                          : `Rank ห่าง ${rankDistance} ระดับ`} • คะแนนห่าง {pointGap}
                      </small>
                      <span className="rmp-pick-history">
                        <b className={`rmp-pick-teammate-count ${teammateHistory.length > 0 ? 'has-history' : ''}`} title={`เคยอยู่ทีมเดียวกับ: ${formatRelationHistory(teammateHistory)}`}>
                          <span aria-hidden="true">👥</span>
                          <span className="rmp-pick-relation-list">{formatRelationHistory(teammateHistory)}</span>
                        </b>
                        <b className="rmp-pick-opponent-count" title={`เคยอยู่ฝั่งตรงข้ามกับ: ${formatRelationHistory(opponentHistory)}`}>
                          <span aria-hidden="true">⚔️</span>
                          <span className="rmp-pick-relation-list">{formatRelationHistory(opponentHistory)}</span>
                        </b>
                      </span>
                      {lockedPartner && (
                        <small className="locked">🔒 Locked Pair: {lockedPartner.name}</small>
                      )}
                      <span className="rmp-pick-nearby-action">
                        {holdBlocked
                          ? '⏸ ผู้เล่นหรือคู่ล็อกอยู่ HOLD'
                          : selected
                            ? `✓ #${selectedIndex + 1} • Team ${selectedIndex < 2 ? 1 : 2}`
                            : '+ เลือก'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="rmp-pick-nearby-footer">
                <span>
                  แสดงทั้งหมด {pickOneNearbyCandidates.length} คน
                </span>
              </div>
              <div className="rmp-pick-team-rule">
                <strong>ลำดับ 1–2 = Team 1</strong>
                <span>ลำดับ 3–4 = Team 2</span>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'PRIORITY_SELECT' && (
        <div className="rmp-priority-select">
          <div className="rmp-priority-list-heading">
            <div>
              <strong>
                {priority === 'LEAST_GAMES'
                  ? '🎮 ผู้เล่นที่เล่นน้อยที่สุด'
                  : '⏱ ผู้เล่นที่รอนานที่สุด'}
              </strong>
              <small>
                ระบบเรียงตามอันดับคิว: สองอันดับแรกเป็น Team 1
                และสองอันดับถัดไปเป็น Team 2 ไม่อิงลำดับการกด
              </small>
            </div>
            <span className={selectedManualPlayers.length === 4 ? 'complete' : ''}>
              เลือกแล้ว {selectedManualPlayers.length}/4
            </span>
          </div>

          <input
            type="search"
            className="rmp-priority-search"
            value={priorityKeyword}
            placeholder="ค้นหาชื่อ คะแนน หรือระดับมือ"
            onChange={event => setPriorityKeyword(event.target.value)}
          />

          <div className="rmp-priority-player-list">
            {priorityPlayers.length === 0 && (
              <div className="rmp-priority-empty">ไม่พบผู้เล่น ACTIVE</div>
            )}

            {priorityPlayers.map((player, index) => {
              const selectedIndex = orderedPrioritySelections.findIndex(
                item => Number(item.user_id) === Number(player.user_id)
              );
              const selected = selectedIndex >= 0;
              const selectedTeam = selectedIndex < 2 ? 1 : 2;

              return (
                <button
                  type="button"
                  key={player.user_id}
                  className={[
                    'rmp-priority-player',
                    selected ? 'selected' : '',
                    selected ? `team-${selectedTeam}` : ''
                  ].filter(Boolean).join(' ')}
                  aria-pressed={selected}
                  onClick={() => togglePriorityPlayer(player)}
                >
                  <span className="rmp-priority-order">{index + 1}</span>
                  <span className="rmp-priority-name">
                    <strong><AdminPlayerName player={player}/></strong>
                    <small>
                      {player.level_rank || '-'} • {Number(player.point || 0)} คะแนน • {intensityText(player)}
                      {' • '}{getPlayStyleLabel(player.daily_play_style)}
                    </small>
                  </span>
                  <span className="rmp-priority-stat">
                    <small>เวลารอ</small>
                    <strong>{formatWaitTime(player, now)}</strong>
                  </span>
                  <span className="rmp-priority-stat">
                    <small>จำนวนเกม</small>
                    <strong>{getPlayerGames(player)} เกม</strong>
                  </span>
                  <span className="rmp-priority-check">
                    {selected
                      ? `✓ T${selectedTeam} • คนที่ ${selectedIndex + 1}`
                      : 'เลือก'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mode === 'MANUAL_4' && (
        <div className="rmp-field">
          <label>เลือกผู้เล่นแยกตามทีม</label>
          <div className="rmp-manual-teams">
            <div className="rmp-manual-team rmp-manual-team-one">
              <div className="rmp-manual-team-heading">
                <strong>Team 1</strong>
                <small>คนที่ 1 และคนที่ 2 อยู่ทีมเดียวกัน</small>
              </div>

              {[0, 1].map(index => {
                const player = manualPlayers[index];

                return (
                  <PlayerSearchDropdown
                    key={index}
                    value={normalizeDropdownPlayer(player)}
                    allPlayers={normalizeDropdownPlayers(orderedSelectablePlayers)}
                    usedIds={manualPlayers
                      .map(item => item?.user_id)
                      .filter(id => (
                        id &&
                        Number(id) !== Number(player?.user_id)
                      ))}
                    onChange={newPlayer => {
                      setManualPlayers(currentPlayers => {
                        const nextPlayers = [...currentPlayers];
                        nextPlayers[index] = newPlayer;
                        return nextPlayers;
                      });
                    }}
                  />
                );
              })}
            </div>

            <span className="rmp-manual-vs">VS</span>

            <div className="rmp-manual-team rmp-manual-team-two">
              <div className="rmp-manual-team-heading">
                <strong>Team 2</strong>
                <small>ผู้เล่นฝั่งตรงข้าม</small>
              </div>

              {[2, 3].map(index => {
                const player = manualPlayers[index];

                return (
                  <PlayerSearchDropdown
                    key={index}
                    value={normalizeDropdownPlayer(player)}
                    allPlayers={normalizeDropdownPlayers(orderedSelectablePlayers)}
                    usedIds={manualPlayers
                      .map(item => item?.user_id)
                      .filter(id => (
                        id &&
                        Number(id) !== Number(player?.user_id)
                      ))}
                    onChange={newPlayer => {
                      setManualPlayers(currentPlayers => {
                        const nextPlayers = [...currentPlayers];
                        nextPlayers[index] = newPlayer;
                        return nextPlayers;
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
          {selectedManualPlayers.length === 4 && (
            <div className={`rmp-manual-history ${manualRepeatAlerts.length ? 'has-alert' : ''}`}>
              <strong>ประวัติผู้เล่นทั้ง 4 คน</strong>
              {manualRelations.filter(item => item.count > 0).length === 0 ? (
                <span>ยังไม่เคยคู่กันหรือตรงข้ามกัน</span>
              ) : manualRelations.filter(item => item.count > 0).map((item, index) => (
                <span className={item.count >= 2 && !item.protectedPair ? 'is-alert' : ''} key={`${item.type}-${item.names}-${index}`}>
                  {item.type === 'TEAMMATE' ? '👥 คู่กัน' : '⚔️ ตรงข้าม'}: {item.names} · เคยพบ {item.count} รอบ{item.protectedPair ? ' · 🔒 ยกเว้นการแจ้งเตือน' : item.count >= 2 ? ` → Match นี้จะเป็นรอบที่ ${item.count + 1}` : ''}
                </span>
              ))}
              {manualApprovalAlerts.length > 0 && (
                <label className="rmp-manual-history-approve">
                  <input type="checkbox" checked={manualHistoryApproved} onChange={event => setManualHistoryApproved(event.target.checked)} />
                  <span><b>Approve การพบกันตั้งแต่รอบที่ 3</b><small>ตรวจชื่อและจำนวนรอบด้านบนแล้ว และอนุญาตให้สร้าง Match นี้</small></span>
                </label>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rmp-error" role="alert">
          ❌ {error}
        </div>
      )}

      {mode === 'PICK_ONE' && (
        <button
          type="button"
          className="rmp-generate-button"
          disabled={loading || pickOneOrderedPlayers.length !== 4}
          onClick={createQueueFromSelectedPlayers}
          style={{ marginBottom: 12 }}
        >
          {loading
            ? 'กำลังสร้าง Queue...'
            : `➕ สร้าง Queue จากผู้เล่นที่เลือก (${pickOneOrderedPlayers.length}/4)`}
        </button>
      )}

      {mode === 'MANUAL_4' && (
        <button
          type="button"
          className="rmp-generate-button"
          disabled={
            loading ||
            previewLoading ||
            aiAutoLoading ||
            selectedManualPlayers.length !== 4 ||
            (manualApprovalAlerts.length > 0 && !manualHistoryApproved)
          }
          onClick={generateQueue}
          style={{ marginBottom: 12 }}
        >
          {loading
            ? 'กำลังสร้าง Match Queue...'
            : manualApprovalAlerts.length > 0 && !manualHistoryApproved
              ? '⚠️ กรุณาติ๊กยืนยันประวัติซ้ำ'
              : `➕ สร้าง Match Queue (${selectedManualPlayers.length}/4)`}
        </button>
      )}

      {perfectMatchOpen && createPortal((
        <div
          className="rmp-perfect-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setPerfectMatchOpen(false);
          }}
        >
          <section className="rmp-perfect-modal" role="dialog" aria-modal="true" aria-labelledby="perfect-match-title">
            <header className="rmp-perfect-header">
              <div>
                <strong id="perfect-match-title">💯 จัดแมทแบบ 100%</strong>
                <small>เลือกความหนักตรงกัน หรือผ่อนเฉพาะระดับที่อยู่ติดกัน โดยยังคงกฎคู่ซ้ำ</small>
              </div>
              <button type="button" aria-label="ปิด" onClick={() => setPerfectMatchOpen(false)}>×</button>
            </header>

            <div className="rmp-perfect-status-rule">
              ใช้เฉพาะสถานะ <b>ACTIVE / PLAY / QUEUE</b>
              <span>ไม่รวม HOLD และ CHECKOUT</span>
            </div>

            <div className="rmp-perfect-settings">
              <label htmlFor="perfect-intensity">รูปแบบเวทมือ / ความหนัก</label>
              <select
                id="perfect-intensity"
                value={perfectMatchIntensity}
                disabled={perfectMatchLoading}
                onChange={event => {
                  setPerfectMatchIntensity(event.target.value);
                  setPerfectMatchResults([]);
                  setPerfectMatchMessage(null);
                }}
              >
                <option value="HEAVY">🔴 หนัก เจอ หนัก</option>
                <option value="MEDIUM">🟡 กลาง เจอ กลาง</option>
                <option value="LIGHT">🟢 เบา เจอ เบา</option>
                <option value="HEAVY_MEDIUM">🔴🟡 หนัก–กลาง (ผ่อนกฎ)</option>
                <option value="MEDIUM_LIGHT">🟡🟢 กลาง–เบา (ผ่อนกฎ)</option>
              </select>
              <label htmlFor="perfect-gender">รูปแบบเพศสำหรับจัดคู่</label>
              <select
                id="perfect-gender"
                className={`rmp-gender-select gender-${perfectMatchGender.toLowerCase()}`}
                value={perfectMatchGender}
                disabled={perfectMatchLoading}
                onChange={event => {
                  setPerfectMatchGender(event.target.value);
                  setPerfectMatchResults([]);
                  setPerfectMatchMessage(null);
                }}
              >
                <option value="ANY">ทุกเพศ</option>
                <option value="FEMALE">♀ หญิง–หญิง</option>
                <option value="MALE">♂ ชาย–ชาย</option>
                <option value="BEAUTIFUL">🌈 สวยที่สุด–สวยที่สุด</option>
              </select>
              <button type="button" disabled={perfectMatchLoading} onClick={loadPerfectMatches}>
                {perfectMatchLoading
                  ? 'กำลังตรวจสอบทุกเงื่อนไข…'
                  : PERFECT_INTENSITY_RULES[perfectMatchIntensity]?.relaxed
                    ? 'ค้นหา Match แบบผ่อนกฎ'
                    : 'ค้นหา Match ที่ตรง 100%'}
              </button>
            </div>

            {perfectMatchMessage && (
              <div className={`rmp-perfect-message ${perfectMatchMessage.type}`}>
                {perfectMatchMessage.text}
              </div>
            )}

            <div className="rmp-perfect-results">
              {perfectMatchResults.map((match, index) => (
                <article key={getPreviewMatchKey(match)} className="rmp-perfect-card">
                  <div className="rmp-perfect-card-title">
                    <b>
                      ตัวเลือก {index + 1}
                      {match.repeatRuleRelaxed && <i className="rmp-perfect-relaxed-badge">ผ่อนกฎซ้ำ · ต้อง Approve</i>}
                    </b>
                    <span>
                      {[...new Set([...(match.team1 || []), ...(match.team2 || [])]
                        .map(player => intensityText(player)))].join(' / ')}
                      {' • '}Team gap {match.analysis?.teamGap ?? '-'}
                    </span>
                  </div>
                  {match.repeatRuleRelaxed && match.repeatRuleDetails?.length > 0 && (
                    <div className="rmp-perfect-repeat-details">
                      {match.repeatRuleDetails.map((detail, detailIndex) => (
                        <span key={`${detail}-${detailIndex}`}>⚠️ {detail}</span>
                      ))}
                    </div>
                  )}
                  <div className="rmp-perfect-teams">
                    <div><b>Team 1</b>{(match.team1 || []).map((player, playerIndex) => {
                      const slotKey = `${index}-team1-${playerIndex}`;
                      const history = getPerfectPlayerHistory(player);
                      const usedIds = new Set([...match.team1, ...match.team2].map(item => Number(item.user_id)));
                      return <span key={player.user_id} className="rmp-perfect-player-detail">
                        <strong><AdminPlayerName player={player} /> · {genderText(player)} · {player.level_rank || '-'} · {getStatus(player)}</strong>
                        <small>👥 เคยคู่: {history.teammates.length ? history.teammates.map(item => `${item.name} (${item.teammateCount})`).join(', ') : '-'}</small>
                        <small>⚔️ เคยตรงข้าม: {history.opponents.length ? history.opponents.map(item => `${item.name} (${item.opponentCount})`).join(', ') : '-'}</small>
                        <button type="button" className="rmp-perfect-edit" onClick={() => { setPerfectEditSearch(''); setPerfectEditingSlot(current => current === slotKey ? null : slotKey); }}>{perfectEditingSlot === slotKey ? '✕ ปิด' : '✏️ Edit'}</button>
                        {perfectEditingSlot === slotKey && <div className="rmp-perfect-edit-picker"><input autoFocus type="search" value={perfectEditSearch} placeholder="ค้นหาผู้เล่น" onChange={event => setPerfectEditSearch(event.target.value)}/><div>{normalizedPlayers.filter(candidate => PERFECT_MATCH_STATUS.has(getStatus(candidate))).filter(candidate => PERFECT_INTENSITY_RULES[perfectMatchIntensity].values.includes(String(candidate.play_intensity || '').toUpperCase())).filter(candidate => perfectMatchGender === 'ANY' || String(candidate.gender || '').toUpperCase() === perfectMatchGender).filter(candidate => Number(candidate.user_id) === Number(player.user_id) || !usedIds.has(Number(candidate.user_id))).filter(candidate => matchesSearch(candidate.name, perfectEditSearch)).map(candidate => <button type="button" key={candidate.user_id} onClick={() => editPerfectMatchPlayer(index, 'team1', playerIndex, candidate)}><b>{candidate.name}</b><small>{candidate.level_rank || '-'} • {intensityText(candidate)} • {Number(candidate.point || 0)}</small></button>)}</div></div>}
                      </span>;
                    })}</div>
                    <em>VS</em>
                    <div><b>Team 2</b>{(match.team2 || []).map((player, playerIndex) => {
                      const slotKey = `${index}-team2-${playerIndex}`;
                      const history = getPerfectPlayerHistory(player);
                      const usedIds = new Set([...match.team1, ...match.team2].map(item => Number(item.user_id)));
                      return <span key={player.user_id} className="rmp-perfect-player-detail">
                        <strong><AdminPlayerName player={player} /> · {genderText(player)} · {player.level_rank || '-'} · {getStatus(player)}</strong>
                        <small>👥 เคยคู่: {history.teammates.length ? history.teammates.map(item => `${item.name} (${item.teammateCount})`).join(', ') : '-'}</small>
                        <small>⚔️ เคยตรงข้าม: {history.opponents.length ? history.opponents.map(item => `${item.name} (${item.opponentCount})`).join(', ') : '-'}</small>
                        <button type="button" className="rmp-perfect-edit" onClick={() => { setPerfectEditSearch(''); setPerfectEditingSlot(current => current === slotKey ? null : slotKey); }}>{perfectEditingSlot === slotKey ? '✕ ปิด' : '✏️ Edit'}</button>
                        {perfectEditingSlot === slotKey && <div className="rmp-perfect-edit-picker"><input autoFocus type="search" value={perfectEditSearch} placeholder="ค้นหาผู้เล่น" onChange={event => setPerfectEditSearch(event.target.value)}/><div>{normalizedPlayers.filter(candidate => PERFECT_MATCH_STATUS.has(getStatus(candidate))).filter(candidate => PERFECT_INTENSITY_RULES[perfectMatchIntensity].values.includes(String(candidate.play_intensity || '').toUpperCase())).filter(candidate => perfectMatchGender === 'ANY' || String(candidate.gender || '').toUpperCase() === perfectMatchGender).filter(candidate => Number(candidate.user_id) === Number(player.user_id) || !usedIds.has(Number(candidate.user_id))).filter(candidate => matchesSearch(candidate.name, perfectEditSearch)).map(candidate => <button type="button" key={candidate.user_id} onClick={() => editPerfectMatchPlayer(index, 'team2', playerIndex, candidate)}><b>{candidate.name}</b><small>{candidate.level_rank || '-'} • {intensityText(candidate)} • {Number(candidate.point || 0)}</small></button>)}</div></div>}
                      </span>;
                    })}</div>
                  </div>
                  <button
                    type="button"
                    disabled={perfectMatchCreating !== null}
                    onClick={() => createPerfectMatchQueue(match, index)}
                  >
                    {perfectMatchCreating === index ? 'กำลังสร้าง Queue…' : 'เลือก Match นี้เพื่อสร้าง Queue'}
                  </button>
                </article>
              ))}
            </div>
          </section>
        </div>
      ), document.body)}

      {previewOpen && createPortal((
        <div
          className="rmp-preview-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setPreviewOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            width: '100vw',
            height: '100dvh',
            padding: 12,
            background: 'rgba(15, 23, 42, 0.7)'
          }}
        >
          <div
            className="rmp-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rmp-preview-title"
            style={{
              width: 'min(960px, calc(100vw - 24px))',
              maxWidth: '960px',
              height: 'auto',
              maxHeight: '90dvh',
              overflowX: 'hidden',
              overflowY: 'auto',
              boxSizing: 'border-box',
              padding: 20,
              border: '3px solid #2563eb',
              borderRadius: 18,
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.42), 0 0 0 4px rgba(147, 197, 253, 0.5)',
              background: '#fff',
              color: '#172033'
            }}
          >
            <div className="rmp-preview-heading" style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              alignItems: 'flex-start',
              marginBottom: 16
            }}>
              <div>
                <h3 id="rmp-preview-title" style={{ margin: 0 }}>
                  {previewType === 'FIXED_PLAYER'
                    ? `Preview โดยยึด ${pickedPlayer?.name || 'ผู้เล่นหลัก'}`
                    : 'Preview Match ที่พร้อมลง'}
                </h3>
                <small>
                  {previewType === 'FIXED_PLAYER'
                    ? 'Match แนะนำ 2 Match + คะแนนผู้เล่นห่างไม่เกิน 100 อีก 2 Match • ผลรวมทีมต่างไม่เกิน 50'
                    : 'ผู้เล่น ACTIVE และ PLAY • เลือก Rank เดียวกันก่อน แล้วขยายเป็น Rank ใกล้กันเมื่อคนไม่พอ'}
                </small>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  disabled={previewLoading || previewCreatingIndex !== null}
                  onClick={refreshCurrentPreview}
                >
                  🔄 Refresh Preview
                </button>
                <button type="button" onClick={() => setPreviewOpen(false)}>
                  ✕ ปิด
                </button>
              </div>
            </div>

            {renderPreviewRankSelector()}

            {previewAlert && (
              <div
                role="alert"
                style={{
                  marginBottom: 14,
                  padding: '10px 12px',
                  borderRadius: 10,
                  color: previewAlert.type === 'error' ? '#991b1b' : '#166534',
                  background: previewAlert.type === 'error' ? '#fee2e2' : '#dcfce7',
                  border: `1px solid ${previewAlert.type === 'error' ? '#fecaca' : '#bbf7d0'}`
                }}
              >
                {previewAlert.type === 'error' ? '❌' : '✅'} {previewAlert.text}
              </div>
            )}

            {!previewLoading && previewMatches.length > 0 && (
              <div className="rmp-preview-batch-actions">
                <button type="button" onClick={selectAllCompatiblePreviewMatches}>
                  ✓ เลือกทั้งหมดที่ผู้เล่นไม่ซ้ำ
                </button>
                <button type="button" onClick={() => setSelectedPreviewKeys([])}>
                  ล้างการเลือก
                </button>
                <button
                  type="button"
                  className="rmp-generate-button"
                  disabled={previewCreatingIndex !== null || selectedPreviewKeys.length === 0}
                  onClick={createSelectedPreviewQueues}
                >
                  สร้าง Queue ที่เลือก ({selectedPreviewKeys.length} Match)
                </button>
              </div>
            )}

            <div className="rmp-preview-list" style={{ display: 'grid', gap: 14 }}>
              {previewLoading && (
                <div style={{ padding: 28, textAlign: 'center' }}>
                  ⏳ กำลังวิเคราะห์ผู้เล่น ACTIVE และสร้าง Preview...
                </div>
              )}
              {!previewLoading && previewMatches.length === 0 && (
                <div style={{ padding: 28, textAlign: 'center' }}>
                  ยังไม่มี Match ที่พร้อมสร้าง Queue
                </div>
              )}
              {previewMatches.map((match, index) => (
                <article
                  key={getPreviewMatchKey(match)}
                  className={`rmp-preview-match${selectedPreviewKeys.includes(getPreviewMatchKey(match)) ? ' is-selected' : ''}`}
                  style={{
                    padding: 16,
                    border: '1px solid #dbe3ef',
                    borderRadius: 14
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 12
                  }}>
                    <label className="rmp-preview-select">
                      <input
                        type="checkbox"
                        checked={selectedPreviewKeys.includes(getPreviewMatchKey(match))}
                        onChange={() => togglePreviewMatch(match)}
                      />
                      <strong>{match.previewLabel || `เลือก Match ${index + 1}`} • Rank {match.previewRank}</strong>
                    </label>
                    <span>
                      {(match.requiresRecentEncounterApproval || getPreviewRuleViolations(match).some(reason => reason.includes('ต้อง Approve'))) && (
                        <b style={{ color: '#b45309', marginRight: 8 }}>
                          ⚠ ต้อง Approve ประวัติผู้เล่นซ้ำ
                        </b>
                      )}
                      Team gap {match.analysis?.teamGap ?? '-'}
                    </span>
                  </div>

                  <div className="rmp-preview-rule-summary">
                    {match.rangeExpandedForLockedPair && (
                      <b>🔒 ขยายกติกาเพื่อให้ Locked Pair อยู่ทีมเดียวกัน</b>
                    )}
                    <span>ผลรวมทีมต่าง {match.analysis?.teamGap ?? match.teamGap ?? '-'} คะแนน</span>
                    <span>ช่วงคะแนนผู้เล่น {match.pointSpread ?? '-'} คะแนน</span>
                    {getPreviewRuleViolations(match).map(reason => (
                      <b key={reason}>⚠ {reason}</b>
                    ))}
                  </div>

                  <div className="rmp-result-teams">
                    {renderPreviewTeam('Team 1', match.team1 || [], index, 'team1', match.team2 || [])}
                    <span className="rmp-vs">VS</span>
                    {renderPreviewTeam('Team 2', match.team2 || [], index, 'team2')}
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: 10,
                    marginTop: 14
                  }}>
                    <button
                      type="button"
                      disabled={previewCreatingIndex !== null}
                      onClick={() => swapPreviewPair(index)}
                    >
                      🔄 สลับคู่
                    </button>
                    <button
                      type="button"
                      className="rmp-generate-button"
                      disabled={previewCreatingIndex !== null}
                      onClick={() => createQueueFromPreview(index)}
                    >
                      {previewCreatingIndex === index
                        ? 'กำลังสร้าง Queue...'
                        : 'เลือก Match นี้เพื่อสร้าง Queue'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      ), document.body)}

      {lastResult && (
        <div className="rmp-result">
          <div className="rmp-result-header">
            <strong>
              สร้าง {createdMatches.length} Match สำเร็จ
            </strong>
            <span>
              Match #{createdMatches
                .map(item => item.groupMatchNo || item.matchId)
                .join(', #')}
            </span>
          </div>

          {lastResult.approvedPlayingPlayers?.length > 0 && (
            <div className="rmp-approved-result">
              Approved สถานะอื่น:{' '}
              {lastResult.approvedPlayingPlayers
                .map(player => `${player.name} (${player.status || '-'})`)
                .join(', ')}
            </div>
          )}

          <div className="rmp-result-list">
            {createdMatches.map((result, index) => (
              <article className="rmp-result-match" key={result.matchId || index}>
                <div className="rmp-result-match-heading">
                  <strong>
                    Match #{result.groupMatchNo || result.matchId || index + 1}
                  </strong>
                  <span>Team gap {result.analysis?.teamGap ?? '-'}</span>
                </div>

                <details className="rmp-result-details">
                  <summary>ดูรายละเอียดการจัด Match</summary>
                  <div className="rmp-result-details-body">
                    {result.mode !== 'MANUAL_4' && result.analysis && (
                      <div className="rmp-range-result">
                        ลำดับคัดเลือก:{' '}
                        {result.analysis.sameRank
                          ? `Rank ${result.analysis.ranks?.[0] || '-'} เดียวกัน`
                          : `ขยาย Rank ห่าง ${result.analysis.rankDistance ?? '-'} ระดับ`}
                        {' • '}ช่วงคะแนนที่ใช้ไม่เกิน{' '}
                        {result.analysis.pointExpansionStep ?? 300} คะแนน
                        {' • '}คะแนนสูงสุด–ต่ำสุดห่าง{' '}
                        {result.analysis.playerSpread ?? '-'} คะแนน
                      </div>
                    )}

                    {result.opponentRepeatRelaxed && (
                      <div className="rmp-repeat-result relaxed">
                        ผ่อนคู่ต่อสู้ซ้ำจาก Match ล่าสุดอัตโนมัติ
                        เพราะไม่พบตัวเลือกใหม่ที่ผ่านเงื่อนไข
                      </div>
                    )}

                    {result.rangeExpandedForRepeatAvoidance && (
                      <div className="rmp-repeat-result relaxed">
                        ขยายคะแนน/Rank อัตโนมัติเพื่อหลีกเลี่ยงผู้เล่นซ้ำ
                        โดยใช้ช่วงใกล้ที่สุด {result.effectivePointRange || result.analysis?.pointExpansionStep || '-'} คะแนน
                      </div>
                    )}

                    {result.partnerRepeatOverridden && (
                      <div className="rmp-repeat-result approved">
                        ผู้ใช้ Approve คู่ทีมที่เคยเล่นด้วยกันแล้ว
                      </div>
                    )}

                    {result.analysis?.waitReleasedPartnerPairs?.length > 0 && (
                      <div className="rmp-repeat-result waited">
                        อนุญาตคู่ทีมเดิมเพราะผู้เล่นทั้งคู่รอครบ 30 นาที:{' '}
                        {result.analysis.waitReleasedPartnerPairs
                          .map(pair => `${pair.nameA} + ${pair.nameB}`)
                          .join(', ')}
                      </div>
                    )}

                    {result.mode === 'POINT_RANGE_RANDOM' && (
                      <div className="rmp-range-result">
                        ผู้เล่นอ้างอิง: {result.anchorPlayer?.name || '-'}{' '}
                        ({result.anchorPlayer?.point ?? '-'} คะแนน) • ช่วงไม่เกิน{' '}
                        {result.pointRange} คะแนน
                      </div>
                    )}

                    {(
                      result.mode === 'PICK_ONE' ||
                      result.mode === 'PRIORITY_SELECT'
                    ) && (
                        <div className="rmp-range-result">
                          เทียบรายบุคคล:{' '}
                          {result.team1?.[0]?.name || 'T1-1'} ↔{' '}
                          {result.team2?.[0]?.name || 'T2-1'} ห่าง{' '}
                          {result.analysis?.individualGaps?.[0] ?? '-'} คะแนน
                          {' • '}
                          {result.team1?.[1]?.name || 'T1-2'} ↔{' '}
                          {result.team2?.[1]?.name || 'T2-2'} ห่าง{' '}
                          {result.analysis?.individualGaps?.[1] ?? '-'} คะแนน
                          {' • '}
                          ภายในทีม: T1 ห่าง{' '}
                          {result.analysis?.teammateGaps?.[0] ?? '-'} คะแนน
                          {' / '}T2 ห่าง{' '}
                          {result.analysis?.teammateGaps?.[1] ?? '-'} คะแนน
                          {' • '}
                          {result.rangeExpandedForRepeatAvoidance
                            ? `ระบบขยายอัตโนมัติเพื่อเลี่ยงคนซ้ำ (เดิมกำหนดไม่เกิน ${result.pointRange})`
                            : result.rangeOverridden
                              ? `ผู้ใช้ยืนยันใช้คู่ที่ใกล้ที่สุด (เดิมกำหนดไม่เกิน ${result.pointRange})`
                              : `เกณฑ์ไม่เกิน ${result.pointRange}`}
                        </div>
                      )}

                    {result.mode === 'PRIORITY_SELECT' && (
                      <div className="rmp-queue-team-order">
                        เรียงตามคิว: Team 1 ={' '}
                        {(result.team1 || []).map(player => player.name).join(' + ')}
                        {' • '}Team 2 ={' '}
                        {(result.team2 || []).map(player => player.name).join(' + ')}
                      </div>
                    )}

                    {result.playStyle && result.playStyle !== 'ANY' && (
                      <div className="rmp-result-play-style">
                        Mood ที่ใช้จัด Match: {result.playStyleLabel ||
                          getPlayStyleLabel(result.playStyle)}
                      </div>
                    )}

                    <p className="rmp-reason">{result.reason}</p>
                  </div>
                </details>

                <div className="rmp-result-teams">
                  {renderTeam('Team 1', result.team1 || [])}
                  <span className="rmp-vs">VS</span>
                  {renderTeam('Team 2', result.team2 || [])}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default RandomMatchPanel;
